import React, {useEffect, useLayoutEffect, useState, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {text} from '../text';
import {hooks} from '../hooks';
import {utils} from '../utils';
import {custom} from '../custom';
import {theme} from '../constants';
import {components} from '../components';
import {svg} from '../assets/svg';
import axiosInstance from '../services/axiosInstance';
import {socketService} from '../services';
import {RootState} from '../store';
import {formatDateTime} from '../utils/formatText';
import { useLanguage } from '../context/LanguageContext';
import { compressImage } from '../utils/imageUtils';
import { listCache } from '../utils/listCache';
import { offlineQueue } from '../services/offlineQueueService';
import { setPendingCount } from '../store/slices/offlineSlice';
import { savePhotoForQueue } from '../services/offlineFileService';
import { taskCache } from '../services/taskCacheService';
import { UserAvatarCard } from '../components/UserAvatarCard';
import { useUiPreferences } from '../context/UiPreferencesContext';
import { TaskDetailsTabLayout, TaskDetailTab, TaskDetailTabKey } from './TaskDetailsTabLayout';
import { VesselPositionsTab } from '../components/VesselPositionsTab';
import { useTripGroup, TripGroupList } from '../components/TripGroupSection';
import { InstalledSerialsSection } from '../components/InstalledSerialsSection';
import { MdPersonOutline, MdKeyboardArrowDown, MdLocationOn, MdClose, MdFileDownload, MdCheckCircle, MdError, MdWarning, MdDelete, MdEdit, MdSave, MdCheckBox, MdCheckBoxOutlineBlank, MdCalendarMonth, MdHandshake, MdDirectionsBoat, MdAttachFile, MdDescription, MdVisibility, MdChevronLeft, MdChevronRight, MdSearch, MdExpandMore, MdExpandLess, MdReceipt, MdMoreVert, MdGroupWork } from 'react-icons/md';
import { FaFileExcel, FaFileWord, FaFilePdf, FaFilePowerpoint, FaFileArchive, FaFileAlt, FaFileImage } from 'react-icons/fa';

// Fix for TS2786: 'Icon' cannot be used as a JSX component.
const PersonIcon = MdPersonOutline as any;
const ArrowIcon = MdKeyboardArrowDown as any;
const LocationIcon = MdLocationOn as any;
const DescriptionIcon = MdDescription as any;
const CloseIcon = MdClose as any;
const DownloadIcon = MdFileDownload as any;
const FileExcelIcon = FaFileExcel as any;
const FileWordIcon = FaFileWord as any;
const FilePdfIcon = FaFilePdf as any;
const FilePowerpointIcon = FaFilePowerpoint as any;
const FileArchiveIcon = FaFileArchive as any;
const FileAltIcon = FaFileAlt as any;
const FileImageIcon = FaFileImage as any;
const CalendarIcon = MdCalendarMonth as any;
const CheckCircleIcon = MdCheckCircle as any;
const ErrorIcon = MdError as any;
const WarningIcon = MdWarning as any;
const DeleteIcon = MdDelete as any;
const EditIcon = MdEdit as any;
const SaveIcon = MdSave as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;
const HandshakeIcon = MdHandshake as any;
const BoatIcon = MdDirectionsBoat as any;
const AttachFileIcon = MdAttachFile as any;
const VisibilityIcon = MdVisibility as any;
const ChevronLeftIcon = MdChevronLeft as any;
const ChevronRightIcon = MdChevronRight as any;
const SearchIcon = MdSearch as any;
const ExpandMoreIcon = MdExpandMore as any;
const ExpandLessIcon = MdExpandLess as any;
const ReceiptIcon = MdReceipt as any;
const MoreVertIcon = MdMoreVert as any;
const GroupWorkIcon = MdGroupWork as any;

const renderTextWithLinks = (text: string) => {
  if (!text) return text;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part?.match(urlRegex)) {
      const href = part.startsWith('www.') ? 'http://' + part : part;
      return (
        <a 
          key={i} 
          href={href} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ color: '#3B82F6', textDecoration: 'underline' }}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

export const TaskDetails: React.FC = () => {
  const {pathname, state, search} = useLocation();
  const navigate = hooks.useAppNavigate();
  const dispatch = hooks.useAppDispatch();
  const authSlice = hooks.useAppSelector((state: RootState) => state.authSlice);
  const isOnline = hooks.useAppSelector((state: RootState) => state.offlineSlice.isOnline);
  const { t, language } = useLanguage();
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [selectedAttachment, setSelectedAttachment] = useState<{
    url: string, name: string, type: string, icon?: React.ReactNode, note?: string, submitted_by?: string,
    galleryIndex?: number,
    gallery?: {url: string, name: string, type: string, icon?: React.ReactNode, note?: string, submitted_by?: string}[]
  } | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewScore, setReviewScore] = useState('0');
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingNote, setPendingNote] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [expandedCommentFeedbacks, setExpandedCommentFeedbacks] = useState<Set<number>>(new Set());
  const [newComments, setNewComments] = useState<{[key: string]: string}>({});
  const [commentAttachments, setCommentAttachments] = useState<{[key: string]: File | null}>({});
  const [commentSendingKey, setCommentSendingKey] = useState<string | null>(null);
  const [commentUploadProgress, setCommentUploadProgress] = useState(0);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);
  const [confirmDeleteCommentId, setConfirmDeleteCommentId] = useState<number | null>(null);
  const [expandedAttachmentId, setExpandedAttachmentId] = useState<number | null>(null);
  const [expandedCommentIds, setExpandedCommentIds] = useState<Set<number>>(new Set());
  const [scrollAtEnd, setScrollAtEnd] = useState<Record<string, boolean>>({});
  const [scrollAtStart, setScrollAtStart] = useState<Record<string, boolean>>({});
  const commentsRef = useRef<HTMLDivElement>(null);
  const [prevCommentCount, setPrevCommentCount] = useState<number>(-1);
  
  // Parse query param untuk mendapatkan ID jika dibuka dari notifikasi/link langsung
  const searchParams = new URLSearchParams(search);
  const taskIdFromUrl = searchParams.get('id');
  // Dari daftar task (state) atau link notifikasi komentar (?comments=1)
  const wantsScrollToComments = !!state?.scrollToComments || searchParams.get('comments') === '1';

  // Gunakan state agar data bisa di-update dari API (untuk mendapatkan data lengkap seperti assigned_by.role)
  // Prioritas: 1. State dari navigasi, 2. ID dari URL
  const [task, setTask] = useState(state?.task || (taskIdFromUrl ? { id: parseInt(taskIdFromUrl) } : null));
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(!state?.task && !!taskIdFromUrl);
  const [isTaskRefreshing, setIsTaskRefreshing] = useState(!!state?.task);
  const [usingOfflineCache, setUsingOfflineCache] = useState<string | null>(null); // ISO timestamp jika pakai cache
  const [cacheBannerTop, setCacheBannerTop] = useState(85); // diukur dinamis dari bottom OfflineBanner
  const [cacheBannerHeight, setCacheBannerHeight] = useState(33);

  // ── Layout tab (preferensi user di Profil) ─────────────────────────────────
  const { taskDetailLayout } = useUiPreferences();
  const isTabLayout = taskDetailLayout === 'tabs';
  // ?tab=detail|documents|checker|report di URL (mis. dari notifikasi) → buka tab itu
  const [tabRequest, setTabRequest] = useState<{ tab: TaskDetailTabKey; nonce: number } | null>(() => {
    const urlTab = new URLSearchParams(window.location.search).get('tab');
    return urlTab ? { tab: urlTab as TaskDetailTabKey, nonce: 1 } : null;
  });
  const activeTaskTabRef = useRef<TaskDetailTabKey | null>(null);
  // Tab yang dapat komentar baru dari orang lain saat user sedang di tab lain → titik merah
  const [commentAlertTabs, setCommentAlertTabs] = useState<TaskDetailTabKey[]>([]);

  // Ukur tinggi aktual OfflineBanner agar cache banner pas di bawahnya tanpa gap
  useLayoutEffect(() => {
    if (isOnline) return;
    const measure = () => {
      const el = document.querySelector('[data-offline-banner]');
      if (el) {
        const bottom = Math.round(el.getBoundingClientRect().bottom);
        setCacheBannerTop(bottom);
      }
    };
    measure();
    // Re-measure jika konten banner berubah (misal: pendingCount muncul)
    const observer = new ResizeObserver(measure);
    const el = document.querySelector('[data-offline-banner]');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, [isOnline]);

  useEffect(() => {
    const newTask = state?.task || (taskIdFromUrl ? { id: parseInt(taskIdFromUrl) } : null);
    if (newTask && newTask.id !== task?.id) {
      setTask(newTask);
      setIsFetching(true);
    }
  }, [state?.task, taskIdFromUrl]);

  useEffect(() => {
    setImgZoom(1);
    setImgTranslate({ x: 0, y: 0 });
    setAttachmentPdfZoom(1);
  }, [selectedAttachment]);

  // Kebab menu state
  const [showKebabMenu, setShowKebabMenu] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const [showCheckerResponseModal, setShowCheckerResponseModal] = useState(false);
  const [checkerResponseNote, setCheckerResponseNote] = useState('');
  const [checkerResponsePhotos, setCheckerResponsePhotos] = useState<File[]>([]);

  // Pre-check report link state
  const [showLinkReportSheet, setShowLinkReportSheet] = useState(false);
  const [availableReports, setAvailableReports] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [linkingReport, setLinkingReport] = useState(false);
  
  // State for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [assignSearchTerm, setAssignSearchTerm] = useState('');

  const [previewPdfUrl, setPreviewPdfUrl] = useState<{url: string, name: string} | null>(null);
  const [attachmentPdfZoom, setAttachmentPdfZoom] = useState(1);
  const [previewPdfZoom, setPreviewPdfZoom] = useState(1);

  useEffect(() => {
    setPreviewPdfZoom(1);
  }, [previewPdfUrl]);
  const [showVesselHistoryModal, setShowVesselHistoryModal] = useState(false);
  const [vesselTasks, setVesselTasks] = useState<any[]>([]);
  const [isLoadingVesselTasks, setIsLoadingVesselTasks] = useState(false);
  const [showPreparedItemsModal, setShowPreparedItemsModal] = useState(false);
  const [showCheckedItemsModal, setShowCheckedItemsModal] = useState(false);
  const [showSelfCheckedItemsModal, setShowSelfCheckedItemsModal] = useState(false);

  // Bottom sheet animation — Prepared Items
  const [sheetPreparedDelta, setSheetPreparedDelta] = useState(0);
  const [sheetPreparedIsClosing, setSheetPreparedIsClosing] = useState(false);
  const sheetPreparedRef = useRef<HTMLDivElement>(null);
  const sheetPreparedDragStartY = useRef(0);
  const sheetPreparedDragActive = useRef(false);
  const closePreparedSheet = () => {
    const h = sheetPreparedRef.current?.offsetHeight ?? 500;
    setSheetPreparedIsClosing(true);
    setSheetPreparedDelta(h);
    setTimeout(() => { setShowPreparedItemsModal(false); setSheetPreparedDelta(0); setSheetPreparedIsClosing(false); }, 260);
  };

  // Bottom sheet animation — Checked Items
  const [sheetCheckedDelta, setSheetCheckedDelta] = useState(0);
  const [sheetCheckedIsClosing, setSheetCheckedIsClosing] = useState(false);
  const sheetCheckedRef = useRef<HTMLDivElement>(null);
  const sheetCheckedDragStartY = useRef(0);
  const sheetCheckedDragActive = useRef(false);
  const closeCheckedSheet = () => {
    const h = sheetCheckedRef.current?.offsetHeight ?? 500;
    setSheetCheckedIsClosing(true);
    setSheetCheckedDelta(h);
    setTimeout(() => { setShowCheckedItemsModal(false); setSheetCheckedDelta(0); setSheetCheckedIsClosing(false); }, 260);
  };

  // Bottom sheet animation — Self-Checked Items (inspeksi mandiri)
  const [sheetSelfCheckedDelta, setSheetSelfCheckedDelta] = useState(0);
  const [sheetSelfCheckedIsClosing, setSheetSelfCheckedIsClosing] = useState(false);
  const sheetSelfCheckedRef = useRef<HTMLDivElement>(null);
  const sheetSelfCheckedDragStartY = useRef(0);
  const sheetSelfCheckedDragActive = useRef(false);
  const closeSelfCheckedSheet = () => {
    const h = sheetSelfCheckedRef.current?.offsetHeight ?? 500;
    setSheetSelfCheckedIsClosing(true);
    setSheetSelfCheckedDelta(h);
    setTimeout(() => { setShowSelfCheckedItemsModal(false); setSheetSelfCheckedDelta(0); setSheetSelfCheckedIsClosing(false); }, 260);
  };

  const [imgZoom, setImgZoom] = useState(1);
  const [imgTranslate, setImgTranslate] = useState({ x: 0, y: 0 });
  const lastTapRef = useRef(0);
  const pinchRef = useRef<{ startDist: number; startZoom: number } | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; startTx: number; startTy: number } | null>(null);

  // Gunakan API Base URL sebagai referensi jika Image Base URL tidak diset
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://apiteratasks.teralintas.com/api';
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  const [alertConfig, setAlertConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning';
    errorDetail?: string;
    onClose?: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'success' });
  const [errorDetailExpanded, setErrorDetailExpanded] = useState(false);

  // Travel expense state
  const [showAddModal, setShowAddModal] = useState(false);
  const [travelExpenses, setTravelExpenses] = useState<any[]>([]);
  // Naik setiap grup perjalanan berubah → dokumen bersama & daftar bawaan checker dimuat ulang
  const [groupVersion, setGroupVersion] = useState(0);
  // Grup perjalanan: data + dialog (tambah/keluarkan task) — dipicu dari menu kebab,
  // daftar anggota tampil di tab "Grup" hanya kalau task sudah tergabung
  const tripGroup = useTripGroup(task?.id, () => setGroupVersion(v => v + 1));
  const [teExpandedId, setTeExpandedId] = useState<number | null>(null);
  const [teDeletingId, setTeDeletingId] = useState<number | null>(null);
  const [teActionId, setTeActionId] = useState<number | null>(null);
  const [teActionLoading, setTeActionLoading] = useState(false);
  const [teConfirmModal, setTeConfirmModal] = useState<{ expId: number; action: 'approve' | 'reject' } | null>(null);
  const [teConfirmNotes, setTeConfirmNotes] = useState('');

  // Travel expense recap state
  const [recaps, setRecaps] = useState<any[]>([]);
  const [recapExpandedId, setRecapExpandedId] = useState<number | null>(null);
  const [recapDeletingId, setRecapDeletingId] = useState<number | null>(null);
  const [recapLightboxUrl, setRecapLightboxUrl] = useState<string | null>(null);
  const recapImageBaseUrl = (process.env.REACT_APP_IMAGE_BASE_URL || '').replace(/\/$/, '');

  // Time sheet state
  const [timeSheets, setTimeSheets] = useState<any[]>([]);
  const [tsExpandedId, setTsExpandedId] = useState<number | null>(null);
  const [tsDeletingId, setTsDeletingId] = useState<number | null>(null);

  const handleTravelExpenseAction = async (id: number, action: 'approve' | 'reject', notes?: string) => {
    setTeActionLoading(true);
    try {
      await axiosInstance.put(`/travel-expenses/${id}/${action}`, { notes: notes || undefined });
      setTravelExpenses(prev => prev.map(e =>
        e.id === id ? { ...e, status: action === 'approve' ? 'approved' : 'rejected', reviewed_by_name: (authSlice as any).name } : e
      ));
      setTeActionId(null);
      showAlert(
        t.common.success,
        action === 'approve' ? 'Pengajuan uang jalan berhasil disetujui.' : 'Pengajuan uang jalan berhasil ditolak.',
        'success'
      );
    } catch (err: any) {
      showAlert(t.common.error, err.response?.data?.message || 'Aksi gagal', 'error');
    } finally {
      setTeActionLoading(false);
    }
  };

  const handleDeleteTravelExpense = async (id: number) => {
    setTeDeletingId(id);
    try {
      await axiosInstance.delete(`/travel-expenses/${id}`);
      setTravelExpenses(prev => prev.filter(e => e.id !== id));
      if (teExpandedId === id) setTeExpandedId(null);
    } catch (err: any) {
      showAlert(t.common.error, err.response?.data?.message || 'Gagal menghapus', 'error');
    } finally {
      setTeDeletingId(null);
    }
  };

  const handleDeleteRecap = async (id: number) => {
    setRecapDeletingId(id);
    try {
      await axiosInstance.delete(`/travel-expense-recaps/${id}`);
      setRecaps(prev => prev.filter(r => r.id !== id));
      if (recapExpandedId === id) setRecapExpandedId(null);
    } catch (err: any) {
      showAlert(t.common.error, err.response?.data?.message || 'Gagal menghapus', 'error');
    } finally {
      setRecapDeletingId(null);
    }
  };

  const handleDeleteTimeSheet = async (id: number) => {
    setTsDeletingId(id);
    try {
      await axiosInstance.delete(`/time-sheets/${id}`);
      setTimeSheets(prev => prev.filter(ts => ts.id !== id));
      if (tsExpandedId === id) setTsExpandedId(null);
    } catch (err: any) {
      showAlert(t.common.error, err.response?.data?.message || 'Gagal menghapus', 'error');
    } finally {
      setTsDeletingId(null);
    }
  };

  const userPermissions = (authSlice as any).permissions || [];
  const hasPermission = (perm: string) => userPermissions.includes(perm);

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  // fetch data user untuk select
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/users');
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      }
    };
    fetchUsers();
  }, []);

  
  // Invalidate task list cache saat unmount — agar data selalu fresh saat back dari detail
  useEffect(() => {
    return () => { listCache.invalidate('tasks'); };
  }, []);

  // Fetch detail task terbaru dari API untuk memastikan data lengkap
  useEffect(() => {
    const fetchTaskDetail = async () => {
      if (!task?.id) {
        setIsFetching(false);
        setIsTaskRefreshing(false);
        return;
      }
      setIsTaskRefreshing(true);
      try {
        const response = await axiosInstance.get(`/tasks/${task.id}`);
        setTask((prev: any) => ({...prev, ...response.data}));
        setUsingOfflineCache(null);
        // Simpan ke cache setiap berhasil fetch (hanya saat online)
        if (authSlice.userId) {
          taskCache.save(response.data, authSlice.userId.toString()).catch(() => {});
        }
      } catch (error: any) {
        const isNetworkError = !error?.response; // tidak ada response = network error, bukan 404/403
        if (isNetworkError && !isOnline) {
          // Offline — coba fallback ke cache
          const cached = authSlice.userId
            ? await taskCache.get(task.id, authSlice.userId.toString()).catch(() => null)
            : null;
          if (cached) {
            setTask((prev: any) => ({...prev, ...cached.data}));
            setUsingOfflineCache(cached.cached_at);
          } else {
            setTask(null);
          }
        } else {
          // Online tapi gagal (404, 403, server error) — jangan pakai cache
          console.error('Failed to fetch task details:', error);
          setTask(null);
        }
      } finally {
        setIsFetching(false);
        setIsTaskRefreshing(false);
      }
    };
    fetchTaskDetail();
  }, [task?.id]);


  // Fetch travel expenses for this task
  useEffect(() => {
    if (!task?.id) return;
    axiosInstance.get(`/travel-expenses/task/${task.id}`)
      .then(r => setTravelExpenses(r.data))
      .catch(() => {});
  }, [task?.id, groupVersion]);

  // Fetch travel expense recaps for this task
  useEffect(() => {
    if (!task?.id) return;
    axiosInstance.get(`/travel-expense-recaps/task/${task.id}`)
      .then(r => setRecaps(r.data))
      .catch(() => {});
  }, [task?.id, groupVersion]);

  // Fetch time sheets for this task
  useEffect(() => {
    if (!task?.id) return;
    axiosInstance.get(`/time-sheets/task/${task.id}`)
      .then(r => setTimeSheets(r.data))
      .catch(() => {});
  }, [task?.id, groupVersion]);

  // Grup berubah → muat ulang task (checker, daftar bawaan grup, task_group_id)
  useEffect(() => {
    if (!task?.id || groupVersion === 0) return;
    axiosInstance.get(`/tasks/${task.id}`)
      .then(r => setTask((prev: any) => ({...prev, ...r.data})))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupVersion]);

  useEffect(() => {
    const groups = (task?.required_attachments || []).map((att: any) => att.group_name || 'Lainnya');
    if (groups.length > 0) setExpandedGroups(groups);
  }, [task?.required_attachments]);

  const viewGroupedAttachments = React.useMemo(() => {
    const reqs: any[] = task?.required_attachments || [];
    const groups: { [key: string]: any[] } = {};
    reqs.forEach((att: any) => {
      const g = att.group_name || (t.common?.others || 'Lainnya');
      if (!groups[g]) groups[g] = [];
      groups[g].push(att);
    });
    const sortedKeys = Object.keys(groups).sort();
    return sortedKeys.map(key => ({ group_name: key, attachments: groups[key] }));
  }, [task?.required_attachments, t.common?.others]);

  // WebSocket Integration
  useEffect(() => {
    const socketUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';
    
    socketService.connect(socketUrl, authSlice.accessToken || undefined);

    const handleTaskUpdate = async (updatedTask: any) => {
      if (updatedTask.id === task?.id) {
        try {
          const response = await axiosInstance.get(`/tasks/${updatedTask.id}`);
          setTask((prev: any) => ({ ...prev, ...response.data }));
        } catch (error) {
          console.error('Failed to update task details from socket:', error);
        }
      }
    };

    socketService.on('task_updated', handleTaskUpdate);

    return () => {
      socketService.off('task_updated', handleTaskUpdate);
    };
  }, [task?.id, authSlice.accessToken]);

  // Scroll ke komentar baru secara otomatis jika diakses dari notifikasi/label "Pesan Baru" ATAU ada komentar baru via websocket
  useEffect(() => {
    if (isFetching || !task?.feedbacks) return;
    
    let currentCommentCount = 0;
    let newestCommentOverall: any = null;
    let newestCommentFromOther: any = null;
    let targetAttachmentTypeId: number | null = null;
    let targetCommentId: number | null = null;

    // 1. Hitung total komentar dan cari komentar terbaru
    task.feedbacks.forEach((feedback: any) => {
      if (feedback.comments) {
        currentCommentCount += feedback.comments.length;
        feedback.comments.forEach((c: any) => {
          // Komentar terbaru secara keseluruhan
          if (!newestCommentOverall || new Date(c.created_at) > new Date(newestCommentOverall.created_at)) {
            newestCommentOverall = c;
          }
          
          // Komentar terbaru HANYA dari orang lain
          if (c.user_id != authSlice.userId) {
            if (!newestCommentFromOther || new Date(c.created_at) > new Date(newestCommentFromOther.created_at)) {
              newestCommentFromOther = c;
              targetCommentId = c.id;
              // Cari tahu apakah komentar ini ada di dalam attachment tertentu (Untuk Auto-Expand)
              if (c.feedback_attachment_id) {
                const img = feedback.images?.find((i: any) => i.id === c.feedback_attachment_id);
                if (img && img.task_checklist_id) {
                  targetAttachmentTypeId = img.task_checklist_id;
                }
              } else {
                targetAttachmentTypeId = null;
              }
            }
          }
        });
      }
    });

    const isInitialLoadWithScroll = wantsScrollToComments && prevCommentCount === -1;
    const isNewCommentArrived = prevCommentCount !== -1 && currentCommentCount > prevCommentCount;

    // Hapus highlight jika komentar terbaru secara keseluruhan adalah milik Anda (Anda sudah membalas)
    if (newestCommentOverall && newestCommentOverall.user_id == authSlice.userId) {
       const pulsedElements = document.querySelectorAll('.highlight-pulse');
       pulsedElements.forEach(el => el.classList.remove('highlight-pulse'));
    }

    // Pastikan pulse hanya menyala jika komentar terbaru benar-benar dari orang lain
    const isNewestFromOther = newestCommentOverall && newestCommentOverall.user_id != authSlice.userId;

    if ((isInitialLoadWithScroll || (isNewCommentArrived && isNewestFromOther)) && targetCommentId) {
      // Layout tab: komentar di lampiran → tab Dokumen, komentar report → tab Report. Saat dibuka
      // dari notifikasi langsung pindah ke tab itu; komentar baru saat user di tab lain cukup
      // diberi titik merah (tidak memindahkan tab yang sedang dibaca).
      if (isTabLayout) {
        const commentTab: TaskDetailTabKey = targetAttachmentTypeId ? 'documents' : 'report';
        if (isInitialLoadWithScroll) {
          setTabRequest({ tab: commentTab, nonce: Date.now() });
        } else if (activeTaskTabRef.current !== commentTab) {
          setCommentAlertTabs(prev => prev.includes(commentTab) ? prev : [...prev, commentTab]);
        }
      }

      // 2. Auto-expand accordion jika komentar ada di dalamnya
      if (targetAttachmentTypeId) {
        setExpandedAttachmentId(targetAttachmentTypeId);
        const groupName = task?.required_attachments?.find((a: any) => a.id === targetAttachmentTypeId)?.group_name
          || t.common?.others
          || 'Lainnya';
        setExpandedGroups(prev => Array.from(new Set([...prev, groupName])));
      }

      // 3. Scroll dan beri pulse
      setTimeout(() => {
        const targetEl = document.getElementById(`comment-${targetCommentId}`);
        if (targetEl) {
          targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetEl.classList.add('highlight-pulse');
        } else if (commentsRef.current && isInitialLoadWithScroll) {
          commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 600); // Jeda 600ms memberi waktu bagi DOM/Gambar untuk selesai me-render layout
    }

    if (currentCommentCount !== prevCommentCount) {
      setPrevCommentCount(currentCommentCount);
    }
  }, [isFetching, wantsScrollToComments, task?.feedbacks, authSlice.userId, prevCommentCount]);

  // Sync selectedUser dengan data task saat task dimuat/berubah
  useEffect(() => {
    if (task?.assigned_to && Array.isArray(task.assigned_to)) {
      setSelectedUserIds(task.assigned_to.map((u: any) => u.id));
    }
  }, [task]);

  const showAlert = (title: string, message: string, type: 'success' | 'error' | 'warning' = 'success', onClose?: () => void, errorDetail?: string) => {
    setErrorDetailExpanded(false);
    setAlertConfig({
      isOpen: true,
      title,
      message,
      type,
      errorDetail,
      onClose,
    });
  };

  const hideAlert = () => {
    const onClose = alertConfig.onClose;
    setAlertConfig((prev) => ({ ...prev, isOpen: false }));
    setErrorDetailExpanded(false);
    if (onClose) {
      onClose();
    }
  };

  const toggleAssignUser = async (userId: number) => {
    try {
      let newIds = [...selectedUserIds];
      if (newIds.includes(userId)) {
        newIds = newIds.filter(id => id !== userId);
      } else {
        newIds.push(userId);
      }
      setSelectedUserIds(newIds);
      
      await axiosInstance.put(`/tasks/${task.id}`, {
        assigned_to_user_ids: newIds,
        status: 'pending_approval',
        approved_by_user_id: null,
        approved_at: null,
      });
      // Refresh data task untuk mendapatkan detail user (nama, role) yang baru di-assign
      const response = await axiosInstance.get(`/tasks/${task.id}`);
      setTask((prev: any) => ({...prev, ...response.data}));
    } catch (error: any) {
      console.error('Failed to assign user:', error);
      showAlert(t.common.accessDenied, error?.response?.data?.message || t.taskDetails.assignFailed || 'Failed to assign user', 'error', undefined, error?.response?.data?.error || error?.message);
    }
  };

  const handleSendComment = async (feedbackId: number, attachmentId?: number) => {
    const key = attachmentId ? `attachment_${attachmentId}` : `feedback_${feedbackId}`;
    const comment = newComments[key];
    let attachment = commentAttachments[key];
    if ((!comment || comment.trim() === '') && !attachment) return;

    if (attachment && attachment.type.startsWith('image/')) {
      try {
        attachment = await compressImage(attachment);
      } catch (error) {
        console.error("Compression error:", error);
      }
    }

    if (attachment && attachment.size > 7 * 1024 * 1024) {
      showAlert(t.common.error, 'Ukuran file terlalu besar. Maksimal 7MB.', 'error');
      return;
    }

    setCommentSendingKey(key);
    setCommentUploadProgress(0);

    // Offline mode — simpan ke antrian
    if (!isOnline) {
      try {
        let photoRef = undefined;
        if (attachment) {
          photoRef = await savePhotoForQueue(attachment);
        }
        await offlineQueue.add({
          user_id: String(authSlice.userId),
          action_type: 'feedback_comment',
          payload: {
            feedback_id: feedbackId,
            ...(attachmentId ? { feedback_attachment_id: attachmentId } : {}),
            comment: comment || '',
            ...(photoRef ? { photo_ref: photoRef } : {}),
          },
        });
        const newCount = await offlineQueue.getPendingCount();
        dispatch(setPendingCount(newCount));
        setNewComments(prev => ({ ...prev, [key]: '' }));
        setCommentAttachments(prev => ({ ...prev, [key]: null }));
        showAlert(
          (t.common as any)?.offlineQueued || 'Tersimpan Offline',
          (t.common as any)?.offlineCommentQueued || 'Komentar akan dikirim saat koneksi tersedia.',
          'success'
        );
      } catch (err: any) {
        showAlert(t.common.error, err?.message || 'Gagal menyimpan komentar ke antrian offline.', 'error');
      } finally {
        setCommentSendingKey(null);
      }
      return;
    }

    try {
      const formData = new FormData();
      if (comment) formData.append('comment', comment);
      if (attachment) formData.append('attachment', attachment);
      if (attachmentId) formData.append('feedback_attachment_id', attachmentId.toString());

      await axiosInstance.post(`/tasks/feedbacks/${feedbackId}/comments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e: any) => {
          if (e.total) setCommentUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      setNewComments(prev => ({ ...prev, [key]: '' }));
      setCommentAttachments(prev => ({ ...prev, [key]: null }));
      const response = await axiosInstance.get(`/tasks/${task.id}`);
      setTask((prev: any) => ({ ...prev, ...response.data }));

      // Hentikan animasi pulse jika user sudah membalas komentar tersebut
      const pulsedElements = document.querySelectorAll('.highlight-pulse');
      pulsedElements.forEach(el => el.classList.remove('highlight-pulse'));
    } catch (error: any) {
      console.error('Failed to send comment:', error);
      showAlert(t.common.error, error?.response?.data?.message || 'Failed to send response', 'error', undefined, error?.response?.data?.error || error?.message);
    } finally {
      setCommentSendingKey(null);
      setCommentUploadProgress(0);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    setDeletingCommentId(commentId);
    try {
      await axiosInstance.delete(`/tasks/feedbacks/comments/${commentId}`);
      const response = await axiosInstance.get(`/tasks/${task.id}`);
      setTask((prev: any) => ({ ...prev, ...response.data }));
    } catch (error: any) {
      showAlert(t.common.error, error?.response?.data?.message || 'Gagal menghapus komentar.', 'error');
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleConfirmReplacement = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.post(`/tasks/${task.id}/confirm-replacement`);
      showAlert(
        t.common.success,
        (t.checkerTask as any)?.replacementAccepted || 'Item pengganti dikonfirmasi.',
        'success'
      );
    } catch (error: any) {
      showAlert(
        t.common.error,
        error?.response?.data?.message || (t.checkerTask as any)?.replacementAcceptFailed || 'Gagal mengkonfirmasi',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckerResponse = async () => {
    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append('note', checkerResponseNote);
      checkerResponsePhotos.forEach(photo => formData.append('photos', photo));
      await axiosInstance.post(`/tasks/${task.id}/checker-response`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setShowCheckerResponseModal(false);
      setCheckerResponseNote('');
      setCheckerResponsePhotos([]);
      showAlert(
        t.common.success,
        (t.checkerTask as any)?.confirmSuccess || 'Konfirmasi berhasil dikirim.',
        'success'
      );
    } catch (error: any) {
      showAlert(
        t.common.error,
        error?.response?.data?.message || (t.checkerTask as any)?.confirmFailed || 'Gagal mengirim konfirmasi',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };



  const handleOpenLinkSheet = async () => {
    setShowLinkReportSheet(true);
    setLoadingReports(true);
    try {
      const res = await axiosInstance.get('/checker-reports?unlinked_only=true&status=ready');
      // Also include the currently-linked report so it shows as "selected" in the list
      const linked = task.linked_checker_report;
      const unlinked: any[] = res.data;
      if (linked && !unlinked.find((r: any) => r.id === linked.id)) {
        setAvailableReports([{ ...linked, _is_current: true }, ...unlinked]);
      } else {
        setAvailableReports(unlinked);
      }
    } catch {
      setAvailableReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  const handleLinkReport = async (reportId: number) => {
    const currentLinkedId = task.linked_checker_report?.id;
    if (currentLinkedId === reportId) return; // already selected
    setLinkingReport(true);
    try {
      // If replacing, unlink the current one first
      if (currentLinkedId) {
        await axiosInstance.post(`/checker-reports/${currentLinkedId}/unlink`);
      }
      await axiosInstance.post(`/checker-reports/${reportId}/link`, { task_id: task.id });
      setShowLinkReportSheet(false);
      showAlert(t.common.success, (t as any).preCheckReport?.linkSuccess || 'Laporan berhasil dilampirkan ke task.', 'success', () => {
        window.location.reload();
      });
    } catch (e: any) {
      showAlert(t.common.error, e?.response?.data?.message || (t as any).preCheckReport?.linkFailed || 'Gagal melampirkan laporan.', 'error');
    } finally {
      setLinkingReport(false);
    }
  };

  const handleUnlinkReport = async () => {
    const reportId = task.linked_checker_report?.id;
    if (!reportId) return;
    try {
      await axiosInstance.post(`/checker-reports/${reportId}/unlink`);
      showAlert(t.common.success, (t as any).preCheckReport?.unlinkSuccess || 'Lampiran dibatalkan.', 'success', () => {
        window.location.reload();
      });
    } catch (e: any) {
      showAlert(t.common.error, e?.response?.data?.message || (t as any).preCheckReport?.unlinkFailed || 'Gagal membatalkan lampiran.', 'error');
    }
  };

  const handleCancelPreparation = async () => {
    try {
      await axiosInstance.delete(`/tasks/${task.id}/preparation`);
      showAlert(t.common.success, (t as any).checkerTask?.cancelPreparationSuccess || 'Persiapan dibatalkan.', 'success', () => {
        window.location.reload();
      });
    } catch (e: any) {
      showAlert(t.common.error, e?.response?.data?.message || (t as any).checkerTask?.cancelPreparationFailed || 'Gagal membatalkan persiapan.', 'error');
    }
  };

  const handleChangeReport = () => {
    handleOpenLinkSheet();
  };

  const handleDeleteTask = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.delete(`/tasks/${task.id}`);
      showAlert(t.common.success, t.taskDetails.taskDeleted || 'Task deleted successfully', 'success', () => {
        navigate('/Task');
      });
    } catch (error: any) {
      console.error('Failed to delete task:', error);
      showAlert(t.common.error, error?.response?.data?.message || t.taskDetails.taskDeleteFailed || 'Failed to delete task', 'error', undefined, error?.response?.data?.error || error?.message);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
    }
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => prev.includes(groupName) ? prev.filter(g => g !== groupName) : [...prev, groupName]);
  };


  const totalDuration = React.useMemo(() => {
    const totalMins = task?.total_duration || 0;
    const days = Math.floor(totalMins / 1440);
    const hours = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    const parts = [];
    if (days > 0) parts.push(`${days} ${t.common.days || 'Days'}`);
    if (hours > 0) parts.push(`${hours} ${t.common.hours || 'Hours'}`);
    if (mins > 0) parts.push(`${mins} ${t.common.minutes || 'Minutes'}`);
    return parts.length > 0 ? parts.join(' ') : `0 ${t.common.minutes || 'Minutes'}`;
  }, [task?.total_duration, language]);

  if (isFetching) {
    return (
      <div id='screen' style={{ ...utils.flexCenter(), height: '100vh', flexDirection: 'column' }}>
        <components.Header title={t.taskDetails.title} goBack={true} />
        <text.T16>{t.common.loading}</text.T16>
      </div>
    );
  }

  if (!task || !task.task_type) {
    return (
        <div id='screen' style={{...utils.flexCenter(), height: '100vh', flexDirection: 'column'}}>
            <components.Header title={t.taskDetails.title} goBack={true} />
            <div style={{textAlign: 'center', padding: 20}}>
               <ErrorIcon size={60} color="#EF4444" style={{marginBottom: 16}} />
               <text.H4 style={{marginBottom: 8}}>{t.taskDetails.notFound}</text.H4>
               <text.T16 style={{color: theme.colors.bodyTextColor}}>{t.taskDetails.notFoundDesc}</text.T16>
            </div>
            <components.Button title={t.common.goBack} onClick={() => navigate(-1)} containerStyle={{marginTop: 20, width: 200}} />
        </div>
    );
  }

  const handleAction = async (action: 'approve' | 'reject' | 'incomplete' | 'submit' | 'start' | 'pending') => {
    if (action === 'submit') {
      navigate('/TaskSubmission', { state: { task } });
      return;
    }

    // Buka modal note sebelum pending
    if (action === 'pending') {
      setPendingNote('');
      setShowPendingModal(true);
      return;
    }

    // Offline: queue aksi start (mulai pekerjaan)
    if (action === 'start' && !isOnline) {
      try {
        await offlineQueue.add({
          user_id: String(authSlice.userId),
          action_type: 'task_start',
          payload: { task_id: task.id },
        });
        const newCount = await offlineQueue.getPendingCount();
        dispatch(setPendingCount(newCount));
        listCache.updateItem(
          'tasks',
          (t: any) => String(t.id) === String(task.id),
          (t: any) => ({ ...t, status: 'in_progress', has_pending_sync: true })
        );
        showAlert(
          t.common.success,
          (t.taskDetails as any)?.startOfflineQueued || 'Pekerjaan dimulai. Akan disinkronkan saat koneksi tersedia.',
          'success',
          () => navigate(-1)
        );
      } catch (err: any) {
        showAlert(t.common.error, err?.message || 'Gagal menyimpan ke antrian offline.', 'error');
      }
      return;
    }

    try {
      setIsLoading(true);
      const actionBody: Record<string, any> = { notes: reviewNotes, score: reviewScore };
      if (action === 'start') {
        actionBody.notes = (t.taskDetails as any).resumedNote || 'Pengerjaan task dimulai kembali';
      }
      // Menggunakan endpoint dinamis sesuai aksi
      await axiosInstance.post(`/tasks/${task.id}/${action}`, actionBody);
      const actionMsgMap: Record<string, string> = {
        approve: t.taskDetails.alerts?.approved || 'Task approved',
        reject: t.taskDetails.alerts?.rejected || 'Task rejected',
        incomplete: t.taskDetails.alerts?.incompleted || 'Task marked incomplete',
        start: t.taskDetails.alerts?.started || 'Task started',
        pending: t.taskDetails.alerts?.pendinged || 'Task on hold',
      };
      showAlert(t.common.success, actionMsgMap[action] || t.taskDetails.alerts?.approved || 'Done', 'success', () => {
        listCache.invalidate('tasks');
        navigate(-1);
      });
    } catch (error: any) {
      console.error(`Failed to ${action} task:`, error);
      showAlert(t.common.error, error?.response?.data?.message || t.taskDetails.alerts?.actionFailed || 'Action failed', 'error', undefined, error?.response?.data?.error || error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const submitPending = async () => {
    if (!isOnline) {
      try {
        await offlineQueue.add({
          user_id: String(authSlice.userId),
          action_type: 'task_pending',
          payload: { task_id: task.id, notes: pendingNote || null },
        });
        // Update banner langsung tanpa tunggu network event
        const newCount = await offlineQueue.getPendingCount();
        dispatch(setPendingCount(newCount));
        // Optimistic cache update — tampilkan status baru dengan indikator sync
        listCache.updateItem(
          'tasks',
          (t: any) => String(t.id) === String(task.id),
          (t: any) => ({ ...t, status: 'on_hold', has_pending_sync: true })
        );
        setShowPendingModal(false);
        showAlert(
          t.common.success,
          (t.taskDetails as any)?.pendingOfflineQueued || 'Ditandai pending. Akan disinkronkan saat koneksi tersedia.',
          'success',
          () => { listCache.invalidate('tasks'); navigate(-1); }
        );
      } catch (err: any) {
        showAlert(t.common.error, err?.message || 'Gagal menyimpan ke antrian offline.', 'error');
      }
      return;
    }

    try {
      setIsLoading(true);
      await axiosInstance.post(`/tasks/${task.id}/pending`, { notes: pendingNote || null });
      setShowPendingModal(false);
      showAlert(t.common.success, t.taskDetails.alerts?.pendinged || 'Task on hold', 'success', () => {
        listCache.invalidate('tasks');
        navigate(-1);
      });
    } catch (error: any) {
      showAlert(t.common.error, error?.response?.data?.message || t.taskDetails.alerts?.actionFailed || 'Action failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const normalizedStatus = status?.toLowerCase() || '';
    switch (normalizedStatus) {
      case 'draft':
        return {bg: '#E0F7FA', color: '#00BCD4', label: 'Draft', icon: <svg.InfoSvg />};
      case 'approved':
        return {bg: '#E3F2FD', color: '#2196F3', label: 'Approved', icon: <svg.CheckSvg />};
      case 'on_hold':
      case 'on hold':
        return {bg: '#FFF3E0', color: '#FF9800', label: 'On Hold', icon: <svg.AlertSvg />};
      case 'in_progress':
      case 'in progress':
        return {bg: '#E0F2FE', color: '#2563EB', label: 'In Progress', icon: <svg.AlertSvg />};
      case 'in_review':
      case 'in review':
        return {bg: '#F3E5F5', color: '#9C27B0', label: 'In Review', icon: <svg.AlertSvg />};
      case 'completed':
        return {bg: '#E8F5E9', color: '#4CAF50', label: 'Completed', icon: <svg.NotificationCheckSvg />};
      case 'rejected':
        return {bg: '#FFEBEE', color: '#F44336', label: 'Rejected', icon: <svg.RejectedSvg />};
      case 'incomplete':
        return {bg: '#FBE9E7', color: '#FF5722', label: 'Incomplete', icon: <svg.AlertSvg />};
      case 'cancelled':
        return {bg: '#F3F4F6', color: '#6B7280', label: 'Cancelled', icon: <svg.RejectedSvg />};
      case 'pending_approval':
      default:
        return {bg: '#FFF3E0', color: '#FF9800', label: 'Pending Approval', icon: <svg.AlertSvg />};
    }
  };

  const statusConfig = getStatusConfig(task.status);

  const renderHeader = () => {
    const canEdit = ['draft', 'pending_approval', 'approved'].includes(task.status) &&
      (hasPermission('task:edit') || task.assigned_by?.id == authSlice.userId);
    const canAddDocument = task.assigned_to?.some((u: any) => u.id == authSlice.userId) ||
      task.assigned_by?.id == authSlice.userId;
    const canDelete = task.status === 'draft' &&
      (hasPermission('task:delete') || task.assigned_by?.id == authSlice.userId);
    const isCreator = task.assigned_by?.id == authSlice.userId;
    const canCancel = ['pending_approval', 'approved'].includes(task.status) && (hasPermission('task:approve') || isCreator);
    // Grup perjalanan hanya utk task yg masih berjalan; hak kelola dari backend (pembuat/assignee/checker/admin/approver)
    const canManageGroup = tripGroup.canManage && ['draft', 'pending_approval', 'approved', 'in_progress', 'in_review', 'on_hold'].includes(task.status);
    const showKebab = canEdit || canAddDocument || canDelete || canCancel || canManageGroup;

    const kebabSlot = showKebab ? (
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setShowKebabMenu(prev => !prev)}
          style={{
            height: 52, width: 52, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'none', border: 'none', cursor: 'pointer',
          }}
        >
          <MoreVertIcon size={24} color={theme.colors.mainDark} />
        </button>
        {showKebabMenu && (
          <>
            <div
              onClick={() => setShowKebabMenu(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 99 }}
            />
            <div style={{
              position: 'absolute', top: 52, right: 0, zIndex: 100,
              backgroundColor: '#fff', borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
              minWidth: 220, overflow: 'hidden', whiteSpace: 'nowrap',
            }}>
              {(() => {
                const sep = '1px solid #F3F4F6';
                const itemStyle = (isLast: boolean) => ({ padding: '10px 16px', cursor: 'pointer' as const, display: 'flex', alignItems: 'center', gap: 10, borderBottom: isLast ? 'none' : sep });
                const visibleItems = [canEdit, canAddDocument, canManageGroup, canDelete, canCancel];
                const lastIdx = visibleItems.lastIndexOf(true);
                return (
                  <>
                    {canEdit && (
                      <div onClick={() => { setShowKebabMenu(false); navigate('/EditTask', { state: { task } }); }} style={itemStyle(lastIdx === 0)}>
                        <EditIcon size={16} color={theme.colors.mainColor} />
                        <span style={{ fontSize: 14, color: theme.colors.mainDark }}>Edit Task</span>
                      </div>
                    )}
                    {canAddDocument && (
                      <div onClick={() => { setShowKebabMenu(false); setShowAddModal(true); }} style={itemStyle(lastIdx === 1)}>
                        <AttachFileIcon size={16} color={theme.colors.mainColor} />
                        <span style={{ fontSize: 14, color: theme.colors.mainDark }}>Tambah Dokumen</span>
                      </div>
                    )}
                    {canManageGroup && (
                      <div onClick={() => { setShowKebabMenu(false); tripGroup.openPicker(); }} style={itemStyle(lastIdx === 2)}>
                        <GroupWorkIcon size={16} color="#0284C7" />
                        <span style={{ fontSize: 14, color: theme.colors.mainDark }}>{(t as any).tripGroup?.menuAdd || 'Tambah ke Grup Perjalanan'}</span>
                      </div>
                    )}
                    {canDelete && (
                      <div onClick={() => { setShowKebabMenu(false); setShowDeleteModal(true); }} style={itemStyle(lastIdx === 3)}>
                        <DeleteIcon size={16} color="#EF4444" />
                        <span style={{ fontSize: 14, color: '#EF4444' }}>Hapus Task</span>
                      </div>
                    )}
                    {canCancel && (
                      <div onClick={() => { setShowKebabMenu(false); setCancelNote(''); setShowCancelModal(true); }} style={itemStyle(lastIdx === 4)}>
                        <svg width={16} height={16} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2"/><line x1="15" y1="9" x2="9" y2="15" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/><line x1="9" y1="9" x2="15" y2="15" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/></svg>
                        <span style={{ fontSize: 14, color: '#DC2626' }}>Batalkan Task</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </>
        )}
      </div>
    ) : undefined;

    return (
      <components.Header title={t.taskDetails.title} goBack={true} rightContent={kebabSlot} />
    );
  };

  // compact: versi rapat untuk area sticky di layout tab
  const renderStatusCard = (compact = false) => (
    <div style={{
      backgroundColor: statusConfig.bg,
      padding: compact ? '8px 12px' : '12px 16px',
      borderRadius: compact ? 12 : 16,
      marginBottom: compact ? 8 : 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `1px solid ${statusConfig.color}20`
    }}>
      <div style={{display: 'flex', alignItems: 'center', gap: 16}}>
        <div style={{
          width: compact ? 32 : 48, height: compact ? 32 : 48, borderRadius: compact ? 16 : 24,
          backgroundColor: theme.colors.white,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: statusConfig.color
        }}>
          {/* Placeholder for icon if svg not perfectly matching, but using color helps */}
          <div style={{width: 12, height: 12, borderRadius: 6, backgroundColor: statusConfig.color}} />
        </div>
        <div>
          {!compact && <text.T14 style={{color: statusConfig.color, opacity: 0.8, marginBottom: 4}}>{t.taskDetails.currentStatus}</text.T14>}
          {compact
            ? <text.H5 style={{color: statusConfig.color}}>{statusConfig.label == 'approved' ? t.taskDetails.awaitingReport : statusConfig.label}</text.H5>
            : <text.H3 style={{color: statusConfig.color}}>{statusConfig.label == 'approved' ? t.taskDetails.awaitingReport : statusConfig.label}</text.H3>}
        </div>
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end'}}>
        <text.T12 style={{color: statusConfig.color, fontWeight: 'bold'}}>#{task.id}</text.T12>
        {tripGroup.inGroup && (
          <div
            onClick={() => isTabLayout && setTabRequest({ tab: 'group', nonce: Date.now() })}
            style={{padding: '2px 8px', backgroundColor: '#E0F2FE', borderRadius: 8, border: '1px solid #7DD3FC', cursor: isTabLayout ? 'pointer' : 'default'}}
          >
            <text.T14 style={{color: '#0369A1', fontWeight: 'bold', fontSize: 12}}>
              {((t as any).tripGroup?.chip || 'Grup · {n} kapal').split('{n}').join(String(tripGroup.memberCount))}
            </text.T14>
          </div>
        )}
        {task.is_urgent && (
          <div style={{
            padding: '2px 8px',
            backgroundColor: '#FEE2E2',
            borderRadius: 8,
            border: '1px solid #FCA5A5'
          }}>
            <text.T14 style={{color: '#EF4444', fontWeight: 'bold', fontSize: 12}}>
              URGENT
            </text.T14>
          </div>
        )}
        {task.is_maintenance && (
          <div style={{
            padding: '2px 8px',
            backgroundColor: '#EFF6FF',
            borderRadius: 8,
            border: '1px solid #BFDBFE'
          }}>
            <text.T14 style={{color: '#3B82F6', fontWeight: 'bold', fontSize: 12}}>
              MAINTENANCE
            </text.T14>
          </div>
        )}
      </div>
    </div>
  );

  const renderProgressTracker = () => {
    const steps = [
        { key: 'draft', label: 'Draft' },
        { key: 'pending_approval', label: 'Pending' },
        { key: 'approved', label: 'Approved' },
        { key: 'in_progress', label: 'In Progress' },
        { key: 'in_review', label: 'In Review' },
        { key: 'completed', label: 'Completed' }
    ];
    
    const currentStatus = task.status?.toLowerCase();
    let currentIndex = steps.findIndex(s => s.key === currentStatus);
    
    // Handle edge cases if status is not in the main flow (e.g. rejected)
    if (currentIndex === -1) {
        if (currentStatus === 'rejected') currentIndex = 3; 
        if (currentStatus === 'incomplete') currentIndex = 4;
        if (currentStatus === 'on_hold') currentIndex = 3;
    }

    return (
        <div style={{marginBottom: 24}}>
            <text.H5 style={{marginBottom: 20}}>Progress Status</text.H5>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', margin: '0 -10px'}}>
                {/* Line Background */}
                <div style={{position: 'absolute', top: 9, left: 30, right: 30, height: 2, backgroundColor: '#E0E0E0', zIndex: 0}} />
                
                {/* Active Line */}
                <div style={{
                    position: 'absolute', top: 9, left: 30, 
                    width: currentIndex <= 0 ? 0 : `calc(${(currentIndex / (steps.length - 1)) * 100}% - ${(currentIndex / (steps.length - 1)) * 60}px)`,
                    height: 2, backgroundColor: theme.colors.mainColor, zIndex: 0,
                    transition: 'width 0.3s ease'
                }} />

                {steps.map((step, index) => {
                    const isActive = index <= currentIndex;
                    const isCurrent = index === currentIndex;
                    
                    return (
                        <div key={step.key} style={{display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, width: 60}}>
                            <div style={{
                                width: 20, height: 20, borderRadius: 10,
                                backgroundColor: isActive ? theme.colors.mainColor : '#E0E0E0',
                                border: isCurrent ? `3px solid #E3F2FD` : '2px solid white',
                                boxShadow: isCurrent ? '0 0 0 2px ' + theme.colors.mainColor : 'none',
                                marginBottom: 8,
                                transition: 'all 0.3s ease'
                            }} />
                            <text.T14 style={{
                                fontSize: 10, 
                                textAlign: 'center', 
                                color: isActive ? theme.colors.mainDark : '#9CA3AF',
                                fontWeight: isActive ? '600' : '400'
                            }}>
                                {step.label}
                            </text.T14>
                        </div>
                    );
                })}
            </div>
        </div>
    );
  };

  const renderAttachmentModal = () => {
    if (!selectedAttachment) return null;

    const hasNext = selectedAttachment.gallery && selectedAttachment.galleryIndex !== undefined && selectedAttachment.galleryIndex < selectedAttachment.gallery.length - 1;
    const hasPrev = selectedAttachment.gallery && selectedAttachment.galleryIndex !== undefined && selectedAttachment.galleryIndex > 0;

    const handleNext = (e: any) => {
        e.stopPropagation();
        if (hasNext) {
            const nextItem = selectedAttachment.gallery![selectedAttachment.galleryIndex! + 1];
            setSelectedAttachment({ ...nextItem, galleryIndex: selectedAttachment.galleryIndex! + 1, gallery: selectedAttachment.gallery });
        }
    };

    const handlePrev = (e: any) => {
        e.stopPropagation();
        if (hasPrev) {
            const prevItem = selectedAttachment.gallery![selectedAttachment.galleryIndex! - 1];
            setSelectedAttachment({ ...prevItem, galleryIndex: selectedAttachment.galleryIndex! - 1, gallery: selectedAttachment.gallery });
        }
    };

    const handleImgTouchStart = (e: React.TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchRef.current = { startDist: Math.hypot(dx, dy), startZoom: imgZoom };
        dragRef.current = null;
      } else if (e.touches.length === 1) {
        dragRef.current = {
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startTx: imgTranslate.x,
          startTy: imgTranslate.y,
        };
      }
    };

    const handleImgTouchMove = (e: React.TouchEvent) => {
      e.stopPropagation();
      if (e.touches.length === 2 && pinchRef.current) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const newZoom = Math.min(5, Math.max(1, (Math.hypot(dx, dy) / pinchRef.current.startDist) * pinchRef.current.startZoom));
        setImgZoom(newZoom);
        if (newZoom <= 1) setImgTranslate({ x: 0, y: 0 });
      } else if (e.touches.length === 1 && dragRef.current && imgZoom > 1) {
        const dx = e.touches[0].clientX - dragRef.current.startX;
        const dy = e.touches[0].clientY - dragRef.current.startY;
        setImgTranslate({ x: dragRef.current.startTx + dx, y: dragRef.current.startTy + dy });
      }
    };

    const handleImgTouchEnd = (e: React.TouchEvent) => {
      e.stopPropagation();
      pinchRef.current = null;
      dragRef.current = null;
      if (e.changedTouches.length === 1) {
        const now = Date.now();
        const dt = now - lastTapRef.current;
        lastTapRef.current = now;
        if (dt < 300 && dt > 0) {
          if (imgZoom > 1) {
            setImgZoom(1);
            setImgTranslate({ x: 0, y: 0 });
          } else {
            setImgZoom(2.5);
          }
        }
      }
    };

    return (
      <div 
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)', // Opacity dikurangi agar blur terlihat
          backdropFilter: 'blur(50px)', // Blur ditingkatkan
          WebkitBackdropFilter: 'blur(20px)', // Support Safari
          zIndex: 2000,
          display: 'flex', flexDirection: 'column', 
          animation: 'fadeIn 0.2s ease-out'
        }}
        onClick={() => setSelectedAttachment(null)}
      >
        {/* Close Button - Floating & Modern */}
        <div 
            style={{
                position: 'absolute', top: 20, right: 20, 
                cursor: 'pointer', 
                width: 44, height: 44, borderRadius: 22,
                backgroundColor: 'rgba(255,255,255,0.1)', // Glass effect
                backdropFilter: 'blur(5px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2002,
                border: '1px solid rgba(255,255,255,0.1)',
                transition: 'background-color 0.2s'
            }}
            onClick={() => setSelectedAttachment(null)}
        >
            <CloseIcon size={24} color="white" />
        </div>

        {/* Main Content Area - Centered */}
        <div 
            style={{
                flex: 1, 
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20, overflow: 'hidden', width: '100%', position: 'relative'
            }}
        >
            {hasPrev && imgZoom <= 1 && (
                <div
                    onClick={handlePrev}
                    style={{
                        position: 'absolute', left: 16,
                        width: 44, height: 44, borderRadius: 22,
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        border: '1.5px solid rgba(255,255,255,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 2005,
                    }}
                >
                    <ChevronLeftIcon size={32} color="white" />
                </div>
            )}

            {selectedAttachment.type === 'image' ? (
                <img
                    src={selectedAttachment.url}
                    style={{
                        maxWidth: '100%', maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: imgZoom <= 1 ? 8 : 0,
                        boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
                        transform: `scale(${imgZoom}) translate(${imgTranslate.x / imgZoom}px, ${imgTranslate.y / imgZoom}px)`,
                        transformOrigin: 'center center',
                        transition: imgZoom <= 1 ? 'transform 0.2s ease' : 'none',
                        touchAction: 'none',
                        userSelect: 'none',
                        cursor: imgZoom > 1 ? 'grab' : 'default',
                    }}
                    onTouchStart={handleImgTouchStart}
                    onTouchMove={handleImgTouchMove}
                    onTouchEnd={handleImgTouchEnd}
                    onClick={(e) => e.stopPropagation()}
                    alt={selectedAttachment.name}
                />
            ) : selectedAttachment.type === 'pdf' ? (
                <div
                    style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'white', borderRadius: 8 }}>
                        <div style={{ width: `${attachmentPdfZoom * 100}%`, height: `${attachmentPdfZoom * 100}%`, minHeight: '100%' }}>
                            <iframe
                                src={`${selectedAttachment.url}#toolbar=1&navpanes=0`}
                                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                                title={selectedAttachment.name}
                            />
                        </div>
                    </div>
                    <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: 4, backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)', borderRadius: 20, padding: '5px 14px', zIndex: 5 }}>
                        <button onClick={(e) => { e.stopPropagation(); setAttachmentPdfZoom(z => Math.max(0.5, z - 0.25)); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 28 }}>−</button>
                        <span style={{ color: 'white', fontSize: 13, minWidth: 38, textAlign: 'center' }}>{Math.round(attachmentPdfZoom * 100)}%</span>
                        <button onClick={(e) => { e.stopPropagation(); setAttachmentPdfZoom(z => Math.min(3, z + 0.25)); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 28 }}>+</button>
                    </div>
                </div>
            ) : (
                <div
                    style={{
                        backgroundColor: 'white', padding: 40, borderRadius: 24,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
                        maxWidth: 320, width: '100%',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div style={{
                        width: 80, height: 80, borderRadius: 40,
                        backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        {selectedAttachment.icon && React.cloneElement(selectedAttachment.icon as React.ReactElement, { size: 40 })}
                    </div>
                    <text.H5 style={{textAlign: 'center', wordBreak: 'break-word'}}>{selectedAttachment.name}</text.H5>
                </div>
            )}

            {hasNext && imgZoom <= 1 && (
                <div
                    onClick={handleNext}
                    style={{
                        position: 'absolute', right: 16,
                        width: 44, height: 44, borderRadius: 22,
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        border: '1.5px solid rgba(255,255,255,0.4)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 2005,
                    }}
                >
                    <ChevronRightIcon size={32} color="white" />
                </div>
            )}
        </div>

        {/* Bottom Panel - Glassmorphism & Gradient Overlay */}
        <div 
            style={{
                width: '100%',
                backgroundColor: '#111827', // Panel solid gelap (Gray 900)
                borderTop: '1px solid #374151', // Border pemisah halus
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                boxShadow: '0 -4px 20px rgba(0,0,0,0.4)',
                padding: '20px 24px 40px', // Extra bottom padding
                display: 'flex', flexDirection: 'column', gap: 16
            }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Note & Uploader Section */}
            {(selectedAttachment.note || selectedAttachment.submitted_by) && (
                <div style={{
                    backgroundColor: '#1F2937',
                    padding: '16px', borderRadius: 12,
                    borderLeft: `4px solid ${theme.colors.mainColor}`
                }}>
                    {selectedAttachment.note && (
                        <>
                            <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6}}>
                                 <div style={{width: 16, height: 16, borderRadius: 8, backgroundColor: theme.colors.mainColor, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                     <text.T14 style={{fontSize: 10, color: 'white', fontWeight: 'bold'}}>i</text.T14>
                                 </div>
                                 <text.T14 style={{color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: '700', letterSpacing: 0.5}}>{t.taskDetails.notes.toUpperCase()}</text.T14>
                            </div>
                            <text.T16 style={{color: 'white', lineHeight: 1.5}}>{selectedAttachment.note}</text.T16>
                        </>
                    )}
                    {selectedAttachment.submitted_by && (
                        <div style={{display: 'flex', alignItems: 'center', gap: 6, marginTop: selectedAttachment.note ? 10 : 0}}>
                            <text.T14 style={{color: 'rgba(255,255,255,0.5)', fontSize: 11}}>{t.taskDetails.uploadedBy}</text.T14>
                            <text.T14 style={{color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: '600'}}>{selectedAttachment.submitted_by}</text.T14>
                        </div>
                    )}
                </div>
            )}

            {/* Controls Row */}
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16}}>
                <div style={{flex: 1, minWidth: 0}}>
                    <text.H5 style={{color: 'white', marginBottom: 4}} numberOfLines={1}>{selectedAttachment.name}</text.H5>
                    <text.T14 style={{color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', fontSize: 11, fontWeight: '600'}}>
                        {selectedAttachment.type}
                    </text.T14>
                </div>

                <div 
                    style={{
                        backgroundColor: 'white', 
                        height: 44, padding: '0 20px',
                        borderRadius: 22, 
                        display: 'flex', alignItems: 'center', gap: 8,
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                        flexShrink: 0
                    }}
                    onClick={async (e) => {
                        e.stopPropagation();
                        await utils.downloadFile(selectedAttachment.url, selectedAttachment.name);
                    }}
                >
                    <DownloadIcon size={18} color={theme.colors.mainDark} />
                    <text.T16 style={{color: theme.colors.mainDark, fontWeight: '600', fontSize: 14}}>{t.taskDetails.downloadFile}</text.T16>
                </div>
            </div>
        </div>
      </div>
    );
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext || '')) return { icon: <FileImageIcon size={40} color="#3B82F6" />, type: 'image' };
    if (['pdf'].includes(ext || '')) return { icon: <FilePdfIcon size={40} color="#EF4444" />, type: 'pdf' };
    if (['xls', 'xlsx', 'csv'].includes(ext || '')) return { icon: <FileExcelIcon size={40} color="#10B981" />, type: 'excel' };
    if (['doc', 'docx'].includes(ext || '')) return { icon: <FileWordIcon size={40} color="#3B82F6" />, type: 'word' };
    if (['ppt', 'pptx'].includes(ext || '')) return { icon: <FilePowerpointIcon size={40} color="#F59E0B" />, type: 'ppt' };
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext || '')) return { icon: <FileArchiveIcon size={40} color="#6B7280" />, type: 'archive' };
    return { icon: <FileAltIcon size={40} color="#9CA3AF" />, type: 'file' };
  };

  const renderPendingModal = () => {
    if (!showPendingModal) return null;
    const pendingTitle = (t.taskDetails as any)?.pendingModal?.title || 'Tandai Pending';
    const pendingLabel = (t.taskDetails as any)?.pendingModal?.noteLabel || 'Alasan / Catatan (opsional)';
    const pendingPlaceholder = (t.taskDetails as any)?.pendingModal?.notePlaceholder || 'Contoh: Menunggu material, koordinasi lapangan, dll...';
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '16px 16px 0 0',
          padding: '24px', width: '100%', maxWidth: 560,
        }}>
          <text.H4 style={{ marginBottom: 16 }}>{pendingTitle}</text.H4>
          <textarea
            value={pendingNote}
            onChange={(e) => setPendingNote(e.target.value)}
            placeholder={pendingPlaceholder}
            rows={4}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: '1px solid #E5E7EB', fontSize: 14, resize: 'none',
              outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
              color: '#374151',
            }}
          />
          <text.T14 style={{ color: '#6B7280', marginBottom: 20, marginTop: 4 }}>{pendingLabel}</text.T14>
          <div style={{ display: 'flex', gap: 12 }}>
            <components.Button
              title={t.common.cancel}
              style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
              containerStyle={{ flex: 1 }}
              onClick={() => setShowPendingModal(false)}
            />
            <components.Button
              title={isLoading ? (t.common.processing || 'Memproses...') : (t.common.save || 'Simpan')}
              style={{ backgroundColor: '#FF9800', color: '#fff' }}
              containerStyle={{ flex: 1 }}
              onClick={submitPending}
            />
          </div>
        </div>
      </div>
    );
  };

  const submitCancel = async () => {
    const noteRequired = (t.taskDetails as any)?.cancelModal?.noteRequired || 'Alasan pembatalan wajib diisi.';
    if (!cancelNote.trim()) {
      showAlert(t.common.error, noteRequired, 'error');
      return;
    }
    try {
      setIsLoading(true);
      await axiosInstance.post(`/tasks/${task.id}/cancel`, { notes: cancelNote.trim() });
      setShowCancelModal(false);
      showAlert(t.common.success, (t.taskDetails as any)?.alerts?.cancelled || 'Task berhasil dibatalkan', 'success', () => {
        listCache.invalidate('tasks');
        navigate(-1);
      });
    } catch (error: any) {
      console.error('Failed to cancel task:', error);
      showAlert(t.common.error, error?.response?.data?.message || t.taskDetails.alerts?.actionFailed || 'Action failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderCancelModal = () => {
    if (!showCancelModal) return null;
    const cancelTitle = (t.taskDetails as any)?.cancelModal?.title || 'Batalkan Task';
    const cancelLabel = (t.taskDetails as any)?.cancelModal?.noteLabel || 'Alasan pembatalan (wajib)';
    const cancelPlaceholder = (t.taskDetails as any)?.cancelModal?.notePlaceholder || 'Contoh: Task tidak lagi diperlukan...';
    const cancelBtn = (t.taskDetails as any)?.cancelModal?.confirmBtn || 'Batalkan Task';
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: '#fff', borderRadius: '16px 16px 0 0',
          padding: '24px', width: '100%', maxWidth: 560,
        }}>
          <text.H4 style={{ marginBottom: 16, color: '#DC2626' }}>{cancelTitle}</text.H4>
          <textarea
            value={cancelNote}
            onChange={(e) => setCancelNote(e.target.value)}
            placeholder={cancelPlaceholder}
            rows={4}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 8,
              border: '1px solid #E5E7EB', fontSize: 14, resize: 'none',
              outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
              color: '#374151',
            }}
          />
          <text.T14 style={{ color: '#6B7280', marginBottom: 20, marginTop: 4 }}>{cancelLabel}</text.T14>
          <div style={{ display: 'flex', gap: 12 }}>
            <components.Button
              title={t.common.cancel}
              style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
              containerStyle={{ flex: 1 }}
              onClick={() => setShowCancelModal(false)}
            />
            <components.Button
              title={isLoading ? (t.common.processing || 'Memproses...') : cancelBtn}
              style={{ backgroundColor: '#DC2626', color: '#fff' }}
              containerStyle={{ flex: 1 }}
              onClick={submitCancel}
            />
          </div>
        </div>
      </div>
    );
  };


  const renderDeleteModal = () => {
    if (!showDeleteModal) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 320, textAlign: 'center',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <text.H4 style={{marginBottom: 12}}>{t.taskDetails.deleteTask}</text.H4>
          <text.T16 style={{marginBottom: 24, color: theme.colors.bodyTextColor}}>
            {t.taskDetails.deleteConfirmation}
          </text.T16>
          <div style={{display: 'flex', gap: 12}}>
            <components.Button 
              title={t.common.cancel} 
              style={{backgroundColor: '#F3F4F6', color: '#4B5563', padding: 10 }} 
              onClick={() => setShowDeleteModal(false)} 
            />
            <components.Button 
              title={isLoading ? t.common.deleting : t.common.delete} 
              style={{backgroundColor: '#EF4444', padding: 10 }}
              onClick={handleDeleteTask} 
            />
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteCommentModal = () => {
    if (!confirmDeleteCommentId) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 320, textAlign: 'center',
          boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
        }}>
          <text.H4 style={{marginBottom: 12}}>{t.taskDetails.deleteComment}</text.H4>
          <text.T16 style={{marginBottom: 24, color: theme.colors.bodyTextColor}}>
            {t.taskDetails.deleteCommentConfirmation}
          </text.T16>
          <div style={{display: 'flex', gap: 12}}>
            <components.Button
              title={t.common.cancel}
              style={{backgroundColor: '#F3F4F6', color: '#4B5563', padding: 10}}
              onClick={() => setConfirmDeleteCommentId(null)}
            />
            <components.Button
              title={deletingCommentId ? t.common.deleting : t.common.delete}
              style={{backgroundColor: '#EF4444', padding: 10}}
              onClick={() => {
                if (deletingCommentId) return;
                handleDeleteComment(confirmDeleteCommentId);
                setConfirmDeleteCommentId(null);
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const handleOpenVesselHistory = async () => {
    if (!task.vessel_id) return;
    setShowVesselHistoryModal(true);
    setIsLoadingVesselTasks(true);
    try {
      const response = await axiosInstance.get(`/vessels/${task.vessel_id}/tasks`);
      setVesselTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch vessel tasks:', error);
      setVesselTasks([]);
    } finally {
      setIsLoadingVesselTasks(false);
    }
  };

  const renderVesselHistoryModal = () => {
    if (!showVesselHistoryModal) return null;

    const getTaskStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
        case 'completed': return { bg: '#E8F5E9', color: '#4CAF50' };
        case 'in_progress': return { bg: '#E0F2FE', color: '#2563EB' };
        case 'in_review': return { bg: '#F3E5F5', color: '#9C27B0' };
        case 'rejected': return { bg: '#FFEBEE', color: '#F44336' };
        case 'approved': return { bg: '#E3F2FD', color: '#2196F3' };
        case 'incomplete': return { bg: '#FBE9E7', color: '#FF5722' };
        case 'on_hold': return { bg: '#FFF3E0', color: '#FF9800' };
        default: return { bg: '#FFF3E0', color: '#FF9800' };
      }
    };

    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'flex-end',
        }}
        onClick={() => setShowVesselHistoryModal(false)}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: '20px 20px 0 0',
            width: '100%', maxWidth: 480, maxHeight: '80vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 20px 16px', borderBottom: '1px solid #F3F4F6', flexShrink: 0
          }}>
            <div>
              <text.H4 style={{ marginBottom: 2 }}>
                {(t.taskDetails as any).vesselHistory || 'Vessel Task History'}
              </text.H4>
              <text.T14 style={{ color: '#9CA3AF', textTransform: 'capitalize' }}>{task.vessel_name}</text.T14>
            </div>
            <div onClick={() => setShowVesselHistoryModal(false)} style={{ cursor: 'pointer', padding: 4 }}>
              <CloseIcon size={24} color="#6B7280" />
            </div>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0 24px' }}>
            {isLoadingVesselTasks ? (
              <div style={{ padding: 40, textAlign: 'center' }}>
                <text.T16 style={{ color: theme.colors.bodyTextColor }}>{t.common.loading}</text.T16>
              </div>
            ) : vesselTasks.filter((vTask: any) => vTask.id !== task.id).length === 0 ? (
              <div style={{ padding: 40 }}>
                <text.T16 style={{ color: theme.colors.bodyTextColor, textAlign: 'center' }}>
                  {(t.taskDetails as any).noVesselTasks || 'No tasks found for this vessel.'}
                </text.T16>
              </div>
            ) : (
              vesselTasks.filter((vTask: any) => vTask.id !== task.id).map((vTask: any) => {
                const statusStyle = getTaskStatusColor(vTask.status);
                const assignedNames: string[] = vTask.assigned_to_names || [];
                return (
                  <div
                    key={vTask.id}
                    onClick={() => {
                      setShowVesselHistoryModal(false);
                      navigate('/TaskDetails', { state: { task: vTask } });
                    }}
                    style={{
                      padding: '14px 20px',
                      borderBottom: '1px solid #F9FAFB',
                      cursor: 'pointer',
                      backgroundColor: 'white',
                      display: 'flex', flexDirection: 'column', gap: 6,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <text.T16
                        style={{
                          color: theme.colors.mainDark, flex: 1,
                          display: '-webkit-box' as any,
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical' as any,
                          overflow: 'hidden',
                        }}
                      >
                        {vTask.description || '-'}
                      </text.T16>
                      <div style={{
                        padding: '2px 8px', borderRadius: 10, flexShrink: 0,
                        backgroundColor: statusStyle.bg,
                      }}>
                        <text.T14 style={{ fontSize: 11, fontWeight: '600', color: statusStyle.color }}>
                          {vTask.status?.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                        </text.T14>
                      </div>
                    </div>
                    {assignedNames.length > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <PersonIcon size={13} color="#9CA3AF" />
                        <text.T14 style={{ color: '#6B7280', fontSize: 12 }}>
                          {assignedNames.join(', ')}
                        </text.T14>
                      </div>
                    )}
                    <text.T14 style={{ color: '#9CA3AF', fontSize: 11 }}>
                      <span style={{ fontWeight: 600, color: '#6B7280' }}>#{vTask.id}</span> · {formatDateTime(vTask.created_at)}
                    </text.T14>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbacks = () => {
    if (!task.feedbacks || task.feedbacks.length === 0) return null;

    return (
      // Mode tab: tab sudah jadi pemisah → tanpa garis & jarak atas
      <div ref={commentsRef} style={isTabLayout ? undefined : {marginTop: 24, borderTop: '1px solid #F4F6F8', paddingTop: 10}}>
        <text.H5 style={{marginBottom: 16, color: theme.colors.mainDark}}>{t.taskDetails.taskReports}</text.H5>
        {task.feedbacks.map((feedback: any) => {
          const generalComments = feedback.comments?.filter((c: any) => !c.feedback_attachment_id) || [];
          return (
            <div key={feedback.id} style={{
            backgroundColor: '#F9FAFB', 
            padding: 16, 
            borderRadius: 12, 
            marginBottom: 16,
            border: '1px solid #F3F4F6'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'flex-start'}}>
               <div style={{display: 'flex', flexDirection: 'column', gap: 2}}>
                   <text.T14 style={{color: '#9CA3AF', fontSize: 12}}>{formatDateTime(feedback.created_at)}</text.T14>
                   {task.assigned_to && task.assigned_to.length > 1 && feedback.submitted_by_name && (
                       <text.T14 style={{color: theme.colors.mainDark, fontSize: 12, fontWeight: '600'}}>
                           {t.taskDetails.submittedBy || 'Dilaporkan oleh'}: {feedback.submitted_by_name}
                       </text.T14>
                   )}
               </div>
               <div style={{
                   padding: '2px 6px', 
                   borderRadius: 4, 
                   backgroundColor: feedback.review_status === 'approved' ? '#E3F2FD' : (feedback.review_status === 'rejected' ? '#FFEBEE' : (feedback.review_status === 'incomplete' ? '#FBE9E7' : '#FFF3E0')),
                   color: feedback.review_status === 'approved' ? '#2196F3' : (feedback.review_status === 'rejected' ? '#F44336' : (feedback.review_status === 'incomplete' ? '#FF5722' : '#FF9800')),
                   fontSize: 10,
                   fontWeight: '600',
                   textTransform: 'uppercase'
               }}>
                   {feedback.review_status?.replace('_', ' ') || 'PENDING'}
               </div>
            </div>

            {feedback.reviewed_by_name && (
                <div style={{marginBottom: 8, marginTop: -4}}>
                    <text.T14 style={{fontSize: 12, color: '#6B7280', fontStyle: 'italic'}}>
                        {t.taskDetails.reviewedBy} {feedback.reviewed_by_name} • {formatDateTime(feedback.reviewed_at)}
                    </text.T14>
                </div>
            )}
            
            {((feedback.notes && feedback.notes.trim() !== '') || (feedback.score !== null && feedback.score !== '')) && (
                <div style={{marginTop: 8, marginBottom: 12, padding: 12, backgroundColor: '#FFFFFF', borderRadius: 8, border: '1px solid #E5E7EB'}}>
                    {(feedback.score !== null && feedback.score !== '') && (
                        <div style={{marginBottom: (feedback.notes && feedback.notes.trim() !== '') ? 8 : 0, display: 'inline-block', backgroundColor: '#EFF6FF', padding: '2px 8px', borderRadius: 6, border: '1px solid #BFDBFE'}}>
                            <text.T14 style={{fontWeight: 'bold', color: '#1D4ED8'}}>Score: {feedback.score}</text.T14>
                        </div>
                    )}
                    {(feedback.notes && feedback.notes.trim() !== '') && (
                        <text.T14 style={{color: '#4B5563', display: 'block', marginTop: (feedback.score !== null && feedback.score !== '') ? 8 : 0}}>
                            <span style={{fontWeight: '600', color: theme.colors.mainDark}}>{t.taskDetails.notes}: </span> 
                            {renderTextWithLinks(feedback.notes)}
                        </text.T14>
                    )}
                </div>
            )}

            <text.T16 style={{marginBottom: 12, display: 'block', color: theme.colors.mainDark, whiteSpace: 'pre-wrap'}}>{renderTextWithLinks(feedback.description)}</text.T16>
            
            {feedback.location && (
                <div style={{display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 12, color: '#6B7280'}}>
                    <LocationIcon size={16} style={{marginTop: 2, flexShrink: 0}} />
                    <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${feedback.location}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{color: '#6B7280', textDecoration: 'underline', lineHeight: 1.4}}
                    >
                        <text.T14 style={{color: 'inherit'}}>{feedback.address || feedback.location}</text.T14>
                    </a>
                </div>
            )}

            {feedback.images && feedback.images.length > 0 && (() => {
                const fixedAttachments = feedback.images.filter((img: any) => img.task_checklist_id);
                const additionalAttachments = feedback.images.filter((img: any) => !img.task_checklist_id);
                const textAttachments = fixedAttachments.filter((img: any) => img.response_value);
                const fileAttachments = fixedAttachments.filter((img: any) => !img.response_value);

                return (
                    <>
                        {/* Text Attachments (Fixed) */}
                        {textAttachments.length > 0 && (
                            <div style={{marginBottom: 16}}>
                                {textAttachments.map((txt: any) => (
                                    <div key={txt.id} style={{marginBottom: 12}}>
                                        <text.T14 style={{color: '#6B7280', marginBottom: 4, fontSize: 12, fontWeight: '600', textTransform: 'uppercase'}}>
                                            {txt.attachment_title}
                                        </text.T14>
                                        <div style={{
                                            padding: '10px 14px',
                                            backgroundColor: '#FFFFFF',
                                            borderRadius: 8,
                                            border: '1px solid #E5E7EB',
                                            fontSize: 14,
                                            color: theme.colors.mainDark,
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                        {renderTextWithLinks(txt.response_value)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* File Attachments (Fixed / Required) — digroup per checklist item */}
                        {fileAttachments.length > 0 && (() => {
                            // Group per task_checklist_id
                            const grouped: Map<string, any[]> = new Map();
                            fileAttachments.forEach((img: any) => {
                                const key = img.task_checklist_id?.toString() ?? 'other';
                                if (!grouped.has(key)) grouped.set(key, []);
                                grouped.get(key)!.push(img);
                            });

                            // Build gallery in display order: per group → before → after → noTiming
                            const orderedImgs: any[] = [];
                            grouped.forEach((items) => {
                                items.filter((i: any) => i.timing === 'before').forEach((i: any) => orderedImgs.push(i));
                                items.filter((i: any) => i.timing === 'after').forEach((i: any) => orderedImgs.push(i));
                                items.filter((i: any) => !i.timing).forEach((i: any) => orderedImgs.push(i));
                            });

                            const fileAttachmentsGallery = orderedImgs.map((img: any) => {
                                const fileUrl = img.file_path ? `${imageBaseUrl}${img.file_path.replace(/\\/g, '/')}` : '';
                                const fileName = img.file_path ? img.file_path.split(/[/\\]/).pop() : '';
                                const { icon, type } = getFileIcon(fileName);
                                return { url: fileUrl, name: fileName, type, icon, note: img.note };
                            });

                            const checkScroll = (k: string) => (e: React.UIEvent<HTMLDivElement>) => {
                                const el = e.currentTarget;
                                const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
                                const atStart = el.scrollLeft <= 4;
                                setScrollAtEnd(prev => ({ ...prev, [k]: atEnd }));
                                setScrollAtStart(prev => ({ ...prev, [k]: atStart }));
                            };

                            const renderThumb = (img: any) => {
                                const globalIdx = orderedImgs.findIndex((i: any) => i.id === img.id);
                                const fileUrl = img.file_path ? `${imageBaseUrl}${img.file_path.replace(/\\/g, '/')}` : '';
                                const fileName = img.file_path ? img.file_path.split(/[/\\]/).pop() : '';
                                const { icon, type } = getFileIcon(fileName);
                                return (
                                    <div key={img.id} style={{ flexShrink: 0, width: 90, height: 90, borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                        onClick={() => setSelectedAttachment({ ...fileAttachmentsGallery[globalIdx], galleryIndex: globalIdx, gallery: fileAttachmentsGallery })}
                                    >
                                        {type === 'image' ? (
                                            <img src={fileUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, textAlign: 'center', width: '100%' }}>
                                                {icon}
                                                <text.T14 style={{ fontSize: 10, marginTop: 4, wordBreak: 'break-word' }} numberOfLines={2}>{fileName}</text.T14>
                                            </div>
                                        )}
                                    </div>
                                );
                            };

                            return (
                                <div style={{ marginBottom: 16 }}>
                                    <text.T14 style={{ color: '#6B7280', marginBottom: 8, fontSize: 12, fontWeight: '600', textTransform: 'uppercase' }}>{t.section.requiredAttachments}</text.T14>
                                    {Array.from(grouped.entries()).map(([key, items]) => {
                                        const title = items[0]?.attachment_title;
                                        const beforeItems = items.filter((img: any) => img.timing === 'before');
                                        const afterItems = items.filter((img: any) => img.timing === 'after');
                                        const noTimingItems = items.filter((img: any) => !img.timing);
                                        const hasTiming = beforeItems.length > 0 || afterItems.length > 0;

                                        return (
                                            <div key={key} style={{ marginBottom: 14 }}>
                                                {title && (
                                                    <text.T14 style={{ fontSize: 12, fontWeight: '600', color: theme.colors.mainDark, marginBottom: 8, display: 'block' }}>
                                                        {title}
                                                    </text.T14>
                                                )}
                                                {hasTiming ? (
                                                    <div style={{ display: 'flex', flexDirection: 'row', border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
                                                        <div style={{ flex: 1, minWidth: 0, padding: '8px 10px' }}>
                                                            <div style={{ display: 'inline-block', backgroundColor: 'rgba(239,68,68,0.12)', color: '#DC2626', fontSize: 10, fontWeight: '700', padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', marginBottom: 6 }}>Before</div>
                                                            {beforeItems.length > 0 ? (() => {
                                                                const sk = `file_${key}_before`;
                                                                return (
                                                                    <div style={{ position: 'relative' }}>
                                                                        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }} onScroll={checkScroll(sk)}>
                                                                            {beforeItems.map(renderThumb)}
                                                                        </div>
                                                                        {scrollAtStart[sk] === false && (
                                                                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 4, width: 28, background: 'linear-gradient(to right, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                                                        )}
                                                                        {beforeItems.length > 1 && !scrollAtEnd[sk] && (
                                                                            <div style={{ position: 'absolute', right: 0, top: 0, bottom: 4, width: 28, background: 'linear-gradient(to left, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                                                        )}
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>—</div>
                                                            )}
                                                        </div>
                                                        <div style={{ width: 1, backgroundColor: '#E5E7EB', flexShrink: 0 }} />
                                                        <div style={{ flex: 1, minWidth: 0, padding: '8px 10px' }}>
                                                            <div style={{ display: 'inline-block', backgroundColor: 'rgba(34,197,94,0.12)', color: '#16A34A', fontSize: 10, fontWeight: '700', padding: '2px 8px', borderRadius: 4, textTransform: 'uppercase', marginBottom: 6 }}>After</div>
                                                            {afterItems.length > 0 ? (() => {
                                                                const sk = `file_${key}_after`;
                                                                return (
                                                                    <div style={{ position: 'relative' }}>
                                                                        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }} onScroll={checkScroll(sk)}>
                                                                            {afterItems.map(renderThumb)}
                                                                        </div>
                                                                        {scrollAtStart[sk] === false && (
                                                                            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 4, width: 28, background: 'linear-gradient(to right, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                                                        )}
                                                                        {afterItems.length > 1 && !scrollAtEnd[sk] && (
                                                                            <div style={{ position: 'absolute', right: 0, top: 0, bottom: 4, width: 28, background: 'linear-gradient(to left, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                                                        )}
                                                                    </div>
                                                                );
                                                            })() : (
                                                                <div style={{ fontSize: 11, color: '#9CA3AF', fontStyle: 'italic' }}>—</div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ) : (() => {
                                                    const sk = `file_${key}_notiming`;
                                                    return (
                                                        <div style={{ position: 'relative' }}>
                                                            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }} onScroll={checkScroll(sk)}>
                                                                {noTimingItems.map(renderThumb)}
                                                            </div>
                                                            {scrollAtStart[sk] === false && (
                                                                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 4, width: 40, background: 'linear-gradient(to right, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                                            )}
                                                            {noTimingItems.length > 2 && !scrollAtEnd[sk] && (
                                                                <div style={{ position: 'absolute', right: 0, top: 0, bottom: 4, width: 40, background: 'linear-gradient(to left, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}

                        {/* Additional Attachments */}
                        {additionalAttachments.length > 0 && (
                            <div>
                                {(textAttachments.length > 0 || fileAttachments.length > 0) && <text.T14 style={{color: '#6B7280', marginBottom: 8, fontSize: 12, fontWeight: '600', textTransform: 'uppercase', marginTop: 8}}>{t.taskDetails.additionalAttachments}</text.T14>}
                                <div style={{ position: 'relative' }}>
                                <div style={{
                                    display: 'flex',
                                    gap: 10,
                                    overflowX: 'auto',
                                    paddingBottom: 8,
                                    scrollbarWidth: 'none',
                                    WebkitOverflowScrolling: 'touch'
                                }} onScroll={(e) => { const el = e.currentTarget; const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4; const atStart = el.scrollLeft <= 4; setScrollAtEnd(prev => ({ ...prev, file_additional: atEnd })); setScrollAtStart(prev => ({ ...prev, file_additional: atStart })); }}>
                                    {(() => {
                                        const additionalAttachmentsGallery = additionalAttachments.map((img: any) => {
                                            const fileUrl = img.file_path ? `${imageBaseUrl}${img.file_path.replace(/\\/g, '/')}` : '';
                                            const fileName = img.file_path ? img.file_path.split(/[/\\]/).pop() : '';
                                            const { icon, type } = getFileIcon(fileName);
                                            return {url: fileUrl, name: fileName, type, icon, note: img.note, submitted_by: feedback.submitted_by_name};
                                        });

                                        return additionalAttachments.map((img: any, idx: number) => {
                                        const fileUrl = img.file_path ? `${imageBaseUrl}${img.file_path.replace(/\\/g, '/')}` : '';
                                        const fileName = img.file_path ? img.file_path.split(/[/\\]/).pop() : '';
                                        const { icon, type } = getFileIcon(fileName);
                                        
                                        return (
                                                <div style={{
                                                    flexShrink: 0,
                                                    minWidth: 100, width: 100, height: 100,
                                                    borderRadius: 8, overflow: 'hidden',
                                                    border: '1px solid #E5E7EB',
                                                    backgroundColor: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    position: 'relative'
                                                }}>
                                                    {type === 'image' ? (
                                                        <img src={fileUrl} alt={fileName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => setSelectedAttachment({ ...additionalAttachmentsGallery[idx], galleryIndex: idx, gallery: additionalAttachmentsGallery })} />
                                                    ) : (
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 4, textAlign: 'center', width: '100%' }} onClick={() => setSelectedAttachment({ ...additionalAttachmentsGallery[idx], galleryIndex: idx, gallery: additionalAttachmentsGallery })}>
                                                            {icon}
                                                            <text.T14 style={{ fontSize: 10, marginTop: 4, wordBreak: 'break-word' }} numberOfLines={2}>{fileName}</text.T14>
                                                        </div>
                                                    )}
                                                {task.is_maintenance && img.timing && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        left: 4,
                                                        backgroundColor: img.timing === 'before' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(34, 197, 94, 0.8)',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: 4,
                                                        fontSize: 9,
                                                        fontWeight: 'bold',
                                                        textTransform: 'uppercase'
                                                    }}>
                                                        {img.timing}
                                                    </div>
                                                )}
                                                </div>
                                        );
                                    });
                                    })()}
                                </div>
                                {scrollAtStart['file_additional'] === false && (
                                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 8, width: 40, background: 'linear-gradient(to right, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                )}
                                {additionalAttachments.length > 2 && !scrollAtEnd['file_additional'] && (
                                    <div style={{ position: 'absolute', right: 0, top: 0, bottom: 8, width: 40, background: 'linear-gradient(to left, #fff 40%, transparent)', pointerEvents: 'none' }} />
                                )}
                                </div>
                            </div>
                        )}
                    </>
                );
            })()}

            {/* Comments Section */}
            <div style={{marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 16}}>
                {generalComments.length > 0 && (
                    <div style={{marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12}}>
                    {generalComments.map((comment: any) => {
                        const isMyComment = comment.user_id == authSlice.userId;
                        return (
                        <div id={`comment-${comment.id}`} key={comment.id} className={!isMyComment ? 'other-user-comment' : ''} style={{backgroundColor: isMyComment ? '#FFFFFF' : '#FEF9C3', padding: 12, borderRadius: 8, border: isMyComment ? '1px solid #F3F4F6' : '1px solid #FDE047'}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4}}>
                                    <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, textTransform: 'capitalize'}}>{comment.user_name}</text.T14>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                        <text.T14 style={{fontSize: 10, color: '#9CA3AF'}}>{formatDateTime(comment.created_at)}</text.T14>
                                        {isMyComment && (
                                            <div
                                                onClick={() => setConfirmDeleteCommentId(comment.id)}
                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                title="Hapus komentar"
                                            >
                                                <DeleteIcon size={14} color="#EF4444" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            {comment.comment && (
                                <text.T14 style={{color: '#4B5563', lineHeight: 1.4, whiteSpace: 'pre-wrap', marginBottom: comment.attachment_url ? 8 : 0}}>{renderTextWithLinks(comment.comment)}</text.T14>
                            )}
                            {comment.attachment_url && (() => {
                                const fileUrl = comment.attachment_url ? `${imageBaseUrl}${comment.attachment_url.replace(/\\/g, '/')}` : '';
                                const fileName = comment.attachment_url.split(/[/\\]/).pop() || 'Attachment';
                                const { icon, type } = getFileIcon(fileName);
                                if (type === 'image') {
                                    return (
                                        <div
                                            style={{ width: 120, height: 120, borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer', position: 'relative' }}
                                            onClick={() => setSelectedAttachment({url: fileUrl, name: fileName, type, icon})}
                                        >
                                            <img src={fileUrl} alt={fileName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                        </div>
                                    );
                                } else {
                                    return (
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: 8,
                                            padding: '8px 12px', backgroundColor: '#F9FAFB', borderRadius: 8,
                                            border: '1px solid #E5E7EB', cursor: 'pointer', maxWidth: '100%'
                                        }}
                                        onClick={() => setSelectedAttachment({url: fileUrl, name: fileName, type, icon})}
                                        >
                                            {React.cloneElement(icon as React.ReactElement, { size: 18, style: { flexShrink: 0 } })}
                                            <text.T14 style={{fontSize: 12, color: theme.colors.mainDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{fileName}</text.T14>
                                        </div>
                                    );
                                }
                            })()}
                                                            </div>
                                                        )})}
                    </div>
                )}

                {/* Comment Input */}
                {(feedback.review_status === 'pending' || feedback.review_status === 'resumed') && !expandedCommentFeedbacks.has(feedback.id) ? (
                  <div
                    onClick={() => setExpandedCommentFeedbacks(prev => { const next = new Set(prev); next.add(feedback.id); return next; })}
                    style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: theme.colors.mainColor, fontSize: 13, fontWeight: '500' }}
                  >
                    <span style={{ fontSize: 16, lineHeight: 1 }}>↩</span>
                    <span>{(t.taskDetails as any)?.showReply || 'Balas'}</span>
                  </div>
                ) : (
                <div style={{display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 8}}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: 88, gap: 8 }}>
                        {commentAttachments[`feedback_${feedback.id}`] && (() => {
                            const att = commentAttachments[`feedback_${feedback.id}`]!;
                            const isImage = att.type.startsWith('image/');
                            const previewUrl = isImage ? URL.createObjectURL(att) : null;
                            return (
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '8px 12px', backgroundColor: '#F3F4F6', borderRadius: 8, border: '1px solid #E5E7EB'
                                }}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden'}}>
                                        {isImage ? (
                                            <img src={previewUrl!} alt="Preview" style={{width: 32, height: 32, objectFit: 'cover', borderRadius: 4}} />
                                        ) : (
                                            <AttachFileIcon size={16} color={theme.colors.mainColor} />
                                        )}
                                        <text.T14 style={{fontSize: 12}} numberOfLines={1}>{att.name}</text.T14>
                                    </div>
                                    <CloseIcon size={16} color="#EF4444" style={{cursor: 'pointer'}} onClick={() => setCommentAttachments({...commentAttachments, [`feedback_${feedback.id}`]: null})} />
                                </div>
                            );
                        })()}
                        <textarea 
                            value={newComments[`feedback_${feedback.id}`] || ''}
                            onChange={(e) => setNewComments({...newComments, [`feedback_${feedback.id}`]: e.target.value})}
                            placeholder={t.taskDetails.replyToAttachmentPlaceholder || "Balas ke lampiran ini..."}
                            style={{
                                flex: 1, height: 120, padding: '10px 12px',
                                borderRadius: 8, border: '1px solid #E5E7EB',
                                fontSize: 14, resize: 'none', fontFamily: 'inherit',
                                outline: 'none', boxSizing: 'border-box'
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendComment(feedback.id);
                                }
                            }}
                        />
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column', gap: 8, width: 55, flexShrink: 0}}>
                        <label style={{cursor: 'pointer', backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, boxSizing: 'border-box'}}>
                            <AttachFileIcon size={20} color="#6B7280" />
                            <input 
                                type="file" 
                                style={{display: 'none'}} 
                                onChange={async (e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                        let file = e.target.files[0];
                                        if (file.type.startsWith('image/')) {
                                          try {
                                            file = await compressImage(file);
                                          } catch (error) {
                                            console.error("Compression error:", error);
                                          }
                                        }
                                        setCommentAttachments({...commentAttachments, [`feedback_${feedback.id}`]: file});
                                    }
                                    e.target.value = '';
                                }} 
                            />
                        </label>
                        {(() => {
                            const fKey = `feedback_${feedback.id}`;
                            const isSending = commentSendingKey === fKey;
                            return (
                                <div
                                    onClick={() => !isSending && handleSendComment(feedback.id)}
                                    style={{
                                        backgroundColor: isSending ? '#9CA3AF' : theme.colors.mainColor,
                                        color: 'white', borderRadius: 8,
                                        cursor: isSending ? 'not-allowed' : 'pointer',
                                        fontWeight: '600', fontSize: isSending ? 11 : 14,
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', justifyContent: 'center',
                                        height: 40, boxSizing: 'border-box', gap: 3, overflow: 'hidden', padding: '0 4px',
                                    }}
                                >
                                    {isSending ? (
                                        <>
                                            <span>{commentUploadProgress}%</span>
                                            <div style={{ width: '80%', height: 3, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 2, overflow: 'hidden' }}>
                                                <div style={{ width: `${commentUploadProgress}%`, height: '100%', backgroundColor: 'white', transition: 'width 0.15s ease' }} />
                                            </div>
                                        </>
                                    ) : (t.taskDetails.send || 'Kirim')}
                                </div>
                            );
                        })()}
                    </div>
                </div>
                )}
            </div>
          </div>
        )})}
      </div>
    );
  };

  const renderCheckerSection = () => {
    if (!task.checker) return null;

    const isChecker = task.checker?.id == authSlice.userId;
    const isPrepared = task.prepared_at != null;
    const isChecked = task.checker_status === 'checked';
    const isPending = task.checker_status === 'pending';
    const isAssignedTo = task.assigned_to?.some((u: any) => u.id == authSlice.userId);

    const allItems: any[] = task.checker_checklists || [];
    // Prepared Items: semua item yang ada di daftar (semua adalah prepared items)
    const preparedItems = allItems;
    // Checked Items: semua item termasuk consumables
    const checkedViewItems = allItems;
    // Missing tools: tools yang tidak diceklis saat pemeriksaan
    const missingToolItems = checkedViewItems.filter((c: any) => c.category === 'tools' && !c.is_checked);

    // Inspeksi mandiri oleh assignee — harus selesai (checked) sebelum checker bisa Pemeriksaan
    const isSelfChecked = task.self_check_status === 'checked';
    const isSelfPending = task.self_check_status === 'pending';
    const selfMissingToolItems = allItems.filter((c: any) => c.category === 'tools' && !c.self_is_checked);

    const badgeBg = isChecked ? '#E8F5E9' : isPending ? '#FEE2E2' : isPrepared ? '#FFF3E0' : '#F3F4F6';
    const badgeColor = isChecked ? '#4CAF50' : isPending ? '#EF4444' : isPrepared ? '#FF9800' : '#9CA3AF';
    const badgeText = isChecked
      ? ((t.checkerTask as any)?.statusChecked || 'Sudah Diperiksa')
      : isPending
        ? (t.checkerTask as any)?.statusPending || 'Item Kurang'
        : isPrepared
          ? ((t.checkerTask as any)?.statusPrepared || 'Sudah Disiapkan')
          : ((t.checkerTask as any)?.statusNotPrepared || 'Belum Disiapkan');

    // ── Modal: Prepared Items ─────────────────────────────────────────────────
    const renderPreparedItemsModal = () => {
      if (!showPreparedItemsModal) return null;
      return (
        <div
          onClick={closePreparedSheet}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 5000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
          }}>
          <div
            ref={sheetPreparedRef}
            onClick={e => e.stopPropagation()}
            onTouchStart={e => { sheetPreparedDragStartY.current = e.touches[0].clientY; sheetPreparedDragActive.current = (sheetPreparedRef.current?.scrollTop ?? 0) === 0; }}
            onTouchMove={e => { if (!sheetPreparedDragActive.current) return; const d = e.touches[0].clientY - sheetPreparedDragStartY.current; if (d > 0) setSheetPreparedDelta(d); }}
            onTouchEnd={() => { if (!sheetPreparedDragActive.current) return; sheetPreparedDragActive.current = false; if (sheetPreparedDelta > 80) { closePreparedSheet(); } else { setSheetPreparedDelta(0); } }}
            style={{
              backgroundColor: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 650,
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
              transform: `translateY(${sheetPreparedDelta}px)`,
              transition: (sheetPreparedDelta === 0 || sheetPreparedIsClosing) ? 'transform 0.26s ease' : 'none',
            }}>
            <div style={{padding: '10px 0 4px'}}>
              <div style={{width: 36, height: 4, borderRadius: 2, backgroundColor: '#E5E7EB', margin: '0 auto'}} />
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px 12px', borderBottom: '1px solid #F3F4F6'}}>
              <div>
                <text.H5 style={{margin: 0}}>{(t.checkerTask as any)?.preparedItemsTitle || 'Prepared Items'}</text.H5>
                <text.T14 style={{color: '#9CA3AF', marginTop: 2}}>{preparedItems.length} {(t.checkerTask as any)?.items || 'item'}</text.T14>
              </div>
              <div onClick={closePreparedSheet} style={{cursor: 'pointer', padding: 4}}>
                <text.T14 style={{color: theme.colors.mainColor, fontWeight: '600', fontSize: 20}}>✕</text.T14>
              </div>
            </div>
            <div style={{overflowY: 'auto', flex: 1, padding: '12px 16px 24px'}}>
              {preparedItems.length === 0 ? (
                <text.T14 style={{color: '#9CA3AF', textAlign: 'center', display: 'block', marginTop: 20}}>
                  {(t.checkerTask as any)?.noChecklistItems || 'Belum ada item.'}
                </text.T14>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  {preparedItems.slice().sort((a: any, b: any) => {
                    const order: Record<string, number> = { 'tools': 0, 'installation items': 1, 'consumables': 2 };
                    return (order[a.category] ?? 3) - (order[b.category] ?? 3);
                  }).map((item: any, idx: number) => {
                    const catLabel = item.category === 'tools' ? 'Tools'
                      : item.category === 'installation items' ? 'Installation'
                      : item.category === 'consumables' ? 'Consumable' : null;
                    const catColor = item.category === 'tools' ? { bg: '#EFF6FF', color: '#1D4ED8' }
                      : item.category === 'installation items' ? { bg: '#F0FDF4', color: '#15803D' }
                      : item.category === 'consumables' ? { bg: '#FFF7ED', color: '#C2410C' }
                      : { bg: '#F3F4F6', color: '#6B7280' };
                    return (
                      <div key={item.id} style={{
                        backgroundColor: '#FAFAFA', borderRadius: 10, padding: '10px 12px',
                        border: '1px solid #E5E7EB'
                      }}>
                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                          <text.T14 style={{color: '#9CA3AF', fontSize: 12, minWidth: 20}}>{idx + 1}.</text.T14>
                          <text.T14 style={{flex: 1, color: theme.colors.mainDark, fontWeight: '500'}}>{item.title}</text.T14>
                          <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                            {catLabel && (
                              <span style={{
                                fontSize: 10, fontWeight: '700', padding: '2px 7px', borderRadius: 99,
                                backgroundColor: catColor.bg, color: catColor.color
                              }}>{catLabel}</span>
                            )}
                            <span style={{
                              fontSize: 11, fontWeight: '600', padding: '2px 8px', borderRadius: 99,
                              backgroundColor: '#EEF2FF', color: '#4338CA'
                            }}>×{item.qty ?? 1}</span>
                          </div>
                        </div>
                        {item.serial_number && (
                          <text.T14 style={{color: '#6B7280', fontSize: 12, marginTop: 4, paddingLeft: 28}}>
                            S/N: <span style={{fontWeight: '600', color: '#374151'}}>{item.serial_number}</span>
                          </text.T14>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    // ── Modal: Checked Items ──────────────────────────────────────────────────
    // ── Baris item hasil inspeksi — dipakai modal Checked Items (checker, is_checked) dan
    // Self-Inspection Items (assignee, self_is_checked) supaya status & qty selalu konsisten.
    // Dicentang = barangnya ada/kembali saat dicek:
    //   tools:              tidak → Missing,   dicentang → OK (atau Replaced, khusus checker)
    //   installation items: tidak → Installed, dicentang → Returned
    //   consumables:        tidak → Used,      dicentang → Returned
    // Returned nantinya dipakai modul inventory (barang masuk lagi ke stok).
    const renderInspectionItem = (item: any, idx: number, checked: boolean, isReplaced = false) => {
      const ct = t.checkerTask as any;
      const isInstallation = item.category === 'installation items';
      const isTools = item.category === 'tools';
      const isConsumable = item.category === 'consumables';
      const isMissing = isTools && !checked;

      type Badge = { label: string; bg: string; color: string; rowBg: string; rowBorder: string };
      const plain = { rowBg: '#FAFAFA', rowBorder: '#E5E7EB' };
      const badge: Badge =
        isMissing ? { label: ct?.itemMissingBadge || 'Missing', bg: '#FEE2E2', color: '#DC2626', rowBg: '#FEF2F2', rowBorder: '#FECACA' }
        : isReplaced ? { label: ct?.itemReplacedBadge || 'Replaced', bg: '#F3E8FF', color: '#7C3AED', rowBg: '#FAF5FF', rowBorder: '#DDD6FE' }
        : isInstallation && !checked ? { label: ct?.itemInstalledBadge || 'Installed', bg: '#EFF6FF', color: '#1D4ED8', rowBg: '#F0F9FF', rowBorder: '#BAE6FD' }
        : isConsumable && !checked ? { label: ct?.itemConsumedBadge || 'Used', bg: '#F3F4F6', color: '#6B7280', ...plain }
        : (isInstallation || isConsumable) && checked ? { label: ct?.itemReturnBadge || 'Returned', bg: '#FFF7ED', color: '#C2410C', rowBg: '#FFFBEB', rowBorder: '#FDE68A' }
        : checked ? { label: '✓ OK', bg: '#DCFCE7', color: '#16A34A', ...plain }
        : { label: ct?.itemNotInspectedBadge || 'Belum Dicek', bg: '#F3F4F6', color: '#9CA3AF', ...plain };

      // Qty: item yg ada/kembali dgn jumlah beda dari persiapan → "×persiapan → ×saat dicek"
      const prepared = item.prepared_qty != null ? Number(item.prepared_qty) : null;
      const qty = item.qty != null ? Number(item.qty) : null;
      const qtyChanged = checked && prepared != null && qty != null && prepared !== qty;
      const qtyShown = checked ? (qty ?? prepared) : (prepared ?? qty);
      const chip = { fontSize: 11, fontWeight: '600' as const, padding: '2px 8px', borderRadius: 99, backgroundColor: '#EEF2FF', color: '#4338CA' };

      return (
        <div key={item.id} style={{ backgroundColor: badge.rowBg, borderRadius: 10, padding: '10px 12px', border: `1px solid ${badge.rowBorder}` }}>
          <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <text.T14 style={{color: '#9CA3AF', fontSize: 12, minWidth: 20}}>{idx + 1}.</text.T14>
            <text.T14 style={{ flex: 1, fontWeight: isMissing ? '600' : '500', color: isMissing ? '#DC2626' : theme.colors.mainDark }}>{item.title}</text.T14>
            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
              {qtyChanged ? <span style={chip}>×{prepared} → ×{qty}</span> : qtyShown != null ? <span style={chip}>×{qtyShown}</span> : null}
              <span style={{fontSize: 10, fontWeight: '700', padding: '2px 8px', borderRadius: 99, backgroundColor: badge.bg, color: badge.color}}>{badge.label}</span>
            </div>
          </div>
          {item.serial_number && isInstallation && (
            <text.T14 style={{color: '#6B7280', fontSize: 12, marginTop: 4, paddingLeft: 28}}>
              S/N: <span style={{fontWeight: '600', color: '#374151'}}>{item.serial_number}</span>
            </text.T14>
          )}
        </div>
      );
    };

    const renderCheckedItemsModal = () => {
      if (!showCheckedItemsModal) return null;
      return (
        <div
          onClick={closeCheckedSheet}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 5000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
          }}>
          <div
            ref={sheetCheckedRef}
            onClick={e => e.stopPropagation()}
            onTouchStart={e => { sheetCheckedDragStartY.current = e.touches[0].clientY; sheetCheckedDragActive.current = (sheetCheckedRef.current?.scrollTop ?? 0) === 0; }}
            onTouchMove={e => { if (!sheetCheckedDragActive.current) return; const d = e.touches[0].clientY - sheetCheckedDragStartY.current; if (d > 0) setSheetCheckedDelta(d); }}
            onTouchEnd={() => { if (!sheetCheckedDragActive.current) return; sheetCheckedDragActive.current = false; if (sheetCheckedDelta > 80) { closeCheckedSheet(); } else { setSheetCheckedDelta(0); } }}
            style={{
              backgroundColor: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 650,
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
              transform: `translateY(${sheetCheckedDelta}px)`,
              transition: (sheetCheckedDelta === 0 || sheetCheckedIsClosing) ? 'transform 0.26s ease' : 'none',
            }}>
            <div style={{padding: '10px 0 4px'}}>
              <div style={{width: 36, height: 4, borderRadius: 2, backgroundColor: '#E5E7EB', margin: '0 auto'}} />
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px 12px', borderBottom: '1px solid #F3F4F6'}}>
              <div>
                <text.H5 style={{margin: 0}}>{(t.checkerTask as any)?.checkedItemsTitle || 'Checked Items'}</text.H5>
                <text.T14 style={{color: '#9CA3AF', marginTop: 2}}>{checkedViewItems.length} {(t.checkerTask as any)?.items || 'item'}</text.T14>
              </div>
              <div onClick={closeCheckedSheet} style={{cursor: 'pointer', padding: 4}}>
                <text.T14 style={{color: theme.colors.mainColor, fontWeight: '600', fontSize: 20}}>✕</text.T14>
              </div>
            </div>
            <div style={{overflowY: 'auto', flex: 1, padding: '12px 16px 24px'}}>
              {checkedViewItems.length === 0 ? (
                <text.T14 style={{color: '#9CA3AF', textAlign: 'center', display: 'block', marginTop: 20}}>
                  {(t.checkerTask as any)?.noChecklistItems || 'Belum ada item.'}
                </text.T14>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  {checkedViewItems.map((item: any, idx: number) =>
                    renderInspectionItem(item, idx, !!item.is_checked, item.category === 'tools' && item.is_checked && item.is_replaced))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    // ── Modal: Self-Checked Items (inspeksi mandiri assignee) ──────────────────
    const renderSelfCheckedItemsModal = () => {
      if (!showSelfCheckedItemsModal) return null;
      return (
        <div
          onClick={closeSelfCheckedSheet}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 5000,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
          }}>
          <div
            ref={sheetSelfCheckedRef}
            onClick={e => e.stopPropagation()}
            onTouchStart={e => { sheetSelfCheckedDragStartY.current = e.touches[0].clientY; sheetSelfCheckedDragActive.current = (sheetSelfCheckedRef.current?.scrollTop ?? 0) === 0; }}
            onTouchMove={e => { if (!sheetSelfCheckedDragActive.current) return; const d = e.touches[0].clientY - sheetSelfCheckedDragStartY.current; if (d > 0) setSheetSelfCheckedDelta(d); }}
            onTouchEnd={() => { if (!sheetSelfCheckedDragActive.current) return; sheetSelfCheckedDragActive.current = false; if (sheetSelfCheckedDelta > 80) { closeSelfCheckedSheet(); } else { setSheetSelfCheckedDelta(0); } }}
            style={{
              backgroundColor: '#fff', borderRadius: '16px 16px 0 0', width: '100%', maxWidth: 650,
              maxHeight: '80vh', display: 'flex', flexDirection: 'column',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
              transform: `translateY(${sheetSelfCheckedDelta}px)`,
              transition: (sheetSelfCheckedDelta === 0 || sheetSelfCheckedIsClosing) ? 'transform 0.26s ease' : 'none',
            }}>
            <div style={{padding: '10px 0 4px'}}>
              <div style={{width: 36, height: 4, borderRadius: 2, backgroundColor: '#E5E7EB', margin: '0 auto'}} />
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px 12px', borderBottom: '1px solid #F3F4F6'}}>
              <div>
                <text.H5 style={{margin: 0}}>{(t.checkerTask as any)?.selfCheckedItemsTitle || 'Item Inspeksi Mandiri'}</text.H5>
                <text.T14 style={{color: '#9CA3AF', marginTop: 2}}>{allItems.length} {(t.checkerTask as any)?.items || 'item'}</text.T14>
              </div>
              <div onClick={closeSelfCheckedSheet} style={{cursor: 'pointer', padding: 4}}>
                <text.T14 style={{color: theme.colors.mainColor, fontWeight: '600', fontSize: 20}}>✕</text.T14>
              </div>
            </div>
            <div style={{overflowY: 'auto', flex: 1, padding: '12px 16px 24px'}}>
              {allItems.length === 0 ? (
                <text.T14 style={{color: '#9CA3AF', textAlign: 'center', display: 'block', marginTop: 20}}>
                  {(t.checkerTask as any)?.noChecklistItems || 'Belum ada item.'}
                </text.T14>
              ) : (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                  {allItems.slice().sort((a: any, b: any) => {
                    const order: Record<string, number> = { 'tools': 0, 'installation items': 1, 'consumables': 2 };
                    return (order[a.category] ?? 3) - (order[b.category] ?? 3);
                  }).map((item: any, idx: number) => renderInspectionItem(item, idx, !!item.self_is_checked))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    return (
      <>
        {renderPreparedItemsModal()}
        {renderCheckedItemsModal()}
        {renderSelfCheckedItemsModal()}
        {/* Mode tab: tab sudah jadi pemisah → tanpa garis & jarak atas */}
        <div style={isTabLayout ? undefined : {marginTop: 24, borderTop: '1px solid #F4F6F8', paddingTop: 10}}>
          <text.H5 style={{marginBottom: 16, color: theme.colors.mainDark}}>{t.taskDetails.checker || 'Pemeriksaan Tugas'}</text.H5>

          <div style={{
            backgroundColor: '#F9FAFB',
            padding: 16,
            borderRadius: 12,
            border: '1px solid #F3F4F6'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 16, alignItems: 'flex-start'}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 14, textTransform: 'capitalize'}}>
                <div style={{
                    width: 40, height: 40, borderRadius: 20,
                    backgroundColor: '#E0E0E0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, fontWeight: 'bold', color: '#757575'
                }}>
                    {task.checker.name?.charAt(0) || '?'}
                </div>
                <div>
                    <text.H5>{task.checker.name}</text.H5>
                    <text.T14 style={{color: '#9CA3AF'}}>{task.checker.group_name || (t.checkerTask as any)?.checkerRole || 'Pemeriksa'}</text.T14>
                </div>
              </div>

              <div style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  backgroundColor: badgeBg,
                  color: badgeColor,
                  fontSize: 10,
                  fontWeight: '600',
                  textTransform: 'uppercase'
              }}>
                  {badgeText}
              </div>
            </div>

            {isPrepared && task.prepared_at && (
              <text.T14 style={{display: 'block', marginBottom: 8, color: '#6B7280'}}>
                {(t.checkerTask as any)?.preparedAt || 'Disiapkan pada:'} {formatDateTime(task.prepared_at)}
              </text.T14>
            )}

            {task.prepare_note && (
              <div style={{marginBottom: 12, padding: 12, backgroundColor: '#FFFFFF', borderRadius: 8, border: '1px solid #E5E7EB'}}>
                <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, marginBottom: 4}}>{(t.checkerTask as any)?.preparationNote || 'Catatan Persiapan:'}</text.T14>
                <text.T14 style={{color: '#4B5563', whiteSpace: 'pre-wrap'}}>{task.prepare_note}</text.T14>
              </div>
            )}

            {isPrepared && preparedItems.length > 0 && (
              <div
                onClick={() => setShowPreparedItemsModal(true)}
                style={{
                  marginBottom: 12, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  backgroundColor: '#F0FDF4', border: '1px solid #86EFAC',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}
              >
                <text.T14 style={{color: '#15803D', fontWeight: '600', fontSize: 13}}>
                  📦 {(t.checkerTask as any)?.btnPreparedItems || 'Prepared Items'}
                </text.T14>
                <span style={{
                  backgroundColor: '#15803D', color: '#fff', borderRadius: 99,
                  fontSize: 11, fontWeight: '700', padding: '1px 6px'
                }}>{preparedItems.length}</span>
              </div>
            )}

            {task.checked_at && (
              <text.T14 style={{display: 'block', marginBottom: 8, color: '#6B7280'}}>
                {(t.checkerTask as any)?.checkedAt || 'Diperiksa pada:'} {formatDateTime(task.checked_at)}
              </text.T14>
            )}

            {task.checker_note && (
              <div style={{marginBottom: 12, padding: 12, backgroundColor: '#FFFFFF', borderRadius: 8, border: '1px solid #E5E7EB'}}>
                <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, marginBottom: 4}}>{(t.checkerTask as any)?.checkerNote || 'Catatan Pemeriksa:'}</text.T14>
                <text.T14 style={{color: '#4B5563', whiteSpace: 'pre-wrap'}}>{task.checker_note}</text.T14>
              </div>
            )}

            {(isChecked || isPending) && checkedViewItems.length > 0 && (
              <div
                onClick={() => setShowCheckedItemsModal(true)}
                style={{
                  marginBottom: 12, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  backgroundColor: isPending ? '#FEF2F2' : '#EFF6FF',
                  border: `1px solid ${isPending ? '#FECACA' : '#BFDBFE'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}
              >
                <text.T14 style={{color: isPending ? '#DC2626' : '#1D4ED8', fontWeight: '600', fontSize: 13}}>
                  {isPending ? '⚠️' : '🔍'} {(t.checkerTask as any)?.btnCheckedItems || 'Checked Items'}
                </text.T14>
                {isPending && missingToolItems.length > 0 && (
                  <span style={{
                    backgroundColor: '#DC2626', color: '#fff', borderRadius: 99,
                    fontSize: 11, fontWeight: '700', padding: '1px 6px'
                  }}>{missingToolItems.length} missing</span>
                )}
              </div>
            )}

            {/* Preparation button — visible for checker on approved status (even if already prepared) */}
            {isChecker && task.status === 'approved' && (
              <div style={{marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8}}>
                <components.Button
                  title={isPrepared ? ((t.checkerTask as any)?.btnEditPreparation || 'Edit Persiapan') : ((t.checkerTask as any)?.btnPreparation || 'Persiapan')}
                  style={{ backgroundColor: isPrepared ? '#F3F4F6' : theme.colors.mainColor, color: isPrepared ? theme.colors.mainDark : '#FFFFFF', border: '1px solid #E5E7EB' }}
                  containerStyle={{ width: '100%' }}
                  onClick={() => navigate('/CheckerTask', { state: { task, type: 'persiapan' } })}
                />
                {!task.linked_checker_report && !isPrepared && (
                  <components.Button
                    title={(t as any).preCheckReport?.btnLink || 'Lampirkan Pre-Check Report'}
                    style={{ backgroundColor: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' }}
                    containerStyle={{ width: '100%' }}
                    onClick={handleOpenLinkSheet}
                  />
                )}
                {isPrepared && !task.linked_checker_report && (
                  <components.Button
                    title={(t.checkerTask as any)?.btnCancelPreparation || 'Batalkan Persiapan'}
                    style={{ backgroundColor: '#FEE2E2', color: '#DC2626', border: '1px solid #FECACA' }}
                    containerStyle={{ width: '100%' }}
                    onClick={handleCancelPreparation}
                  />
                )}
              </div>
            )}

            {/* Linked pre-check report display */}
            {task.linked_checker_report && (
              <div style={{
                marginTop: 12, backgroundColor: '#F0FDF4', borderRadius: 10, padding: '12px 14px',
                border: '1px solid #86EFAC',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <text.T14 style={{ fontWeight: '700', color: '#166534' }}>
                    📋 {(t as any).preCheckReport?.linkedReportTitle || 'Pre-Check Report Dilampirkan'}
                  </text.T14>
                  <span style={{
                    fontSize: 11, fontWeight: '700', padding: '2px 8px', borderRadius: 99,
                    backgroundColor: '#D1FAE5', color: '#065F46',
                  }}>
                    {task.linked_checker_report.type === 'persiapan'
                      ? ((t as any).preCheckReport?.typePreparation || 'Persiapan')
                      : ((t as any).preCheckReport?.typeInspection || 'Pemeriksaan')}
                  </span>
                </div>
                <text.T14 style={{ color: '#4B5563', fontSize: 12 }}>
                  {new Date(task.linked_checker_report.report_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                  {task.linked_checker_report.vessel_name ? ` · ${task.linked_checker_report.vessel_name}` : ''}
                </text.T14>
                {task.linked_checker_report.note && (
                  <text.T14 style={{ color: '#374151', marginTop: 6, whiteSpace: 'pre-wrap' }}>
                    {task.linked_checker_report.note}
                  </text.T14>
                )}
                {task.linked_checker_report.items?.length > 0 && (
                  <text.T14 style={{ color: '#6B7280', fontSize: 12, marginTop: 4 }}>
                    {task.linked_checker_report.items.length} {(t as any).preCheckReport?.items || 'item checklist'}
                  </text.T14>
                )}
                {isChecker && task.status === 'approved' && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <components.Button
                      title={(t as any).preCheckReport?.btnChange || 'Ganti Dokumen'}
                      style={{ backgroundColor: '#FFFFFF', color: '#15803D', border: '1px solid #86EFAC', padding: '7px 10px', fontSize: 13 }}
                      containerStyle={{ flex: 1 }}
                      onClick={handleChangeReport}
                    />
                    <components.Button
                      title={(t as any).preCheckReport?.btnUnlink || 'Batal Lampiran'}
                      style={{ backgroundColor: '#FEE2E2', color: '#DC2626', padding: '7px 10px', fontSize: 13 }}
                      containerStyle={{ flex: 1 }}
                      onClick={handleUnlinkReport}
                    />
                  </div>
                )}
              </div>
            )}

            {task.self_check_status != null && allItems.length > 0 && (
              <div
                onClick={() => setShowSelfCheckedItemsModal(true)}
                style={{
                  marginTop: 12, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  backgroundColor: isSelfPending ? '#FEF2F2' : '#F0FDF4',
                  border: `1px solid ${isSelfPending ? '#FECACA' : '#86EFAC'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                }}
              >
                <text.T14 style={{color: isSelfPending ? '#DC2626' : '#15803D', fontWeight: '600', fontSize: 13}}>
                  {isSelfPending ? '⚠️' : '👤'} {(t.checkerTask as any)?.btnSelfCheckedItems || 'Item Inspeksi Mandiri'}
                </text.T14>
                {isSelfPending && selfMissingToolItems.length > 0 && (
                  <span style={{
                    backgroundColor: '#DC2626', color: '#fff', borderRadius: 99,
                    fontSize: 11, fontWeight: '700', padding: '1px 6px'
                  }}>{selfMissingToolItems.length} missing</span>
                )}
              </div>
            )}

            {isAssignedTo && (task.status === 'in_review' || task.status === 'completed') && !isSelfChecked && (
              <div style={{marginTop: 12}}>
                {isSelfPending && selfMissingToolItems.length > 0 && (
                  <div style={{
                    backgroundColor: '#FEF2F2', borderRadius: 8, padding: '10px 12px',
                    border: '1px solid #FECACA', marginBottom: 10
                  }}>
                    <text.T14 style={{color: '#DC2626', fontWeight: '600', marginBottom: 4}}>
                      {(t.checkerTask as any)?.selfCheckMissingTitle || 'Item berikut belum ditemukan saat inspeksi mandiri:'}
                    </text.T14>
                    {selfMissingToolItems.map((c: any) => (
                      <text.T14 key={c.id} style={{color: '#991B1B', display: 'block', paddingLeft: 8}}>• {c.title}</text.T14>
                    ))}
                  </div>
                )}
                <components.Button
                  title={(t.checkerTask as any)?.btnSelfInspection || 'Inspeksi Mandiri'}
                  style={{ backgroundColor: theme.colors.mainColor, color: '#FFFFFF' }}
                  containerStyle={{ width: '100%' }}
                  onClick={() => navigate('/CheckerTask', { state: { task, type: 'inspeksi_mandiri' } })}
                />
              </div>
            )}

            {isChecker && (task.status === 'in_review' || task.status === 'completed') && !isChecked && !isSelfChecked && (
              <div style={{
                marginTop: 12, backgroundColor: '#FFFBEB', borderRadius: 8, padding: '10px 12px',
                border: '1px solid #FDE68A', textAlign: 'center'
              }}>
                <text.T14 style={{color: '#92400E', fontStyle: 'italic'}}>
                  {(t.checkerTask as any)?.waitingSelfInspection || 'Menunggu inspeksi mandiri dari pekerja sebelum pemeriksaan dapat dilakukan.'}
                </text.T14>
              </div>
            )}

            {isChecker && (task.status === 'in_review' || task.status === 'completed') && !isChecked && isSelfChecked && (
              <div style={{marginTop: 12}}>
                {isPending && missingToolItems.length > 0 && task.checker_response_at && (
                  <div style={{
                    backgroundColor: '#F0FDF4', borderRadius: 10, padding: '12px 14px',
                    border: '1px solid #86EFAC', marginBottom: 10
                  }}>
                    <text.T14 style={{fontWeight: '600', color: '#166534', marginBottom: 8}}>
                      {(t.checkerTask as any)?.replacementSubmittedTitle || 'Item Pengganti Telah Disubmit'}
                    </text.T14>
                    {task.checker_response_note && (
                      <div style={{marginBottom: 8}}>
                        <text.T14 style={{color: '#4B5563', fontWeight: '600'}}>
                          {(t.checkerTask as any)?.replacementNoteLabel2 || 'Keterangan:'}
                        </text.T14>
                        <text.T14 style={{color: '#374151', whiteSpace: 'pre-wrap'}}>{task.checker_response_note}</text.T14>
                      </div>
                    )}
                    {task.checker_response_photos && task.checker_response_photos.length > 0 && (
                      <div>
                        <text.T14 style={{color: '#4B5563', fontWeight: '600', marginBottom: 6}}>
                          {(t.checkerTask as any)?.replacementPhotosLabel || 'Foto Bukti:'}
                        </text.T14>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                          {task.checker_response_photos.map((photo: string, i: number) => (
                            <img
                              key={i}
                              src={`${imageBaseUrl}${photo}`}
                              alt={`bukti-${i + 1}`}
                              style={{width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #D1FAE5', cursor: 'pointer'}}
                              onClick={() => setSelectedAttachment({ url: `${imageBaseUrl}${photo}`, name: `Foto ${i + 1}`, type: 'image' })}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    <components.Button
                      title={isLoading ? (t.common.processing || 'Memproses...') : ((t.checkerTask as any)?.btnConfirmAccept || 'Terima Item Pengganti')}
                      style={{ backgroundColor: '#16A34A', color: '#FFFFFF', marginTop: 12 }}
                      containerStyle={{ width: '100%' }}
                      onClick={handleConfirmReplacement}
                    />
                  </div>
                )}
                {isPending && missingToolItems.length > 0 && !task.checker_response_at && (
                  <div style={{
                    backgroundColor: '#FFFBEB', borderRadius: 8, padding: '8px 12px',
                    border: '1px solid #FDE68A', marginBottom: 10, textAlign: 'center'
                  }}>
                    <text.T14 style={{color: '#92400E', fontStyle: 'italic'}}>
                      {(t.checkerTask as any)?.waitingUserResponse || 'Menunggu konfirmasi item pengganti dari pekerja'}
                    </text.T14>
                  </div>
                )}
                <components.Button
                  title={(t.checkerTask as any)?.btnInspection || "Pemeriksaan"}
                  style={{ backgroundColor: theme.colors.mainColor, color: '#FFFFFF' }}
                  containerStyle={{ width: '100%' }}
                  onClick={() => navigate('/CheckerTask', { state: { task, type: 'pemeriksaan' } })}
                />
              </div>
            )}

            {isAssignedTo && !isChecker && (task.status === 'in_review' || task.status === 'completed') && isPending && missingToolItems.length > 0 && (
              <div style={{marginTop: 12}}>
                <div style={{
                  backgroundColor: '#FEF2F2', borderRadius: 8, padding: '10px 12px',
                  border: '1px solid #FECACA', marginBottom: 10
                }}>
                  <text.T14 style={{color: '#DC2626', fontWeight: '600', marginBottom: 4}}>
                    {(t.checkerTask as any)?.pendingItemsTitle || 'Item berikut perlu dilengkapi:'}
                  </text.T14>
                  {task.checker_checklists?.filter((c: any) => !c.is_checked && c.category === 'tools').map((c: any) => (
                    <text.T14 key={c.id} style={{color: '#991B1B', display: 'block', paddingLeft: 8}}>
                      • {c.title}
                    </text.T14>
                  ))}
                  {task.checker_note && (
                    <text.T14 style={{color: '#6B7280', marginTop: 6, fontStyle: 'italic'}}>
                      {(t.checkerTask as any)?.checkerNote || 'Catatan Pemeriksa:'} {task.checker_note}
                    </text.T14>
                  )}
                </div>
                {task.checker_response_at ? (
                  <div style={{
                    backgroundColor: '#F0FDF4', borderRadius: 10, padding: '12px 14px',
                    border: '1px solid #86EFAC'
                  }}>
                    <text.T14 style={{fontWeight: '600', color: '#166534', marginBottom: 8}}>
                      {(t.checkerTask as any)?.replacementSubmittedTitle || 'Item Pengganti Telah Disubmit'} ✓
                    </text.T14>
                    {task.checker_response_note && (
                      <div style={{marginBottom: 8}}>
                        <text.T14 style={{color: '#4B5563', fontWeight: '600'}}>
                          {(t.checkerTask as any)?.replacementNoteLabel2 || 'Keterangan:'}
                        </text.T14>
                        <text.T14 style={{color: '#374151', whiteSpace: 'pre-wrap'}}>{task.checker_response_note}</text.T14>
                      </div>
                    )}
                    {task.checker_response_photos && task.checker_response_photos.length > 0 && (
                      <div>
                        <text.T14 style={{color: '#4B5563', fontWeight: '600', marginBottom: 6}}>
                          {(t.checkerTask as any)?.replacementPhotosLabel || 'Foto Bukti:'}
                        </text.T14>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                          {task.checker_response_photos.map((photo: string, i: number) => (
                            <img
                              key={i}
                              src={`${imageBaseUrl}${photo}`}
                              alt={`bukti-${i + 1}`}
                              style={{width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #D1FAE5', cursor: 'pointer'}}
                              onClick={() => setSelectedAttachment({ url: `${imageBaseUrl}${photo}`, name: `Foto ${i + 1}`, type: 'image' })}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <components.Button
                    title={(t.checkerTask as any)?.btnConfirmReplacement || 'Konfirmasi Item Pengganti'}
                    style={{ backgroundColor: '#EF4444', color: '#FFFFFF' }}
                    containerStyle={{ width: '100%' }}
                    onClick={() => setShowCheckerResponseModal(true)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // ── Section halaman (dipakai bersama oleh layout scroll & layout tab — lihat TaskDetailsTabLayout) ──
  // Parent task, follow-up, assign-to editor, client/vessel, deskripsi
  const renderDetailMainSection = () => (
    <>
        {/* Parent Task Indicator */}
        {task.parent_task_id && (
            <div 
              style={{
                backgroundColor: '#EFF6FF', padding: '12px 16px', borderRadius: 12,
                display: 'flex', alignItems: 'center', gap: 12, border: '1px solid #BFDBFE',
                marginBottom: 24, cursor: 'pointer'
              }}
              onClick={() => {
                navigate(`/TaskDetails?id=${task.parent_task_id}`, { state: { task: { id: task.parent_task_id } } });
              }}
            >
                <div style={{
                    width: 40, height: 40, borderRadius: 10, backgroundColor: '#DBEAFE', 
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                    <DescriptionIcon size={20} color="#2563EB" />
                </div>
                <div style={{overflow: 'hidden'}}>
                    <text.T14 style={{color: '#2563EB', fontSize: 12, marginBottom: 2, fontWeight: 'bold'}}>{t.taskDetails?.followUpOf || 'Lanjutan dari'}</text.T14>
                    <text.H6 style={{color: theme.colors.mainDark}} numberOfLines={2}>
                      {task.parent_task_description || `Task #${task.parent_task_id}`}
                    </text.H6>
                </div>
            </div>
        )}

        {task.status === 'completed' && (
            <div style={{ marginBottom: 24 }}>
                <components.Button
                    title={t.taskDetails.createFollowUp || 'Buat Tugas Lanjutan'}
                    onClick={() => navigate('/CreateTask', { state: { parentTask: task } })}
                />
            </div>
        )}

        {(hasPermission('task:edit') || task.assigned_by?.id == authSlice.userId) && ['draft', 'pending_approval', 'approved'].includes(task.status?.toLowerCase()) && (
        <>
        <text.H5 style={{color: theme.colors.mainDark, marginBottom: 12}}>{t.taskDetails.assignTo}</text.H5>

        <div style={{ position: 'relative', marginBottom: '17px' }}>
          {/* Dropdown Trigger */}
          <div 
            onClick={() => setShowAssignDropdown(!showAssignDropdown)}
            style={{
              padding: '12px 16px',
              borderRadius: '10px',
              border: '1px solid #E5E7EB',
              backgroundColor: '#FFF',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer'
            }}
          >
            <text.T14 style={{ color: selectedUserIds.length > 0 ? theme.colors.mainDark : '#9CA3AF' }}>
              {selectedUserIds.length > 0 
                ? `${selectedUserIds.length} ${t.taskDetails.usersSelected || 'Pengguna Dipilih'}` 
                : t.taskDetails.searchUser || 'Pilih pengguna...'}
            </text.T14>
            <ArrowIcon size={20} color="#9CA3AF" style={{ transform: showAssignDropdown ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
          </div>

          {/* Dropdown Menu */}
          {showAssignDropdown && (
            <>
              {/* Invisible Overlay for click-outside */}
              <div 
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 }}
                onClick={() => setShowAssignDropdown(false)}
              />
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '8px',
                backgroundColor: '#FFF',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                zIndex: 100,
                maxHeight: '300px',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Search Input */}
                <div style={{ padding: '12px', borderBottom: '1px solid #F3F4F6' }}>
                  <input 
                    type="text"
                    placeholder={t.taskDetails.searchUser || "Cari pengguna..."}
                    value={assignSearchTerm}
                    onChange={(e) => setAssignSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      outline: 'none',
                      boxSizing: 'border-box',
                      fontSize: 14
                    }}
                  />
                </div>
                {/* List */}
                <div style={{ overflowY: 'auto', padding: '8px', flex: 1 }}>
                  {users
                    .filter(user => user.id != authSlice.userId && (user.name || '').toLowerCase().includes(assignSearchTerm.toLowerCase()))
                    .map(user => {
                      const isSelected = selectedUserIds.includes(user.id);
                      return (
                        <div
                          key={user.id}
                          onClick={() => toggleAssignUser(user.id)}
                          style={{
                            padding: '10px 12px',
                            borderRadius: '8px',
                            backgroundColor: isSelected ? '#EFF6FF' : 'transparent',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '4px',
                            transition: 'background-color 0.2s'
                          }}
                        >
                          {isSelected ? <CheckBoxIcon size={20} color={theme.colors.mainColor} /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
                          <text.T14 style={{ fontWeight: isSelected ? '600' : '400', textTransform: 'capitalize', color: isSelected ? theme.colors.mainColor : theme.colors.mainDark }}>
                            {user.name}
                          </text.T14>
                        </div>
                      )
                  })}
                  {users.filter(user => user.id != authSlice.userId && (user.name || '').toLowerCase().includes(assignSearchTerm.toLowerCase())).length === 0 && (
                    <div style={{ padding: '10px', textAlign: 'center', color: '#9CA3AF' }}>
                      <text.T14>{t.taskDetails.noMatchingUsers || 'Tidak ada pengguna yang cocok'}</text.T14>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Selected Pills */}
          {selectedUserIds.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
              {users.filter(u => selectedUserIds.includes(u.id)).map(user => (
                <div key={user.id} style={{
                  padding: '4px 8px',
                  backgroundColor: '#EFF6FF',
                  border: `1px solid ${theme.colors.mainColor}`,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <text.T14 style={{ color: theme.colors.mainColor, fontSize: 12, textTransform: 'capitalize', fontWeight: '500' }}>{user.name}</text.T14>
                  {(hasPermission('task:edit') || task.assigned_by?.id == authSlice.userId) && (
                    <CloseIcon size={14} color={theme.colors.mainColor} style={{ cursor: 'pointer' }} onClick={() => toggleAssignUser(user.id)} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        </>
        )}
        
        {(task.client_name || task.vessel_name) && (
            <div style={{ marginBottom: 32, padding: '0 4px' }}>
                {task.client_name && (
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 20, backgroundColor: '#EFF6FF', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                                <HandshakeIcon size={20} color="#3B82F6" />
                            </div>
                            {task.vessel_name && (
                                <div style={{ width: 2, flex: 1, backgroundColor: '#E5E7EB', margin: '4px 0', minHeight: 12 }} />
                            )}
                        </div>
                        <div style={{ paddingBottom: task.vessel_name ? 16 : 0, paddingTop: 2 }}>
                            <text.T14 style={{color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: '600', marginBottom: 4}}>{t.taskDetails.client || 'Client'}</text.T14>
                            <text.H4 style={{color: theme.colors.mainDark, textTransform: 'capitalize'}}>{task.client_name}</text.H4>
                        </div>
                    </div>
                )}

                {task.vessel_name && (
                    <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 20, backgroundColor: '#FFFBEB', 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                            }}>
                                <BoatIcon size={20} color="#F59E0B" />
                            </div>
                        </div>
                        <div style={{ paddingTop: 2, flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div
                                style={{ paddingRight: 10, cursor: 'pointer' }}
                                onClick={handleOpenVesselHistory}
                            >
                                <text.T14 style={{color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, fontWeight: '600', marginBottom: 4}}>{t.taskDetails.vessel || 'Vessel'}</text.T14>
                                <text.H4 style={{color: theme.colors.mainColor, textTransform: 'capitalize', textDecoration: 'underline', textDecorationStyle: 'dotted'}}>{task.vessel_name}</text.H4>
                            </div>
                            {task.vessel_ship_particular && (
                                <div 
                                    style={{ 
                                        padding: '6px 12px', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', 
                                        borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                    }}
                                    onClick={async (e: any) => {
                                        e.stopPropagation();
                                        if (task.vessel_ship_particular) {
                                            const fileUrl = `${imageBaseUrl}${task.vessel_ship_particular.replace(/\\/g, '/')}`;
                                            const fileName = task.vessel_ship_particular.split(/[/\\]/).pop() || 'Ship_Particular';
                                            if (fileName.toLowerCase().endsWith('.pdf')) {
                                                setPreviewPdfUrl({ url: fileUrl, name: fileName });
                                            } else {
                                                await utils.downloadFile(fileUrl, fileName);
                                            }
                                        }
                                    }}
                                >
                                    {task.vessel_ship_particular.toLowerCase().endsWith('.pdf') ? (
                                        <VisibilityIcon size={16} color="#4B5563" />
                                    ) : (
                                        <DownloadIcon size={16} color="#4B5563" />
                                    )}
                                    <text.T14 style={{ fontSize: 12, fontWeight: '600', color: '#4B5563' }}>{t.taskDetails.file || 'File'}</text.T14>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )}

        <div style={{marginBottom: 24}}>
            <text.H5 style={{color: theme.colors.mainDark, marginBottom: 12}}>{t.taskDetails.title}</text.H5>

            {(task?.total_duration || 0) > 0 && (
            <div style={{marginBottom: 16}}>
                <text.T14 style={{color: '#9CA3AF', marginBottom: 6}}>{t.taskDetails.totalDuration}</text.T14>
                <text.T16 style={{color: theme.colors.mainDark, fontWeight: '500'}}>
                    {totalDuration}
                </text.T16>
            </div>
            )}
            
            {task.attachment_url && (
                <div style={{marginBottom: 24, display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10}}>
                    {(() => {
                        let parsed = [];
                        try { parsed = JSON.parse(task.attachment_url); } catch(e) { parsed = [task.attachment_url]; }
                        if(!Array.isArray(parsed)) parsed = [parsed];

                        const taskAttachmentsGallery = parsed.map((url: string) => {
                            const fileUrl = url ? `${imageBaseUrl}${url.replace(/\\/g, '/')}` : '';
                            const fileName = url ? url.split(/[/\\]/).pop() || 'Attachment' : 'Attachment';
                            const { icon, type } = getFileIcon(fileName);
                            return {url: fileUrl, name: fileName, type, icon, note: ''};
                        });

                        return parsed.map((url, idx) => {
                            if (!url) return null;
                            const fileUrl = `${imageBaseUrl}${url.replace(/\\/g, '/')}`;
                            const fileName = url.split(/[/\\]/).pop() || 'Attachment';
                            const { icon, type } = getFileIcon(fileName);

                            return (
                                <div key={idx} style={{
                                    display: 'flex', flexDirection: 'column', width: 100, flexShrink: 0
                                }}>
                                    <div style={{
                                        width: 100, height: 100, borderRadius: 8, 
                                        backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'pointer',
                                        marginBottom: 6
                                    }}
                                    onClick={() => setSelectedAttachment({...taskAttachmentsGallery[idx], galleryIndex: idx, gallery: taskAttachmentsGallery})}
                                    >
                                    {type === 'image' ? (
                                        <img src={fileUrl} alt={fileName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                    ) : (
                                        <div style={{padding: 4}}>
                                            {React.cloneElement(icon as React.ReactElement, { size: 24 })}
                                        </div>
                                    )}
                                </div>
                                    <text.T14 style={{fontWeight: '500', color: theme.colors.mainDark, fontSize: 11, textAlign: 'center'}} numberOfLines={1}>{fileName}</text.T14>
                                </div>
                            );
                        });
                    })()}
                </div>
            )}

            <text.T14 style={{color: '#9CA3AF', marginBottom: 6}}>{t.common.description}</text.T14>
            <text.T16 style={{color: theme.colors.bodyTextColor, lineHeight: '1.6', whiteSpace: 'pre-wrap'}}>
              {renderTextWithLinks(task.description)}
            </text.T16>
        </div>
    </>
  );

  // Lampiran wajib task
  const renderRequiredAttachmentsSection = () => (
    <>
        {/* Attachment Types Selection */}
        {viewGroupedAttachments.length > 0 && (
        <div style={{marginBottom: 24}}>
            <text.H5 style={{color: theme.colors.mainDark, marginBottom: 12}}>{t.section.requiredAttachments}</text.H5>

            {viewGroupedAttachments.length > 0 ? (
                <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                    {viewGroupedAttachments.map((group) => {
                        const isGroupExpanded = expandedGroups.includes(group.group_name);

                        return (
                            <div key={group.group_name} style={{ display: 'flex', flexDirection: 'column' }}>
                                <div 
                                    onClick={() => toggleGroup(group.group_name)}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 10, 
                                        padding: '10px', backgroundColor: '#FFF', borderRadius: 8,
                                        border: '1px solid #E5E7EB',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <text.T16 style={{color: theme.colors.mainDark, flex: 1, fontWeight: '600'}}>{group.group_name}</text.T16>
                                    <ArrowIcon size={20} color="#9CA3AF" style={{ transform: isGroupExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease-in-out' }} />
                                </div>
                                
                                <div style={{
                                    display: 'grid',
                                    gridTemplateRows: isGroupExpanded ? '1fr' : '0fr',
                                    transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out',
                                    opacity: isGroupExpanded ? 1 : 0,
                                    marginTop: isGroupExpanded ? 8 : 0,
                                }}>
                                    <div style={{ overflow: 'hidden' }}>
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 10,
                                            paddingLeft: 10,
                                        }}>
                                            {group.attachments.map((att: any) => {
                                                const isExpanded = expandedAttachmentId === att.id;

                                                let submittedAttachments: any[] = [];
                                                if (task.feedbacks) {
                                                    task.feedbacks.forEach((feedback: any) => {
                                                        if (feedback.images) {
                                                            const matched = feedback.images.filter((img: any) => img.task_checklist_id === att.id);
                                                            matched.forEach((m: any) => { m.feedback_id = feedback.id; m.submitted_by_name = feedback.submitted_by_name; });
                                                            submittedAttachments = [...submittedAttachments, ...matched];
                                                        }
                                                    });
                                                }

                                                return (
                                                    <div key={att.id} style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <div
                                                            onClick={() => {
                                                                setExpandedAttachmentId(isExpanded ? null : att.id);
                                                            }}
                                                            style={{
                                                                display: 'flex', alignItems: 'center', gap: 10,
                                                                padding: '10px', backgroundColor: '#FFF', borderRadius: 8,
                                                                border: `1px solid ${theme.colors.mainColor}`,
                                                                cursor: 'pointer',
                                                                opacity: 1
                                                            }}
                                                        >
                                                            <CheckBoxIcon size={24} color={theme.colors.mainColor} />
                                                            <text.T16 style={{color: theme.colors.mainDark, flex: 1}}>{att.title}</text.T16>
                                                            <ArrowIcon size={20} color="#9CA3AF" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease-in-out' }} />
                                                        </div>

                                                        <div style={{
                                                                display: 'grid',
                                                                gridTemplateRows: isExpanded ? '1fr' : '0fr',
                                                                transition: 'grid-template-rows 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-top 0.3s ease-in-out',
                                                                opacity: isExpanded ? 1 : 0,
                                                                marginTop: isExpanded ? 8 : 0,
                                                            }}>
                                                                <div style={{ overflow: 'hidden' }}>
                                                                    <div style={{
                                                                        padding: '10px', 
                                                                        backgroundColor: '#F9FAFB', 
                                                                        borderRadius: 8, 
                                                                        border: '1px solid #E5E7EB',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: 10,
                                                                    }}>
                                                                        {submittedAttachments.length > 0 ? (() => {
                                                                            const submittedAttachmentsGallery = submittedAttachments.filter((img: any) => img.file_path).map((img: any) => {
                                                                                const fileUrl = `${imageBaseUrl}${img.file_path.replace(/\\/g, '/')}`;
                                                                                const fileName = img.file_path.split(/[/\\]/).pop() || 'Attachment';
                                                                                const { icon, type } = getFileIcon(fileName);
                                                                                return {url: fileUrl, name: fileName, type, icon, note: img.note, submitted_by: img.submitted_by_name};
                                                                            });
                                                                            
                                                                            const renderAttachmentRow = (img: any) => {
                                            const globalIdx = submittedAttachments.findIndex((i: any) => i.id === img.id);
                                            const attachmentComments = task.feedbacks?.flatMap((f: any) => f.comments || []).filter((c: any) => c.feedback_attachment_id === img.id) || [];
                                            const fileUrl = img.file_path ? `${imageBaseUrl}${img.file_path.replace(/\\/g, '/')}` : '';
                                            const fileName = img.file_path ? img.file_path.split(/[/\\]/).pop() : '';
                                            const { icon, type } = getFileIcon(fileName || '');

                                            return (
                                                <div key={img.id} style={{
                                                    display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 8,
                                                    paddingBottom: 16, borderBottom: '1px solid #E5E7EB'
                                                }}>
                                                    {img.response_value ? (
                                                        <div style={{
                                                            padding: '10px',
                                                            backgroundColor: '#FFFFFF',
                                                            borderRadius: 8,
                                                            border: '1px solid #E5E7EB',
                                                            fontSize: 14,
                                                            color: theme.colors.mainDark,
                                                            whiteSpace: 'pre-wrap'
                                                        }}>
                                                            {renderTextWithLinks(img.response_value)}
                                                        </div>
                                                    ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px', backgroundColor: '#FFFFFF', borderRadius: 8, border: '1px solid #E5E7EB', cursor: 'pointer' }}
                                                             onClick={() => setSelectedAttachment({...submittedAttachmentsGallery[globalIdx], galleryIndex: globalIdx, gallery: submittedAttachmentsGallery})}
                                                        >
                                                            <div style={{
                                                                width: 40, height: 40, borderRadius: 6, overflow: 'hidden',
                                                                backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                                            }}>
                                                                {type === 'image' ? (
                                                                    <img src={fileUrl} alt={fileName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                                                ) : (
                                                                    <div style={{padding: 4}}>
                                                                        {React.cloneElement(icon as React.ReactElement, { size: 20 })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div style={{ overflow: 'hidden', flex: 1 }}>
                                                                {task.is_maintenance && img.timing && (
                                                                    <div style={{
                                                                        backgroundColor: img.timing === 'before' ? 'rgba(239, 68, 68, 0.8)' : 'rgba(34, 197, 94, 0.8)',
                                                                        color: 'white',
                                                                        padding: '2px 4px',
                                                                        borderRadius: 4,
                                                                        fontSize: 8,
                                                                        fontWeight: 'bold',
                                                                        textTransform: 'uppercase',
                                                                        marginBottom: 2,
                                                                        display: 'inline-block'
                                                                    }}>
                                                                        {img.timing}
                                                                    </div>
                                                                )}
                                                                <text.T14 style={{ fontWeight: '500', color: theme.colors.mainDark }} numberOfLines={1}>{fileName}</text.T14>
                                                                {img.note && <text.T14 style={{ fontSize: 12, color: '#6B7280' }} numberOfLines={1}>{img.note}</text.T14>}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Attachment Comments */}
                                                    {attachmentComments.length > 0 && (
                                                        <div style={{ marginLeft: 16, paddingLeft: 16, borderLeft: '2px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {attachmentComments.map((comment: any) => {
                                                            const isMyComment = comment.user_id == authSlice.userId;
                                                            return (
                                                            <div id={`comment-${comment.id}`} key={comment.id} className={!isMyComment ? 'other-user-comment' : ''} style={{backgroundColor: isMyComment ? '#FFFFFF' : '#FEF9C3', padding: 10, borderRadius: 8, border: isMyComment ? '1px solid #F3F4F6' : '1px solid #FDE047'}}>
                                                                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4}}>
                                                                        <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, textTransform: 'capitalize'}}>{comment.user_name}</text.T14>
                                                                        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                                                                            <text.T14 style={{fontSize: 10, color: '#9CA3AF'}}>{formatDateTime(comment.created_at)}</text.T14>
                                                                            {isMyComment && (
                                                                                <div
                                                                                    onClick={() => setConfirmDeleteCommentId(comment.id)}
                                                                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                                                    title="Hapus komentar"
                                                                                >
                                                                                    <DeleteIcon size={13} color="#EF4444" />
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    {comment.comment && (
                                                                        <text.T14 style={{color: '#4B5563', lineHeight: 1.4, whiteSpace: 'pre-wrap', marginBottom: comment.attachment_url ? 8 : 0}}>{renderTextWithLinks(comment.comment)}</text.T14>
                                                                    )}
                                                                    {comment.attachment_url && (() => {
                                                                        const commentFileUrl = `${imageBaseUrl}${comment.attachment_url.replace(/\\/g, '/')}`;
                                                                        const commentFileName = comment.attachment_url.split(/[/\\]/).pop() || 'Attachment';
                                                                        const { icon: commentIcon, type: commentType } = getFileIcon(commentFileName);
                                                                        if (commentType === 'image') {
                                                                            return (
                                                                                <div
                                                                                    style={{ width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: '1px solid #E5E7EB', cursor: 'pointer' }}
                                                                                    onClick={(e) => {e.stopPropagation(); setSelectedAttachment({url: commentFileUrl, name: commentFileName, type: commentType, icon: commentIcon});}}
                                                                                >
                                                                                    <img src={commentFileUrl} alt={commentFileName} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                                                                                </div>
                                                                            );
                                                                        } else {
                                                                            return (
                                                                                <div style={{
                                                                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                                                                    padding: '6px 10px', backgroundColor: '#F9FAFB', borderRadius: 8,
                                                                                    border: '1px solid #E5E7EB', cursor: 'pointer', maxWidth: '100%'
                                                                                }}
                                                                                onClick={(e) => {e.stopPropagation(); setSelectedAttachment({url: commentFileUrl, name: commentFileName, type: commentType, icon: commentIcon});}}
                                                                                >
                                                                                    {React.cloneElement(commentIcon as React.ReactElement, { size: 14, style: { flexShrink: 0 } })}
                                                                                    <text.T14 style={{fontSize: 11, color: theme.colors.mainDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{commentFileName}</text.T14>
                                                                                </div>
                                                                            );
                                                                        }
                                                                    })()}
                        </div>
                    )})}
                                                        </div>
                                                    )}

                                                    {/* Attachment Comment Input */}
                                                    {expandedCommentIds.has(img.id) ? (
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 4 }}>
                                                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: 88, gap: 8 }}>
                                                                {commentAttachments[`attachment_${img.id}`] && (() => {
                                                                    const att = commentAttachments[`attachment_${img.id}`]!;
                                                                    const isImage = att.type.startsWith('image/');
                                                                    const previewUrl = isImage ? URL.createObjectURL(att) : null;
                                                                    return (
                                                                        <div style={{
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                                            padding: '6px 10px', backgroundColor: '#F3F4F6', borderRadius: 8, border: '1px solid #E5E7EB'
                                                                        }}>
                                                                            <div style={{display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden'}}>
                                                                                {isImage ? (
                                                                                    <img src={previewUrl!} alt="Preview" style={{width: 24, height: 24, objectFit: 'cover', borderRadius: 4}} />
                                                                                ) : (
                                                                                    <AttachFileIcon size={14} color={theme.colors.mainColor} />
                                                                                )}
                                                                                <text.T14 style={{fontSize: 11}} numberOfLines={1}>{att.name}</text.T14>
                                                                            </div>
                                                                            <CloseIcon size={14} color="#EF4444" style={{cursor: 'pointer'}} onClick={() => setCommentAttachments({...commentAttachments, [`attachment_${img.id}`]: null})} />
                                                                        </div>
                                                                    );
                                                                })()}
                                                                <textarea
                                                                    value={newComments[`attachment_${img.id}`] || ''}
                                                                    onChange={(e) => setNewComments({...newComments, [`attachment_${img.id}`]: e.target.value})}
                                                                    placeholder={t.taskDetails.replyToAttachmentPlaceholder || "Balas ke lampiran ini..."}
                                                                    style={{
                                                                        flex: 1, height: 120, padding: '10px 12px',
                                                                        borderRadius: 8, border: '1px solid #E5E7EB',
                                                                        fontSize: 13, resize: 'none', fontFamily: 'inherit',
                                                                        outline: 'none', boxSizing: 'border-box'
                                                                    }}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                                            e.preventDefault();
                                                                            handleSendComment(img.feedback_id, img.id);
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <div style={{display: 'flex', flexDirection: 'column', gap: 8, width: 55, flexShrink: 0}}>
                                                                <label style={{cursor: 'pointer', backgroundColor: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40, boxSizing: 'border-box'}}>
                                                                    <AttachFileIcon size={20} color="#6B7280" />
                                                                    <input
                                                                        type="file"
                                                                        style={{display: 'none'}}
                                                                        onChange={async (e) => {
                                                                            if (e.target.files && e.target.files.length > 0) {
                                                                                let file = e.target.files[0];
                                                                                if (file.type.startsWith('image/')) {
                                                                                  try { file = await compressImage(file); } catch (err) {}
                                                                                }
                                                                                setCommentAttachments({...commentAttachments, [`attachment_${img.id}`]: file});
                                                                            }
                                                                            e.target.value = '';
                                                                        }}
                                                                    />
                                                                </label>
                                                                {(() => {
                                                                    const aKey = `attachment_${img.id}`;
                                                                    const isSending = commentSendingKey === aKey;
                                                                    return (
                                                                        <div
                                                                            onClick={() => !isSending && handleSendComment(img.feedback_id, img.id)}
                                                                            style={{
                                                                                backgroundColor: isSending ? '#9CA3AF' : theme.colors.mainColor,
                                                                                color: 'white', borderRadius: 8,
                                                                                cursor: isSending ? 'not-allowed' : 'pointer',
                                                                                fontWeight: '600', fontSize: isSending ? 11 : 14,
                                                                                display: 'flex', flexDirection: 'column',
                                                                                alignItems: 'center', justifyContent: 'center',
                                                                                height: 40, boxSizing: 'border-box', gap: 3, overflow: 'hidden', padding: '0 4px',
                                                                            }}
                                                                        >
                                                                            {isSending ? (
                                                                                <>
                                                                                    <span>{commentUploadProgress}%</span>
                                                                                    <div style={{ width: '80%', height: 3, backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: 2, overflow: 'hidden' }}>
                                                                                        <div style={{ width: `${commentUploadProgress}%`, height: '100%', backgroundColor: 'white', transition: 'width 0.15s ease' }} />
                                                                                    </div>
                                                                                </>
                                                                            ) : (t.taskDetails.send || 'Kirim')}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            onClick={() => setExpandedCommentIds(prev => new Set(Array.from(prev).concat(img.id)))}
                                                            style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: theme.colors.mainColor, fontSize: 13, fontWeight: '500' }}
                                                        >
                                                            <span style={{ fontSize: 16, lineHeight: 1 }}>↩</span>
                                                            <span>{(t.taskDetails as any)?.showReply || 'Balas'}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                            }; // end renderAttachmentRow

                                            const noTimingItems = submittedAttachments.filter((img: any) => !img.timing);
                                            const beforeItems = submittedAttachments.filter((img: any) => img.timing === 'before');
                                            const afterItems = submittedAttachments.filter((img: any) => img.timing === 'after');
                                            const timingLabel = (label: string, color: string, bg: string) => (
                                                <div style={{ display: 'block', backgroundColor: bg, color, fontSize: 11, fontWeight: '700', padding: '8px 12px', borderRadius: 6, textTransform: 'uppercase', marginBottom: 8, marginTop: 4 }}>{label}</div>
                                            );

                                            return (
                                                <>
                                                    {noTimingItems.map(renderAttachmentRow)}
                                                    {beforeItems.length > 0 && <>
                                                        {timingLabel('Before', '#DC2626', 'rgba(239,68,68,0.1)')}
                                                        {beforeItems.map(renderAttachmentRow)}
                                                    </>}
                                                    {afterItems.length > 0 && <>
                                                        {timingLabel('After', '#16A34A', 'rgba(34,197,94,0.1)')}
                                                        {afterItems.map(renderAttachmentRow)}
                                                    </>}
                                                </>
                                            );
                                        })() : (
                                            <div style={{
                                                padding: '10px', 
                                                textAlign: 'center'
                                            }}>
                                                <text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>{t.taskDetails.noAttachmentsSubmitted || 'Belum ada lampiran yang disubmit.'}</text.T14>
                                            </div>
                                        )}
                                            </div>
                                        </div>
                                    </div>
                            </div>
                        );
                    })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <text.T14 style={{color: '#9CA3AF', fontStyle: 'italic'}}>{t.taskDetails.noSpecificAttachments}</text.T14>
            )}
        </div>
        )}
    </>
  );

  // Info task (tipe, tanggal, approver, checker) + assigned by/to
  const renderDetailInfoSection = () => (
    <>
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 16,
            marginBottom: 10
        }}>
            <div style={{backgroundColor: '#F9FAFB', padding: 16, borderRadius: 12}}>
                <text.T14 style={{color: '#9CA3AF', marginBottom: 6}}>{t.taskDetails.taskType}</text.T14>
                <text.H5 style={{textTransform: 'capitalize', fontSize: 15}}>{task.task_type}</text.H5>
            </div>

            <div style={{backgroundColor: '#F9FAFB', padding: 16, borderRadius: 12}}>
                <text.T14 style={{color: '#9CA3AF', marginBottom: 6}}>{t.taskDetails.createdDate}</text.T14>
                <text.H5 style={{fontSize: 15}}>{formatDateTime(task.created_at)}</text.H5>
            </div>
            
            {!['draft', 'pending_approval'].includes(task.status) && task.approved_at && (
                <div style={{backgroundColor: '#F0FDF4', padding: 16, borderRadius: 12}}>
                    <text.T14 style={{color: '#10B981', marginBottom: 6}}>{t.taskDetails.approvedBy}</text.T14>
                    <text.H5 style={{color: '#065F46', fontSize: 15, textTransform: 'capitalize'}}>{task.approved_by?.name || 'Unknown'}</text.H5>
                    <text.H5 style={{color: '#065F46', fontSize: 15}}>{formatDateTime(task.approved_at)}</text.H5>
                </div>
            )}

            {task.status === 'completed' && task.completed_at && (
                <div style={{backgroundColor: '#ECFDF5', padding: 16, borderRadius: 12}}>
                    <text.T14 style={{color: '#059669', marginBottom: 6}}>{t.taskDetails.completedDate}</text.T14>
                    <text.H5 style={{color: '#064E3B', fontSize: 15}}>{formatDateTime(task.completed_at)}</text.H5>
                </div>
            )}
            
            {task.checked_at && (
                <div style={{backgroundColor: '#F5F3FF', padding: 16, borderRadius: 12}}>
                    <text.T14 style={{color: '#8B5CF6', marginBottom: 6}}>
                        {(t.checkerTask as any)?.checkedAt || 'Diperiksa pada'}
                    </text.T14>
                    <text.H5 style={{color: '#4C1D95', fontSize: 15, textTransform: 'capitalize'}}>{task.checker?.name || 'Unknown'}</text.H5>
                    <text.H5 style={{color: '#4C1D95', fontSize: 15}}>{formatDateTime(task.checked_at)}</text.H5>
                </div>
            )}
        </div>

        <div style={{borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10}}>
            <text.H5 style={{marginBottom: 16}}>{t.taskDetails.assignedBy}</text.H5>
            <UserAvatarCard
                userId={task.assigned_by?.id}
                photo={task.assigned_by?.photo}
                name={task.assigned_by?.name}
                groupName={task.assigned_by?.group_name}
                imageBaseUrl={imageBaseUrl}
            />
        </div>

        <div style={{borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10, display: (task.assigned_to && task.assigned_to.length > 0 ? 'block' : 'none')}}>
            <text.H5 style={{marginBottom: 16}}>{t.taskDetails.assignedTo}</text.H5>
            <div style={{display: 'flex', flexDirection: 'column', gap: 14}}>
                {task.assigned_to?.map((user: any) => (
                    <UserAvatarCard
                        key={user.id}
                        userId={user.id}
                        photo={user.photo}
                        name={user.name}
                        groupName={user.group_name}
                        imageBaseUrl={imageBaseUrl}
                    />
                ))}
            </div>
        </div>
    </>
  );

  // Grup perjalanan — task lain (customer sama) dalam 1 perjalanan teknisi
  const openGroupTask = (m: { id: number }) =>
    navigate(`/TaskDetails?id=${m.id}`, { state: { task: { id: m.id } } });
  const renderTripGroupSection = (withDivider = true) => (
    task?.id && tripGroup.inGroup ? (
      <div style={withDivider ? {borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10} : undefined}>
        <TripGroupList taskId={task.id} group={tripGroup} onOpenTask={openGroupTask} />
      </div>
    ) : null
  );

  // SN installation item yg benar-benar terpasang di kapal task ini (dikonfirmasi teknisi)
  const renderInstalledSerials = () => (
    task?.id && ['approved', 'in_progress', 'on_hold', 'in_review', 'completed'].includes(task.status?.toLowerCase())
      ? <InstalledSerialsSection key={`serials-${groupVersion}`} taskId={task.id} /> : null
  );

  // Dokumen perjalanan boleh diedit pembuatnya atau assignee task ini (dokumen bersama grup:
  // assignee task mana pun di grup) — backend tetap memvalidasi & mencatat editor terakhir
  const canEditTripDoc = (doc: any) =>
    doc.created_by == authSlice.userId || !!task.assigned_to?.some((u: any) => u.id == authSlice.userId);

  // Info dokumen bersama (asal task/kapal) & editor terakhir
  const renderTripDocMeta = (doc: any) => {
    const tg = (t as any).tripGroup || {};
    const fromOther = doc.task_id && Number(doc.task_id) !== Number(task.id);
    return (
      <>
        {doc.is_shared && (
          <div style={{display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap'}}>
            <span style={{backgroundColor: '#E0F2FE', color: '#0369A1', borderRadius: 20, padding: '1px 8px', fontSize: 11, fontWeight: 600}}>{tg.sharedDoc || 'Bersama'}</span>
            {fromOther && (
              <text.T12 style={{color: '#0369A1'}}>{tg.sharedFrom || 'Dibagi dari'}: {doc.vessel_name || doc.task_vessel_name || '-'} · #{doc.task_id}</text.T12>
            )}
          </div>
        )}
        {doc.updated_by_name && (
          <text.T12 style={{color: '#9CA3AF', marginTop: 2, display: 'block'}}>{tg.lastEditedBy || 'Terakhir diedit oleh'}: {doc.updated_by_name}</text.T12>
        )}
      </>
    );
  };

  // Uang jalan, rekapan biaya, time sheet
  const renderSupportingDocsSection = () => (
    <>
        {travelExpenses.length > 0 && (
          <div style={{borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10}}>
            <text.H5 style={{marginBottom: 10}}>{(t as any)?.travelExpenses?.title || 'Lampiran Uang Jalan'}</text.H5>
            {travelExpenses.map((exp) => (
              <div key={exp.id} style={{border: '1px solid #E5E7EB', borderRadius: 12, marginBottom: 8, overflow: 'hidden'}}>
                <div
                  onClick={() => setTeExpandedId(teExpandedId === exp.id ? null : exp.id)}
                  style={{padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', backgroundColor: '#F9FAFB'}}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <ReceiptIcon size={18} color="#3B82F6" />
                    <div>
                      <text.T14 style={{fontWeight: '600', color: '#111827'}}>{exp.from_location} → {exp.to_location}</text.T14>
                      <text.T12 style={{color: '#6B7280'}}>{String(exp.departure_date).substring(0, 10)} – {String(exp.return_date).substring(0, 10)}</text.T12>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <text.T14 style={{fontWeight: '600', color: '#059669'}}>
                      Rp {Number(exp.total_amount).toLocaleString('id-ID')}
                    </text.T14>
                    <div style={{
                      backgroundColor: exp.status === 'approved' ? '#D1FAE5' : exp.status === 'rejected' ? '#FEE2E2' : exp.status === 'draft' ? '#F3F4F6' : '#FEF3C7',
                      color: exp.status === 'approved' ? '#065F46' : exp.status === 'rejected' ? '#991B1B' : exp.status === 'draft' ? '#6B7280' : '#92400E',
                      borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: '600',
                    }}>{exp.status}</div>
                    {teExpandedId === exp.id ? <ExpandLessIcon size={18} color="#6B7280" /> : <ExpandMoreIcon size={18} color="#6B7280" />}
                  </div>
                </div>
                {teExpandedId === exp.id && (
                  <div style={{padding: '10px 14px', borderTop: '1px solid #F3F4F6'}}>
                    <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 13}}>
                      <thead>
                        <tr style={{color: '#6B7280'}}>
                          <th style={{textAlign: 'left', padding: '4px 0', fontWeight: '600'}}>{(t as any)?.travelExpenses?.costType || 'Jenis Biaya'}</th>
                          <th style={{textAlign: 'right', padding: '4px 0', fontWeight: '600'}}>{(t as any)?.travelExpenses?.amount || 'Nominal'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(exp.items || []).map((item: any, i: number) => (
                          <tr key={i}>
                            <td style={{padding: '3px 0', color: '#374151'}}>{item.cost_type}</td>
                            <td style={{padding: '3px 0', textAlign: 'right', color: '#374151'}}>Rp {Number(item.amount).toLocaleString('id-ID')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #E5E7EB', marginTop: 6, paddingTop: 6}}>
                      <text.T12 style={{fontWeight: '700', color: '#111827'}}>Total</text.T12>
                      <text.T12 style={{fontWeight: '700', color: '#059669'}}>Rp {Number(exp.total_amount).toLocaleString('id-ID')}</text.T12>
                    </div>
                    <div style={{marginTop: 4}}>
                      <text.T12 style={{color: '#9CA3AF'}}>Diajukan oleh: {exp.created_by_name}</text.T12>
                      {renderTripDocMeta(exp)}
                      {exp.reviewed_by_name && (
                        <>
                          <text.T12 style={{color: exp.status === 'approved' ? '#059669' : '#EF4444', marginTop: 2}}>
                            {exp.status === 'approved' ? 'Disetujui' : 'Ditolak'} oleh: {exp.reviewed_by_name} • {exp.reviewed_at ? new Date(exp.reviewed_at).toLocaleDateString('id-ID', {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'}) : ''}
                          </text.T12>
                          {exp.review_notes && (
                            <text.T12 style={{color: '#6B7280', marginTop: 2, fontStyle: 'italic'}}>
                              "{exp.review_notes}"
                            </text.T12>
                          )}
                        </>
                      )}
                    </div>
                    {['draft', 'pending'].includes(exp.status) && (
                      <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10}}>
                        {canEditTripDoc(exp) && (
                          <div
                            onClick={(e) => { e.stopPropagation(); navigate('/TravelExpenseForm', { state: { task, expense: exp } }); }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              color: '#3B82F6', cursor: 'pointer',
                              padding: '4px 10px', borderRadius: 8, border: '1px solid #BFDBFE',
                              backgroundColor: '#EFF6FF',
                            }}
                          >
                            <EditIcon size={14} color="#3B82F6" />
                            <text.T12 style={{color: '#3B82F6'}}>Edit</text.T12>
                          </div>
                        )}
                        {exp.status === 'pending' && hasPermission('travel_expense:approve') && (
                          <>
                            <div
                              onClick={(e) => { e.stopPropagation(); if (!teActionLoading) setTeConfirmModal({ expId: exp.id, action: 'approve' }); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                color: '#059669', cursor: teActionLoading ? 'not-allowed' : 'pointer',
                                padding: '4px 8px', borderRadius: 8, border: '1px solid #6EE7B7',
                                backgroundColor: '#F0FDF4',
                              }}
                            >
                              <text.T12 style={{color: '#059669'}}>Setujui</text.T12>
                            </div>
                            <div
                              onClick={(e) => { e.stopPropagation(); if (!teActionLoading) setTeConfirmModal({ expId: exp.id, action: 'reject' }); }}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                color: '#EF4444', cursor: teActionLoading ? 'not-allowed' : 'pointer',
                                padding: '4px 8px', borderRadius: 8, border: '1px solid #FECACA',
                                backgroundColor: '#FFF5F5',
                              }}
                            >
                              <text.T12 style={{color: '#EF4444'}}>Tolak</text.T12>
                            </div>
                          </>
                        )}
                        {canEditTripDoc(exp) && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTravelExpense(exp.id);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 4,
                              color: teDeletingId === exp.id ? '#9CA3AF' : '#EF4444',
                              cursor: teDeletingId === exp.id ? 'not-allowed' : 'pointer',
                              padding: '4px 8px', borderRadius: 8,
                              border: '1px solid',
                              borderColor: teDeletingId === exp.id ? '#E5E7EB' : '#FECACA',
                              backgroundColor: teDeletingId === exp.id ? '#F9FAFB' : '#FFF5F5',
                            }}
                          >
                            <DeleteIcon size={14} color={teDeletingId === exp.id ? '#9CA3AF' : '#EF4444'} />
                            <text.T12 style={{color: teDeletingId === exp.id ? '#9CA3AF' : '#EF4444'}}>
                              {teDeletingId === exp.id ? 'Menghapus...' : 'Batalkan'}
                            </text.T12>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {recaps.length > 0 && (
          <div style={{borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10}}>
            <text.H5 style={{marginBottom: 10}}>{(t as any).travelExpenseRecap?.sectionTitle || 'Rekapan Biaya Perjalanan'}</text.H5>
            {recaps.map((recap) => (
              <div key={recap.id} style={{border: '1px solid #E5E7EB', borderRadius: 12, marginBottom: 8, overflow: 'hidden'}}>
                <div
                  onClick={() => setRecapExpandedId(recapExpandedId === recap.id ? null : recap.id)}
                  style={{padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', backgroundColor: '#F0FDF4'}}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <ReceiptIcon size={18} color="#059669" />
                    <div>
                      <text.T14 style={{fontWeight: '600', color: '#111827'}}>{recap.from_location} → {recap.to_location}</text.T14>
                      <text.T12 style={{color: '#6B7280'}}>{String(recap.departure_date).substring(0, 10)} – {String(recap.return_date).substring(0, 10)}</text.T12>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <text.T14 style={{fontWeight: '600', color: '#059669'}}>
                      Rp {Number(recap.total_amount).toLocaleString('id-ID')}
                    </text.T14>
                    <div style={{
                      backgroundColor: recap.status === 'approved' ? '#D1FAE5' : recap.status === 'rejected' ? '#FEE2E2' : recap.status === 'draft' ? '#F3F4F6' : '#FEF3C7',
                      color: recap.status === 'approved' ? '#065F46' : recap.status === 'rejected' ? '#991B1B' : recap.status === 'draft' ? '#6B7280' : '#92400E',
                      borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: '600',
                    }}>{recap.status === 'draft' ? 'Draft' : recap.status}</div>
                    {recapExpandedId === recap.id ? <ExpandLessIcon size={18} color="#6B7280" /> : <ExpandMoreIcon size={18} color="#6B7280" />}
                  </div>
                </div>

                {recapExpandedId === recap.id && (
                  <div style={{padding: '10px 14px', borderTop: '1px solid #F3F4F6'}}>
                    {(() => {
                      const daysId = ['Minggu','Senin','Selasa','Rabu','Kamis','Jumat','Sabtu'];
                      const dayName = (d: string) => { const dt = new Date(d); return isNaN(dt.getTime()) ? '' : daysId[dt.getDay()]; };
                      return (recap.items || []).map((item: any, i: number) => (
                        <div key={i} style={{display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: i < recap.items.length - 1 ? '1px solid #F9FAFB' : 'none'}}>
                          {/* Foto */}
                          {item.photo_url ? (
                            <img
                              src={`${recapImageBaseUrl}${item.photo_url}`}
                              alt="bukti"
                              onClick={() => setRecapLightboxUrl(`${recapImageBaseUrl}${item.photo_url}`)}
                              style={{width: 44, height: 44, objectFit: 'cover', borderRadius: 8, cursor: 'pointer', border: '1px solid #E5E7EB', flexShrink: 0}}
                            />
                          ) : (
                            <div style={{width: 44, height: 44, borderRadius: 8, backgroundColor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                              <ReceiptIcon size={18} color="#D1D5DB" />
                            </div>
                          )}
                          {/* Info */}
                          <div style={{flex: 1, minWidth: 0}}>
                            <text.T12 style={{color: '#111827', fontWeight: '600', display: 'block', wordBreak: 'break-word'}}>{item.description}</text.T12>
                            <text.T12 style={{color: '#9CA3AF'}}>
                              {item.expense_date ? `${dayName(String(item.expense_date).substring(0, 10))}, ${String(item.expense_date).substring(0, 10)}` : ''}
                              {item.employee_name ? ` · ${item.employee_name}` : ''}
                              {recap.is_shared ? ` · ${item.item_vessel_name || ((t as any).tripGroup?.vesselShared || 'Bersama')}` : ''}
                            </text.T12>
                          </div>
                          <text.T12 style={{fontWeight: '700', color: '#111827', whiteSpace: 'nowrap'}}>Rp {Number(item.amount).toLocaleString('id-ID')}</text.T12>
                        </div>
                      ));
                    })()}
                    <div style={{display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #E5E7EB', marginTop: 6, paddingTop: 6}}>
                      <text.T12 style={{fontWeight: '700', color: '#111827'}}>{(t as any).travelExpenseRecap?.total || 'Total'}</text.T12>
                      <text.T12 style={{fontWeight: '700', color: '#059669'}}>Rp {Number(recap.total_amount).toLocaleString('id-ID')}</text.T12>
                    </div>
                    <text.T12 style={{color: '#9CA3AF', marginTop: 4}}>{(t as any).travelExpenseRecap?.createdBy || 'Dibuat oleh'}: {recap.created_by_name}</text.T12>
                    {renderTripDocMeta(recap)}
                    {/* Pembuat/assignee bisa edit/hapus selama belum approved — termasuk rejected (diperbaiki lalu kirim ulang) */}
                    {canEditTripDoc(recap) && recap.status !== 'approved' && (
                      <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10}}>
                        {/* Edit button */}
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/TravelExpenseRecapForm', { state: { task, recap } });
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            color: '#3B82F6', cursor: 'pointer',
                            padding: '4px 10px', borderRadius: 8, border: '1px solid #BFDBFE',
                            backgroundColor: '#EFF6FF',
                          }}
                        >
                          <EditIcon size={14} color="#3B82F6" />
                          <text.T12 style={{color: '#3B82F6'}}>Edit</text.T12>
                        </div>
                        {/* Delete button */}
                        <div
                          onClick={(e) => { e.stopPropagation(); handleDeleteRecap(recap.id); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            color: recapDeletingId === recap.id ? '#9CA3AF' : '#EF4444',
                            cursor: recapDeletingId === recap.id ? 'not-allowed' : 'pointer',
                            padding: '4px 8px', borderRadius: 8, border: '1px solid',
                            borderColor: recapDeletingId === recap.id ? '#E5E7EB' : '#FECACA',
                            backgroundColor: recapDeletingId === recap.id ? '#F9FAFB' : '#FFF5F5',
                          }}
                        >
                          <DeleteIcon size={14} color={recapDeletingId === recap.id ? '#9CA3AF' : '#EF4444'} />
                          <text.T12 style={{color: recapDeletingId === recap.id ? '#9CA3AF' : '#EF4444'}}>
                            {recapDeletingId === recap.id ? (t.common.deleting || 'Menghapus...') : (t.common.delete || 'Hapus')}
                          </text.T12>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {timeSheets.length > 0 && (
          <div style={{borderTop: '1px solid #F4F6F8', paddingTop: 10, marginTop: 10}}>
            <text.H5 style={{marginBottom: 10}}>{(t as any).timeSheet?.menuTitle || 'Time Sheet'}</text.H5>
            {timeSheets.map((ts: any) => (
              <div key={ts.id} style={{border: '1px solid #E5E7EB', borderRadius: 12, marginBottom: 8, overflow: 'hidden'}}>
                <div
                  onClick={() => setTsExpandedId(tsExpandedId === ts.id ? null : ts.id)}
                  style={{padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', backgroundColor: '#F5F3FF'}}
                >
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <CalendarIcon size={18} color="#7C3AED" />
                    <div>
                      <text.T14 style={{fontWeight: '600', color: '#111827'}}>{ts.technician_name || ts.location}</text.T14>
                      <text.T12 style={{color: '#6B7280'}}>{ts.location} · {new Date(ts.updated_at || ts.created_at).toLocaleDateString('id-ID', {day: '2-digit', month: 'short', year: 'numeric'})}</text.T12>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <text.T12 style={{color: '#9CA3AF'}}>{(ts.items || []).length} {(t as any).timeSheet?.rows || 'baris'}</text.T12>
                    {tsExpandedId === ts.id ? <ExpandLessIcon size={18} color="#6B7280" /> : <ExpandMoreIcon size={18} color="#6B7280" />}
                  </div>
                </div>

                {tsExpandedId === ts.id && (
                  <div style={{padding: '10px 14px', borderTop: '1px solid #F3F4F6'}}>
                    <text.T12 style={{color: '#9CA3AF', marginBottom: 6, display: 'block'}}>
                      {(t as any).timeSheet?.vessel || 'Nama Kapal'}: {ts.vessel_name || '-'} · {(t as any).timeSheet?.technician || 'Nama Teknisi'}: {ts.technician_name || '-'}
                    </text.T12>
                    {renderTripDocMeta({ ...ts, vessel_name: ts.task_vessel_name })}
                    {(() => {
                      const daysId = ['Minggu','Senin','Selasa','Rabu','Kamis','Jumat','Sabtu'];
                      const dayName = (d: string) => { const dt = new Date(d); return isNaN(dt.getTime()) ? '' : daysId[dt.getDay()]; };
                      return (ts.items || []).map((item: any, i: number) => (
                        <div key={i} style={{padding: '6px 0', borderBottom: i < ts.items.length - 1 ? '1px solid #F9FAFB' : 'none'}}>
                          <text.T12 style={{color: '#9CA3AF'}}>{dayName(String(item.work_date).substring(0, 10))}, {String(item.work_date).substring(0, 10)}{ts.is_shared ? ` · ${item.item_vessel_name || ((t as any).tripGroup?.vesselShared || 'Bersama')}` : ''}</text.T12>
                          <text.T12 style={{color: '#111827', display: 'block'}}>{item.description}</text.T12>
                        </div>
                      ));
                    })()}

                    {canEditTripDoc(ts) && (
                      <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10}}>
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/TimeSheetForm', { state: { task, timeSheet: ts } });
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            color: '#3B82F6', cursor: 'pointer',
                            padding: '4px 10px', borderRadius: 8, border: '1px solid #BFDBFE',
                            backgroundColor: '#EFF6FF',
                          }}
                        >
                          <EditIcon size={14} color="#3B82F6" />
                          <text.T12 style={{color: '#3B82F6'}}>Edit</text.T12>
                        </div>
                        <div
                          onClick={(e) => { e.stopPropagation(); handleDeleteTimeSheet(ts.id); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            color: tsDeletingId === ts.id ? '#9CA3AF' : '#EF4444',
                            cursor: tsDeletingId === ts.id ? 'not-allowed' : 'pointer',
                            padding: '4px 8px', borderRadius: 8, border: '1px solid',
                            borderColor: tsDeletingId === ts.id ? '#E5E7EB' : '#FECACA',
                            backgroundColor: tsDeletingId === ts.id ? '#F9FAFB' : '#FFF5F5',
                          }}
                        >
                          <DeleteIcon size={14} color={tsDeletingId === ts.id ? '#9CA3AF' : '#EF4444'} />
                          <text.T12 style={{color: tsDeletingId === ts.id ? '#9CA3AF' : '#EF4444'}}>
                            {tsDeletingId === ts.id ? (t.common.deleting || 'Menghapus...') : (t.common.delete || 'Hapus')}
                          </text.T12>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </>
  );

  const renderDetails = () => (
    <div className='container' style={{marginTop: usingOfflineCache ? cacheBannerTop + cacheBannerHeight : 60, paddingBottom: 100}}>
        {renderStatusCard()}
        {/* {renderProgressTracker()} */}

        {renderDetailMainSection()}

        {renderRequiredAttachmentsSection()}

        {renderDetailInfoSection()}

        {renderTripGroupSection()}

        {renderSupportingDocsSection()}

        {renderCheckerSection()}

        {renderInstalledSerials()}

        {renderFeedbacks()}
    </div>
  );

  // Layout tab — susunan alternatif dari section yang sama dgn renderDetails (layout scroll)
  const renderTabLayout = () => {
    const userId = authSlice.userId;
    const status = task.status?.toLowerCase();
    const isAssignee = task.assigned_to?.some((u: any) => u.id == userId);
    const isTaskChecker = task.checker?.id == userId;
    const isAssigner = task.assigned_by?.id == userId;
    const selfChecked = task.self_check_status === 'checked';

    // Giliran aksi user ini — sama dgn routing pengingat di backend (task_reminder.service.js)
    const selfCheckTurn = !!task.checker && isAssignee && !selfChecked && ['approved', 'in_review'].includes(status);
    const checkerTurn = !!task.checker && isTaskChecker && selfChecked && (
      (status === 'approved' && task.checker_status !== 'prepared') ||
      (status === 'in_review' && task.checker_status !== 'checked')
    );
    const reviewTurn = status === 'in_review' && (isAssigner || hasPermission('task:approve')) &&
      (!task.checker || task.checker_status === 'checked');

    const defaultTab: TaskDetailTabKey = (checkerTurn || selfCheckTurn) ? 'checker' : reviewTurn ? 'report' : 'detail';
    const feedbackCount = task.feedbacks?.length || 0;
    const docsEmpty = viewGroupedAttachments.length === 0 && travelExpenses.length === 0 && recaps.length === 0 && timeSheets.length === 0;

    const tabs: TaskDetailTab[] = [
      {
        key: 'detail', label: t.taskDetails.tabDetail,
        render: () => <>{renderDetailMainSection()}{renderDetailInfoSection()}</>,
      },
      {
        key: 'documents', label: t.taskDetails.tabDocuments,
        render: () => <>{renderRequiredAttachmentsSection()}{renderSupportingDocsSection()}</>,
        dot: commentAlertTabs.includes('documents'),
        isEmpty: docsEmpty, emptyText: t.taskDetails.tabDocumentsEmpty,
      },
      ...(tripGroup.inGroup ? [{
        key: 'group' as TaskDetailTabKey, label: (t as any).tripGroup?.tab || 'Grup',
        render: () => renderTripGroupSection(false),
      }] : []),
      ...(task.checker ? [{
        key: 'checker' as TaskDetailTabKey, label: t.taskDetails.tabChecker,
        render: () => <>{renderCheckerSection()}{renderInstalledSerials()}</>,
        dot: checkerTurn || selfCheckTurn,
      }] : []),
      {
        key: 'report', label: t.taskDetails.tabReport,
        render: () => renderFeedbacks(),
        badge: feedbackCount,
        dot: reviewTurn || commentAlertTabs.includes('report'),
        isEmpty: feedbackCount === 0, emptyText: t.taskDetails.tabReportEmpty,
      },
      // Uji integrasi GpsGate: posisi & RPM/bahan bakar dari controller kapal task ini (products dgn
      // GpsGate Application ID). Hanya saat task pending_approval / approved / in_progress / on_hold / in_review & punya kapal.
      ...(task.vessel_id && ['pending_approval', 'approved', 'in_progress', 'on_hold', 'in_review'].includes(status) ? [{
        key: 'testing' as TaskDetailTabKey, label: (t.taskDetails as any).tabTesting || 'Testing',
        render: () => <VesselPositionsTab vesselId={task.vessel_id} />,
      }] : []),
    ];

    return (
      <TaskDetailsTabLayout
        taskId={task.id}
        topOffset={usingOfflineCache ? cacheBannerTop + cacheBannerHeight : 60}
        stickyTop={usingOfflineCache ? cacheBannerTop + cacheBannerHeight : 52}
        statusCard={renderStatusCard(true)}
        tabs={tabs}
        defaultTab={defaultTab}
        tabRequest={tabRequest}
        dotLabel={t.taskDetails.tabNeedsAction}
        onActiveTabChange={(tab) => {
          activeTaskTabRef.current = tab;
          setCommentAlertTabs(prev => prev.includes(tab) ? prev.filter(x => x !== tab) : prev);
        }}
      />
    );
  };

  const renderButtons = () => {
    const status = task.status?.toLowerCase();
    
    if (status === 'completed') return null;

    // Fallback translation texts untuk mencegah text undefined (tombol menjadi kosong/putih)
    const btnApprove = t.taskDetails?.buttons?.approve || 'Setujui';
    const btnReject = t.taskDetails?.buttons?.reject || 'Tolak';
    const btnFailed = t.taskDetails?.buttons?.failed || 'Gagal';
    const btnComplete = t.taskDetails?.buttons?.complete || 'Selesai';
    const btnStartWork = t.taskDetails?.buttons?.startWork || 'Mulai Pekerjaan';
    const btnSubmit = t.taskDetails?.buttons?.submitReport || 'Buat Laporan';
    const btnEditDraft = t.taskDetails?.buttons?.editDraft || 'Ubah Draf';
    const btnPending = t.taskDetails?.buttons?.pendingWork || 'Pending';
    const txtProcessing = t.common?.processing || 'Memproses...';
    const txtSubmitting = t.common?.submitting || 'Mengirim...';

    let buttons = null;
    // Permission Based Logic
    if (status === 'pending_approval' && hasPermission('task:approve')) {
        buttons = <components.Button title={isLoading ? txtProcessing : btnApprove} style={{ backgroundColor: theme.colors.mainColor, color: '#FFFFFF' }} containerStyle={{ width: '100%' }} onClick={() => handleAction('approve')} />;
    } else if (status === 'in_review' || status === 'in review') {
        if (hasPermission('task:approve') || hasPermission('task:complete')) {
            const btnReview = (t.taskDetails?.buttons as any)?.review || 'Review';
            buttons = (
                <components.Button
                    key="review"
                    title={isLoading ? '...' : btnReview}
                    style={{ backgroundColor: theme.colors.mainColor, color: '#FFFFFF' }}
                    containerStyle={{ width: '100%' }}
                    onClick={() => navigate('/TaskReview', { state: { task } })}
                />
            );
        }
    }
    
    // General User Logic (Submit for Approved/Rejected)
    const canStartWork = !task.checker?.id || task.prepared_at != null;
    const isAssigned = task.assigned_to?.some((u: any) => u.id == authSlice.userId);
    const isCreator = task.assigned_by?.id == authSlice.userId;
    const isAdmin = hasPermission('task:view_all');
    const canManageWork = isAssigned || isCreator || isAdmin;

    if (status === 'approved' || status === 'on_hold') {
        if (canStartWork) {
            if (isAssigned) {
                buttons = (
                    <components.Button
                        title={isLoading ? txtProcessing : btnStartWork}
                        style={{ backgroundColor: theme.colors.mainColor, color: '#FFFFFF' }}
                        containerStyle={{ width: '100%' }}
                        onClick={() => handleAction('start')}
                    />
                );
            }
        } else {
            buttons = (
                <components.Button
                    title={t.taskDetails.waitingPreparation || 'Menunggu Persiapan'}
                    style={{backgroundColor: '#F3F4F6', color: '#9CA3AF', width: '100%'}}
                    containerStyle={{ width: '100%' }}
                    onClick={() => showAlert(t.common.warning, t.taskDetails.waitingPreparationMsg || 'Task cannot be started because the Checker has not completed preparation yet.', 'warning')}
                />
            );
        }
    } else if (status === 'in_progress' || status === 'rejected') {
        if (isAssigned) {
            const hasDraft = task.feedbacks?.some((f: any) => f.review_status === 'draft');
            const buttonTitle = hasDraft
                ? btnEditDraft
                : btnSubmit;

            if (status === 'in_progress') {
                buttons = (
                    <div style={{display: 'flex', gap: 14, width: '100%'}}>
                        <components.Button
                            title={isLoading ? '...' : btnPending}
                            style={{backgroundColor: '#FFF3E0', color: '#FF9800', border: '1px solid #FFE0B2'}}
                            containerStyle={{flex: 1}}
                            onClick={() => handleAction('pending')}
                        />
                        <components.Button
                            title={isTaskRefreshing ? '...' : (isLoading ? txtSubmitting : buttonTitle)}
                            style={{ backgroundColor: isTaskRefreshing ? '#9CA3AF' : theme.colors.mainColor, color: '#FFFFFF' }}
                            containerStyle={{flex: 2}}
                            onClick={() => { if (!isTaskRefreshing) handleAction('submit'); }}
                        />
                    </div>
                );
            } else {
                buttons = <components.Button title={isTaskRefreshing ? '...' : (isLoading ? txtSubmitting : buttonTitle)} style={{ backgroundColor: isTaskRefreshing ? '#9CA3AF' : theme.colors.mainColor, color: '#FFFFFF' }} containerStyle={{ width: '100%' }} onClick={() => { if (!isTaskRefreshing) handleAction('submit'); }} />;
            }
        }
    }
    


    if (!buttons) return null;

    return (
        <div style={{
            position: 'fixed', bottom: 0, left: 0, right: 0,
            display: 'flex', justifyContent: 'center',
            zIndex: 10
        }}>
            <div className='container' style={{
                width: '100%', maxWidth: 650,
                backgroundColor: 'white', padding: '16px 24px 32px',
                boxShadow: '0 -4px 20px rgba(0,0,0,0.05)',
                display: 'flex', flexDirection: 'column', gap: 10,
            }}>
                {buttons && <div style={{width: '100%'}}>{buttons}</div>}
            </div>
        </div>
    );
  };

  const renderAlert = () => {
    if (!alertConfig.isOpen) return null;
    return (
      <div
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
          display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
        }}
      >
        <div
          style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 320, textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            display: 'flex', flexDirection: 'column', alignItems: 'center'
          }}
        >
          <div style={{marginBottom: 16}}>
            {alertConfig.type === 'success' && <CheckCircleIcon size={60} color="#10B981" />}
            {alertConfig.type === 'error' && <ErrorIcon size={60} color="#EF4444" />}
            {alertConfig.type === 'warning' && <WarningIcon size={60} color="#F59E0B" />}
          </div>
          <text.H4 style={{ marginBottom: 8 }}>{alertConfig.title}</text.H4>
          <text.T16 style={{ marginBottom: alertConfig.errorDetail ? 12 : 24, color: theme.colors.bodyTextColor, lineHeight: 1.5 }}>
            {alertConfig.message}
          </text.T16>
          {alertConfig.errorDetail && (
            <div style={{ width: '100%', marginBottom: 24, textAlign: 'left' }}>
              <button
                onClick={() => setErrorDetailExpanded(prev => !prev)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#9CA3AF', fontSize: 12, textDecoration: 'underline' }}
              >
                {errorDetailExpanded ? 'hide error details' : 'click to expand error details'}
              </button>
              {errorDetailExpanded && (
                <div style={{ marginTop: 8, padding: 10, backgroundColor: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA', fontSize: 11, color: '#DC2626', lineHeight: 1.5, wordBreak: 'break-all' }}>
                  {alertConfig.errorDetail}
                </div>
              )}
            </div>
          )}
          <components.Button title={t.common.ok} onClick={hideAlert} containerStyle={{width: '100%'}} />
        </div>
      </div>
    );
  };

  const renderCheckerResponseModal = () => {
    if (!showCheckerResponseModal) return null;
    const missingItems = task?.checker_checklists?.filter((c: any) => !c.is_checked && c.category === 'tools') || [];
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 4000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: 16, padding: 24,
          width: '100%', maxWidth: 380,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column', gap: 14,
          maxHeight: '80vh', overflowY: 'auto'
        }}>
          <text.H4>{(t.checkerTask as any)?.confirmReplacementTitle || 'Konfirmasi Item Pengganti'}</text.H4>

          {missingItems.length > 0 && (
            <div style={{backgroundColor: '#FEF2F2', borderRadius: 8, padding: '10px 12px', border: '1px solid #FECACA'}}>
              <text.T14 style={{fontWeight: '600', color: '#DC2626', marginBottom: 6}}>
                {(t.checkerTask as any)?.missingItemsLabel || 'Item yang perlu dilengkapi:'}
              </text.T14>
              {missingItems.map((c: any) => (
                <text.T14 key={c.id} style={{color: '#991B1B', display: 'block', paddingLeft: 8}}>• {c.title}</text.T14>
              ))}
            </div>
          )}

          <div>
            <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, marginBottom: 6}}>
              {(t.checkerTask as any)?.replacementNoteLabel || 'Keterangan penggantian item'}
            </text.T14>
            <textarea
              value={checkerResponseNote}
              onChange={(e) => setCheckerResponseNote(e.target.value)}
              placeholder={(t.checkerTask as any)?.replacementNotePlaceholder || 'Jelaskan item pengganti yang sudah disediakan...'}
              rows={3}
              style={{
                width: '100%', padding: '10px 14px', borderRadius: 8,
                border: '1px solid #E5E7EB', fontSize: 14, outline: 'none',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box'
              }}
            />
          </div>

          <div>
            <text.T14 style={{fontWeight: '600', color: theme.colors.mainDark, marginBottom: 6}}>
              {(t.checkerTask as any)?.photoEvidenceLabel || 'Foto bukti (opsional, maks. 5)'}
            </text.T14>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []).slice(0, 5);
                setCheckerResponsePhotos(files);
              }}
              style={{
                width: '100%', padding: '8px 0', fontSize: 14,
                border: 'none', outline: 'none', cursor: 'pointer'
              }}
            />
            {checkerResponsePhotos.length > 0 && (
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6}}>
                {checkerResponsePhotos.map((f, i) => (
                  <div key={i} style={{
                    padding: '3px 8px', backgroundColor: '#EFF6FF', borderRadius: 6,
                    border: '1px solid #BFDBFE', fontSize: 12, color: '#1D4ED8'
                  }}>
                    {f.name.length > 20 ? f.name.substring(0, 20) + '...' : f.name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{display: 'flex', gap: 10}}>
            <components.Button
              title={t.common?.cancel || 'Batal'}
              style={{ backgroundColor: '#F3F4F6', color: '#4B5563', padding: '10px' }}
              containerStyle={{ flex: 1 }}
              onClick={() => { setShowCheckerResponseModal(false); setCheckerResponseNote(''); setCheckerResponsePhotos([]); }}
            />
            <components.Button
              title={isLoading
                ? ((t.checkerTask as any)?.btnSubmitting || 'Mengirim...')
                : ((t.checkerTask as any)?.btnSubmitConfirmation || 'Kirim Konfirmasi')}
              style={{ backgroundColor: '#EF4444', color: '#FFFFFF', padding: '10px' }}
              containerStyle={{ flex: 1 }}
              onClick={handleCheckerResponse}
            />
          </div>
        </div>
      </div>
    );
  };


  const renderPdfPreviewModal = () => {
    if (!previewPdfUrl) return null;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 5000,
        display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
      }} onClick={() => setPreviewPdfUrl(null)}>
        <div style={{ position: 'relative', width: '100%', height: '100%', maxWidth: 1000, display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 12 }}>
            <text.H5 style={{ color: 'white', wordBreak: 'break-all' }} numberOfLines={1}>{previewPdfUrl.name}</text.H5>
            <div onClick={() => setPreviewPdfUrl(null)} style={{ cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', padding: 4, display: 'flex' }}><CloseIcon size={24} color="white" /></div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ flex: 1, overflow: 'auto', backgroundColor: 'white', borderRadius: 8 }}>
              <div style={{ width: `${previewPdfZoom * 100}%`, height: `${previewPdfZoom * 100}%`, minHeight: '100%' }}>
                <iframe
                  src={`${previewPdfUrl.url}#toolbar=1&navpanes=0`}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  title={previewPdfUrl.name}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '5px 14px' }}>
                <button onClick={() => setPreviewPdfZoom(z => Math.max(0.5, z - 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 28 }}>−</button>
                <span style={{ color: 'white', fontSize: 13, minWidth: 38, textAlign: 'center' }}>{Math.round(previewPdfZoom * 100)}%</span>
                <button onClick={() => setPreviewPdfZoom(z => Math.min(3, z + 0.25))} style={{ background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer', lineHeight: 1, width: 28 }}>+</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAddModal = () => {
    if (!showAddModal) return null;
    return (
      <div
        onClick={() => setShowAddModal(false)}
        style={{position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20}}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{backgroundColor: 'white', width: '100%', maxWidth: 320, borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.1)'}}
        >
          <text.H4 style={{marginBottom: 20}}>{(t.personalData as any)?.addDocument || 'Tambah Dokumen'}</text.H4>
          <div
            onClick={() => {
              setShowAddModal(false);
              navigate('/TravelExpenseForm', { state: { task } });
            }}
            style={{padding: '14px 0', display: 'flex', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid #F3F4F6'}}
          >
            <ReceiptIcon size={22} color={theme.colors.mainDark} style={{marginRight: 15}} />
            <text.T16 style={{color: theme.colors.mainDark}}>{(t as any)?.travelExpenses?.addTitle || 'Pengajuan Uang Jalan'}</text.T16>
          </div>
          <div
            onClick={() => {
              setShowAddModal(false);
              navigate('/TravelExpenseRecapForm', { state: { task } });
            }}
            style={{padding: '14px 0', display: 'flex', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid #F3F4F6'}}
          >
            <ReceiptIcon size={22} color="#059669" style={{marginRight: 15}} />
            <text.T16 style={{color: theme.colors.mainDark}}>{(t as any).travelExpenseRecap?.addTitle || 'Rekapan Biaya Perjalanan'}</text.T16>
          </div>
          <div
            onClick={() => {
              setShowAddModal(false);
              navigate('/TimeSheetForm', { state: { task } });
            }}
            style={{padding: '14px 0', display: 'flex', alignItems: 'center', cursor: 'pointer'}}
          >
            <CalendarIcon size={22} color="#7C3AED" style={{marginRight: 15}} />
            <text.T16 style={{color: theme.colors.mainDark}}>{(t as any).timeSheet?.menuTitle || 'Time Sheet'}</text.T16>
          </div>
          <components.Button
            title={(t as any)?.common?.cancel || 'Batal'}
            colorScheme="light"
            containerStyle={{marginTop: 20}}
            onClick={() => setShowAddModal(false)}
          />
        </div>
      </div>
    );
  };



  const renderTeConfirmModal = () => {
    if (!teConfirmModal) return null;
    const isApprove = teConfirmModal.action === 'approve';
    return (
      <div
        onClick={() => { if (!teActionLoading) { setTeConfirmModal(null); setTeConfirmNotes(''); } }}
        style={{position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20}}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{backgroundColor: 'white', width: '100%', maxWidth: 320, borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.1)'}}
        >
          <text.H4 style={{marginBottom: 12}}>
            {isApprove ? 'Setujui Pengajuan?' : 'Tolak Pengajuan?'}
          </text.H4>
          <text.T16 style={{color: '#6B7280', marginBottom: 16}}>
            {isApprove
              ? 'Yakin ingin menyetujui pengajuan uang jalan ini?'
              : 'Yakin ingin menolak pengajuan uang jalan ini?'}
          </text.T16>
          <textarea
            placeholder="Catatan (opsional)"
            value={teConfirmNotes}
            onChange={e => setTeConfirmNotes(e.target.value)}
            rows={3}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid #E5E7EB',
              fontSize: 14, resize: 'none', outline: 'none', boxSizing: 'border-box',
              marginBottom: 20, color: '#374151', fontFamily: 'inherit',
            }}
          />
          <div style={{display: 'flex', gap: 12}}>
            <div
              onClick={() => { if (!teActionLoading) { setTeConfirmModal(null); setTeConfirmNotes(''); } }}
              style={{flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid #E5E7EB', display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer'}}
            >
              <text.T14 style={{color: '#374151'}}>Kembali</text.T14>
            </div>
            <div
              onClick={() => {
                if (teActionLoading) return;
                const { expId, action } = teConfirmModal;
                const notes = teConfirmNotes.trim();
                setTeConfirmModal(null);
                setTeConfirmNotes('');
                setTeActionId(expId);
                handleTravelExpenseAction(expId, action, notes || undefined);
              }}
              style={{
                flex: 1, padding: '10px 0', borderRadius: 10, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer',
                backgroundColor: isApprove ? '#059669' : '#EF4444',
              }}
            >
              <text.T14 style={{color: 'white', fontWeight: '600'}}>
                {teActionLoading ? 'Memproses...' : (isApprove ? 'Setujui' : 'Tolak')}
              </text.T14>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div id='screen' style={{backgroundColor: '#FFFFFF', minHeight: '100vh'}}>
      <style>{`
        ::-webkit-scrollbar {
          display: none;
        }
        * {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @keyframes highlightPulse {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7); }
          70% { box-shadow: 0 0 0 15px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
        .highlight-pulse {
          animation: highlightPulse 1.5s ease-out infinite;
        }
        @keyframes loadingBarSlide {
          0% { left: -40%; width: 40%; }
          50% { width: 55%; }
          100% { left: 110%; width: 40%; }
        }
        .task-refresh-bar::after {
          content: '';
          position: absolute;
          top: 0; bottom: 0;
          left: -40%;
          width: 40%;
          background: rgba(255,255,255,0.5);
          animation: loadingBarSlide 1.2s ease-in-out infinite;
        }
      `}</style>
      {isTaskRefreshing && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0,
          height: 3, zIndex: 999, overflow: 'hidden',
          backgroundColor: `${theme.colors.mainColor}55`,
        }} className="task-refresh-bar">
          <div style={{
            position: 'absolute', inset: 0,
            backgroundColor: theme.colors.mainColor,
          }} />
        </div>
      )}
      {renderHeader()}
      {usingOfflineCache && (
        <div
          ref={(el) => { if (el) setCacheBannerHeight(el.getBoundingClientRect().height); }}
          style={{
            position: 'fixed', top: cacheBannerTop, left: 0, right: 0, zIndex: 998,
            backgroundColor: '#FEF3C7', borderBottom: '1px solid #FDE68A',
            padding: '6px 16px', display: 'flex', alignItems: 'center', gap: 8,
          }}
        >
          <span style={{ fontSize: 14 }}>⚠️</span>
          <text.T12 style={{ color: '#92400E', flex: 1 }}>
            {(t.taskDetails as any)?.offlineCacheBanner || 'Data offline'} · {new Date(usingOfflineCache).toLocaleString()}
          </text.T12>
        </div>
      )}
      {isTabLayout ? renderTabLayout() : renderDetails()}
      {renderButtons()}
      {renderAttachmentModal()}
      {renderPendingModal()}
      {renderCancelModal()}
      {renderDeleteModal()}
      {renderDeleteCommentModal()}
      {renderVesselHistoryModal()}
      {renderAlert()}
      {renderPdfPreviewModal()}
      {renderCheckerResponseModal()}
      {renderAddModal()}
      {tripGroup.modals}

      {/* Link Pre-Check Report Bottom Sheet */}
      {showLinkReportSheet && (
        <div
          onClick={() => setShowLinkReportSheet(false)}
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
            zIndex: 5000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              backgroundColor: '#fff', borderRadius: '16px 16px 0 0',
              width: '100%', maxWidth: 650, maxHeight: '75vh',
              display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
            }}
          >
            <div style={{ padding: '10px 0 4px' }}>
              <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: '#E5E7EB', margin: '0 auto' }} />
            </div>
            <div style={{ padding: '8px 20px 14px', borderBottom: '1px solid #F3F4F6' }}>
              <text.H5 style={{ margin: 0 }}>
                {(t as any).preCheckReport?.sheetTitle || 'Pilih Pre-Check Report'}
              </text.H5>
              <text.T14 style={{ color: '#9CA3AF', marginTop: 2 }}>
                {(t as any).preCheckReport?.sheetDesc || 'Hanya laporan berstatus "Siap" yang ditampilkan'}
              </text.T14>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '12px 16px 24px' }}>
              {loadingReports ? (
                <text.T14 style={{ color: '#9CA3AF', textAlign: 'center', display: 'block', marginTop: 20 }}>
                  {(t as any).preCheckReport?.loading || 'Memuat...'}
                </text.T14>
              ) : availableReports.length === 0 ? (
                <div style={{ textAlign: 'center', marginTop: 20 }}>
                  <text.T14 style={{ color: '#9CA3AF' }}>
                    {(t as any).preCheckReport?.noAvailable || 'Tidak ada pre-check report yang tersedia.'}
                  </text.T14>
                  <text.T14 style={{ color: '#9CA3AF', fontSize: 12, marginTop: 4 }}>
                    {(t as any).preCheckReport?.noAvailableHint || 'Buat laporan baru dan tandai sebagai "Siap" terlebih dahulu.'}
                  </text.T14>
                </div>
              ) : (
                availableReports.map((r: any) => {
                  const isCurrent = r._is_current === true;
                  return (
                  <div
                    key={r.id}
                    style={{
                      backgroundColor: isCurrent ? '#F0FDF4' : '#FAFAFA',
                      borderRadius: 10, padding: '12px 14px',
                      marginBottom: 10,
                      border: `1px solid ${isCurrent ? '#86EFAC' : '#E5E7EB'}`,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{
                        fontSize: 11, fontWeight: '700', padding: '2px 8px', borderRadius: 99,
                        backgroundColor: r.type === 'persiapan' ? '#EFF6FF' : '#FFF7ED',
                        color: r.type === 'persiapan' ? '#1D4ED8' : '#C2410C',
                      }}>
                        {r.type === 'persiapan'
                          ? ((t as any).preCheckReport?.typePreparation || 'Persiapan')
                          : ((t as any).preCheckReport?.typeInspection || 'Pemeriksaan')}
                      </span>
                      <text.T14 style={{ color: '#9CA3AF', fontSize: 12 }}>
                        {new Date(r.report_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </text.T14>
                    </div>
                    {r.vessel_name && (
                      <text.T14 style={{ color: '#4B5563', fontSize: 13, marginBottom: 2 }}>🚢 {r.vessel_name}</text.T14>
                    )}
                    <text.T14 style={{ color: '#6B7280', fontSize: 12, marginBottom: 10 }}>
                      {r.items_count ?? (r.items?.length ?? 0)} {(t as any).preCheckReport?.items || 'item'}
                    </text.T14>
                    <components.Button
                      title={isCurrent
                        ? ((t as any).preCheckReport?.btnSelected || 'Terpilih')
                        : (linkingReport ? (t.common?.processing || 'Memproses...') : ((t as any).preCheckReport?.btnSelect || 'Pilih Laporan Ini'))}
                      style={{
                        backgroundColor: isCurrent ? '#D1FAE5' : theme.colors.mainColor,
                        color: isCurrent ? '#15803D' : '#fff',
                        padding: '8px',
                        opacity: isCurrent ? 1 : 1,
                      }}
                      containerStyle={{ width: '100%' }}
                      onClick={() => { if (!isCurrent && !linkingReport) handleLinkReport(r.id); }}
                    />
                  </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      {renderTeConfirmModal()}
      {recapLightboxUrl && (
        <div
          onClick={() => setRecapLightboxUrl(null)}
          style={{position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16}}
        >
          <img src={recapLightboxUrl} alt="bukti" style={{maxWidth: '100%', maxHeight: '90vh', borderRadius: 10, objectFit: 'contain'}} />
        </div>
      )}
    </div>
  );
};